import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
// v0.17.52 — 字母头像底色。必须是子路径 default import:
//   vendor/flarum/core/js/src/common/common.ts 里那行 `import './utils/stringToColor'`
//   是 side-effect import (只为注册副作用), **不 re-export**, 从 'flarum/common' 拿不到。
//   'flarum/common/utils/*' 子路径写法在本仓库 community-frontend/js/src/ 有 10+ 处先例。
//   该函数是纯函数: 不碰 store、不发请求, 且返回**不带 #** 的 hex。
import stringToColor from 'flarum/common/utils/stringToColor';
import Api from '../utils/Api';
// v0.6.45 — 课程类型 + 课程分类 labelFor() (辉哥 9/30 9:59+ 反馈, FC event 4 行内容展示)
import LessonOptions from '../utils/LessonOptions';
// v0.6.59 — 日期选择器公用 helper (辉哥 9/30 19:42 拍板: 3 个 mode 统一封装, 跨项目 SOP 沉淀)
import { mountDatePicker } from '../utils/DatePickerHelper';
// v0.7.9e (辉哥 10/5 拍板) — 约课上下文菜单 (桌面右键气泡 / 移动长按底部面板)
import SlotContextMenu from '../utils/SlotContextMenu';
// v0.8.4 (辉哥 10/6 拍板) — 课表时段预留渲染 (绝对定位 div, 不走 FC event, 4 角手柄拖拽)
import SlotReservationRenderer from '../utils/SlotReservationRenderer';
// v0.17.61 — 「没配课表」空态的文案 + 出口 (与约课弹窗共用同一份, 见该文件顶部注释)
// v0.17.72 — 补上第四态「问失败了」的那一套文案 (scheduleErrorPanel), 与 missing 刻意分开
import { SCHEDULE_LOADING_TEXT, scheduleMissingPanel, scheduleErrorPanel } from '../utils/ScheduleMissingNotice';

// v0.9.0 — 固定课 (辉哥 10/7 拍板)。
//
// v0.18.39 (辉哥 10/21 拍板) — 「添加固定课」弹窗**不再是纯 DOM 浮层**:
//   辉哥要求它跟普通约课 modal 对齐 (时间段下拉 / 可搜索教练学员 / Flarum 原生 Dropdown /
//   同样的排版), 而这四样**全都要求组件树**: Flarum 原生 `Dropdown` 与
//   `SearchableUserPicker` 都是 mithril 组件, 塞不进 createElement 拼出来的容器。
//   ⇒ 它已经改成 mithril `Modal` 子类 (js/src/forum/modal/FixedLessonModal.js),
//     打开方式 `app.modal.show(FixedLessonModal, {...})`。
//
// ⚠️ 「检查排课」浮层 (FixedCheckModal) **仍然是纯 DOM** —— 那个弹窗不需要任何
//   mithril 组件 (全是静态表格 + 按钮), 没有理由为了「统一」把它一起重写。
//   本仓「为什么是纯 DOM」的那条背景 (Flarum 2.0 的 `m` 是 Preact 不是 Mithril,
//   m.render() 不可用) 对它**依然成立**, 别照搬成「所有弹窗都得是 Modal 子类」。
import FixedLessonModal from '../modal/FixedLessonModal';
import FixedCheckModal from '../utils/FixedCheckModal';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason, errReasonSafe } from '../utils/FormPrimitives';
// v0.8.4 — 时段预留 modal (时段 + 原因, 无时长栏)
import ReservationModal from '../modal/ReservationModal';
// v0.6.7 — 桌面 dblclick / 移动 2-tap 时间格 → 打开约课 modal (辉哥 9/26 20:44 拍板)
//   ⚠️ v0.8.12 (辉哥 10/7 16:22 拍板) 这条入口**已移除**, 现在唯一入口是右键菜单 / 长按菜单。
// v0.6.11 — 课表日历日期选择器改 ISO text input (跟 ziven-bjxy-website BookingModal v0.2.0ebb16a0.c.f.m.e 同款, 辉哥 9/27 14:55 拍板)
//   旧 v0.6.10 中文 chip "2026年9月27日 | 周日 · 第40周" → ISO "2026-09-27" text input
//   跟 BookingModal 字段"预约日期"完全一致视觉 + 行为:
//     - 单 visible text input + flatpickr 接管 click (disableMobile:true 关 native picker)
//     - dateFormat 'Y-m-d' ISO, locale zh
//     - popover 跟 input 一样宽 (onReady 强制 width)
//     - SOP 105 + 106 三层组合保留: disableMobile + appendTo:body + LESS z-index:9999
// v0.7.9j (辉哥 10/5 22:51 拍板) — 当前 input 跟 flatpickr dateFormat 都改成 'Y年m月d日' 中文格式
//   (历史: v0.6.10 '中文' → v0.6.11 'Y-m-d' ISO → v0.6.12b 'Y/m/d' → v0.7.9j 'Y年m月d日')
// v0.6.11a — 返工修 V 测 HIGH 视觉 bug (辉哥 9/27 17:55 拍板选项 1):
//   V 测 v0.6.11 desktop 1280x880 popover 3/7 weekday 列被裁剪 (周四/五/六不可见).
//   根因: LESS input width 180 < flatpickr dayContainer min-width 308 → overflow:hidden 裁剪.
//   修法 (A+C combo): LESS input width 312 + min-width 312 + LESS .flatpickr-calendar min-width 312
//     + JS onReady Math.max(inputWidth, 312) 兜底. SOP 107 跨项目持久化 (任何 modal 用 flatpickr
//     input width 必须 >= 312 = 308 vendor dayContainer + 4 border 余量, 否则裁剪).
// v0.6.12 — 真跟 BookingModal 同款 (辉哥 9/27 18:17 拍板, 修正 v0.6.11a 形式过 / 视觉错位):
//   v0.6.11a 7/7 PASS 形式过, 但辉哥视觉对比发现跟 BookingModal 实际视觉差异显著:
//     - v0.6.11a dark theme + 中文 + 312px ≠ BookingModal light blue + 英文 + 335px
//   根因 (SOP 108): "代码一样 = 视觉同款" 假设错了. BookingModal bjxy.top 实际:
//     1. 没 LESS dark theme 覆盖 (走 vendor 默认 light blue + bjxy LESS var(--bjxy-bg)=#FFFFFF)
//     2. bjxy.top 不加载 zh.js (locale fallback default 英文 → "September 2026" + "SUN MON TUE WED THU FRI SAT")
//     3. input width 335px (modal 容器自然宽, 不是 312)
//   v0.6.12 修正:
//     - LESS 删 25 条 dark theme 覆盖 → vendor 默认 light blue theme 生效 (白底 + 蓝 selected #569ff7)
//     - LESS input width 312 → 335 (跟 BookingModal input width 同款)
//     - LESS .flatpickr-calendar min-width 312 → 335 (SOP 107 阈值升级)
//     - JS locale: 'zh' fallback → 强制 'default' 英文 (跟 BookingModal bjxy.top 实际视觉一致)
//     - JS onReady Math.max(inputWidth, 312) → Math.max(inputWidth, 335) (跟 input width 对齐)
//     - LESS input 样式: 暗色面板上 input 改白底深字 (跟 light blue popover 视觉协调)
//     - forum/index.js 删 zh.js 加载 (locale fallback default 英文)
//   SOP 108 (跨项目持久化, 本次新增): "同款" 任务必须实际对比视觉, 不能假设 "代码一样 = 视觉同款".
//     跨包扩展实施同款前, 必须先访问目标包实际页面截图 + vendor CSS + LESS 覆盖 全部确认一致.
//   SOP 107 阈值升级 (312 → 335): 任何 modal/tab 用 flatpickr, input width 必须 >= 335px
//     (v0.6.12 升级: 312 → 335, BookingModal 同款), 否则视觉裁剪.
// v0.6.12a — 字母链返工 (辉哥 9/27 18:30 拍板, 修 v0.6.12 走错方向 SOP 108 FAIL):
//   V 测 v0.6.12 真视觉对比发现 (Puppeteer desktop 截图实测):
//     - BookingModal 实际显示中文 ("九月 2026" + "周日 周一 周二..."), 不是英文
//     - 辉哥 iPhone Safari 截图描述英文, 可能是 user 描述误差 / timezone / region
//   v0.6.12 错位: v0.6.12 看到 user 描述英文 + 没真截图 BookingModal, 就把 zpc-scheduler 改成英文:
//     - 删 zh.js 加载
//     - locale: 'zh' fallback → 'default'
//     - override window.flatpickr.l10ns.default = 英文
//   v0.6.12a 修法:
//     - 恢复 zh.js 加载 (forum/index.js L70)
//     - locale: 删 override, 改回 zh fallback (跟 v0.6.11/11a 一致)
//     - 删 window.flatpickr.l10ns.default override
//     - 保留 LESS width:335 + light theme + 7 列完整 (跟 v0.6.12 一致, 不回滚 dark theme)
//     - 保留 SOP 105/106/107 修复
//   SOP 108 强化 (V 测 9/27 18:30 实战沉淀, 本次强化):
//     - '同款' 任务不能假设 user 描述 = 实测视觉 (user 描述可能错, 实测为准)
//     - 必须 puppeteer 截图目标包真实页面 + 当前实施页面, 对比 theme + locale + width + vendor 全部确认一致
//     - 跨包扩展实施同款前的视觉对比 checklist (5 项):
//       1. theme (LESS 覆盖规则)
//       2. locale (加载哪些文件 + zh/en/default fallback + vendor 默认是中文还是英文)
//       3. width (input 实际 width + 容器自然宽)
//       4. vendor 版本 (是否定制, 跟 upstream 区别)
//       5. 实际截图 (puppeteer 截图目标包真实页面 + 当前实施页面, 对比)
// v0.6.37 — 约课 modal 入口抽到 utils/BookingModalHelper (辉哥 9/29 23:46 反馈, DRY)
//   移除 LessonEditorModal 直接 import + app.modal.show(LessonEditorModal, ...) setTimeout 块,
//   改调 openBookingModal({mode, lesson, start, end, room, activeSchoolId, onClose})
//   行为不变: 时间格 → mode=create/edit 弹 modal, start/end/room 预填
//   ⚠️ v0.8.12 起入口是右键菜单 / 长按菜单 (双击 + 2-tap 已移除), helper 本身签名没变。
// v0.6.12b — 字母链返工 mobile native picker 适配 (辉哥 9/27 19:20 真机反馈 + 19:53/20:11 拍板 D 方案):
//   辉哥 9/27 19:20 在 iPhone Safari 真机看 bjxy.top zpc-scheduler 课表:
//     - 桌面 v0.6.12a 100% 同款 (16 个 CSS 属性 + 内容 5 维度全过)
//     - mobile v0.6.12a 不一样: BM 走 native iOS picker (input '2026/09/27' + 📅 native icon),
//       zpc 走 flatpickr popover (input '2026-09-27', 右侧没 native icon)
//   根因:
//     - BM BookingModal.jsx flatpickr config 没传 disableMobile (默认 false), mobile 走 vendor 默认 native picker
//     - zpc CalendarTab.js v0.6.12a 有 disableMobile: true, 强制 flatpickr popover (mobile 体验不 native)
//   v0.6.12b 修法 (D 方案, 辉哥 9/27 20:11 拍板):
//     - JS 删 disableMobile: true → 改 JS 显式 UA 检测: disableMobile = !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
//       桌面走 disableMobile true → flatpickr popover (跟 v0.6.12a 桌面不变)
//       mobile 走 disableMobile false → native iOS picker (跟 BM mobile 100% 同款)
//     - dateFormat 'Y-m-d' → 'Y/m/d' (跟 BM mobile native picker format 一致, 桌面 popover 不受影响)
//     - view() dateStr 改 'Y/m/d' 同步 (mobile native picker 渲染 input value 用此格式)
//     - LESS mobile media query 改造 (桌面 LESS 完全不动, 辉哥约束 #2):
//       - padding: 0 (辉哥约束 #3: 两边不要额外 padding)
//       - text-indent: 12px (替代 left padding, 文字缩进)
//       - height: 44px (iOS HIG 标准点击区)
//       - font-size: 16px (SOP 105 防 iOS Safari auto-zoom)
//       - appearance: none (iOS Safari 去掉 native input 默认 appearance, 让样式生效)
//       - border: 0, background: transparent, color: #e8ecf1 (dark theme 适配)
//   SOP 108 强化 (本次深化, V 测 9/27 19:20 实战沉淀):
//     - '同款' 任务必须 puppeteer 实测 desktop + mobile 双 viewport, 不只 desktop
//     - v0.6.12a 桌面 100% 同款, 但 mobile 不一样 (native picker vs flatpickr popover)
//     - 跨包扩展实施同款前必须 dual-viewport (desktop >= 641px + mobile <= 640px) 实测 5 维度对比
//     - "同款" 任务不能假设 user 描述 = 实测视觉 (user 描述可能错, 实测为准)
//     - 必须 puppeteer 截图目标包真实页面 + 当前实施页面, 对比 theme+locale+width+vendor 全部确认一致
//     - 跨包扩展实施同款前的视觉对比 checklist (5 项):
//       1. theme (LESS 覆盖规则)
//       2. locale (加载哪些文件 + zh/en/default fallback + vendor 默认是中文还是英文)
//       3. width (input 实际 width + 容器自然宽)
//       4. vendor 版本 (是否定制, 跟 upstream 区别)
//       5. 实际截图 (puppeteer 截图目标包真实页面 + 当前实施页面, 对比)
//   辉哥 18:17 反馈: v0.6.11a 7/7 PASS 形式过, 但视觉对比发现跟 BookingModal 实际视觉差异显著:
//     - v0.6.11a dark theme + 中文 + 312px ≠ BookingModal light blue + 英文 + 335px
//   根因: "代码一样 = 视觉同款" 假设错了. BookingModal bjxy.top 实际:
//     1. 没 LESS dark theme 覆盖 (走 vendor 默认 light blue + bjxy LESS var(--bjxy-bg)=#FFFFFF)
//     2. bjxy.top 不加载 zh.js (locale fallback default 英文 → "September 2026" + "SUN MON TUE WED THU FRI SAT")
//     3. input width 335px (modal 容器自然宽)
//   v0.6.12 修正:
//     - 删全部 LESS dark theme 覆盖 (25 条) → 让 vendor 默认 light blue theme 生效
//     - LESS input width 312 → 335 (跟 BookingModal input width 同款)
//     - LESS .flatpickr-calendar min-width 312 → 335 (SOP 107 阈值升级)
//     - JS locale: 'zh' fallback → 强制 'default' 英文 (跟 BookingModal bjxy.top 实际视觉一致)
//     - JS onReady Math.max(inputWidth, 312) → Math.max(inputWidth, 335) (跟 input width 对齐)
//     - LESS input 样式: 暗色面板上 input 改白底深字 (跟 light blue popover 视觉协调)
//     - forum/index.js 删 zh.js 加载 (locale fallback default 英文)
//   SOP 108 (跨项目持久化, 本次新增): "同款" 任务必须实际对比视觉, 不能假设 "代码一样 = 视觉同款".
//     跨包扩展实施同款前, 必须先访问目标包实际页面截图 + vendor CSS + LESS 覆盖 全部确认一致.
// v0.6.37 — LessonEditorModal 直接 import 移除 (改用 utils/BookingModalHelper.openBookingModal, helper 自己 import modal)
import openBookingModal from '../utils/BookingModalHelper';

// v0.8.11 — 'YYYY-MM-DD HH:MM:SS' → Date
//   必须跟 SlotReservationRenderer.js 里的 parseLocal() 完全同语义:
//   后端 ListSlotReservationsController 返回的是**空格分隔**的时间串,
//   直接 new Date(rsv.start_at) 在 Safari 上会返回 Invalid Date (空格格式非 ISO)。
//   这里刻意不 import 那个文件里的 parseLocal —— 它是模块私有的, 复制一份并注明同源,
//   比重构 SlotReservationRenderer 的导出面更小 (改一个纯函数的可见性会牵动 import 图)。
function zsParseLocal(dtStr) {
  if (!dtStr) return null;
  return new Date(String(dtStr).replace(' ', 'T'));
}

// v0.15.0 — 时段预留块从「自绘 DOM」迁到 FullCalendar event (辉哥 10/10 拍板 A 方案)
//   迁移原因见 CHANGELOG v0.15.0。两条硬约束记录在下面, 它们是本次实现的骨架, 别删。
//
// 【1】预留块的 FC event id **必须**带这个前缀。
//   eventDrop / eventResize / eventAllow 全靠它分流 —— 同一个日历里既有课块又有预留块,
//   回调拿到 event 后第一件事就是判断「这是不是预留」。拿 prefix 判断而不是看 classNames,
//   是因为 classNames 是渲染层的东西, 回调里拿到的 EventApi 不保证带 UI class。
const ZS_RSV_PREFIX = 'rsv_';

// ─── v0.19.00 静默轮询: 「待还原的视图状态」的寿命 ──────────────────────────
//
//   静默刷新会重建 FC 实例 ⇒ 用户当前的视图日期/滚动位置要自己存 + 自己还原。
//   还原值必须挂在**每一次** FC 重建之后 (见 tryMountCalendar 末尾的
//   _applyPendingViewRestore) —— 因为 load() 的 finally 里 m.redraw() 是**异步**的
//   ⇒ 它引发的 onupdate 会再重建一次, checkConflicts 收尾还会再重建一次。
//
//   ⚠️ 但还原值**不能无限期留着**: 一旦日后有人给 FC 加了导航按钮, 或新增了不走
//   load() 的改日期入口, 残留的旧日期会把用户的视图**拨回旧日期**。
//   ⇒ 给它一个明确的寿命: 窗口内随重建重放, 窗口一到就主动清掉
//   (宁可少还原一次, 也不能拨错日期)。
//
//   窗口取 8s: 要盖住「load 收尾 → onupdate(rAF) → checkConflicts 收尾」这一串重建
//   (实测 checkConflicts 是**逐条**串行 POST, 课次多的那天可能到秒级), 又不至于长到
//   能跨过一次用户操作。
const ZS_POLL_VIEW_RESTORE_WINDOW_MS = 8000;

// ⚠️ v0.17.57 — 这里原来有一份 ZS_DAY_LABELS (星期几中文文案), 已随
//   businessHoursViolation / _businessHoursMessage 一起删除: 那两个是它唯一的两个使用者。
//   它只服务「营业时间窗口」文案, 而 business_hours 已整体下线; 新的课表也不按星期几分档。
//   留着一个没人引用的 const 会被打包器/tree-shaking 当成可疑的死代码, 故一并清掉。

// 【2】预留块必须走**独立的 EventSource**, 不能混进课块那个 source。
//   ⚠️ v0.15.1 **订正**: 下面这段的立论在 v0.15.0 里写错了, 当年的结论「per-event 写
//   durationEditable 不生效, 所以必须拆 source」**不成立**, 已实测推翻。别再照着它推理。
//   准确的事实 (FC 6.1.21 vendor 实测, index.global.min.js):
//     · source 级:  `To()` = {...Ke, ...wo, ...eventSourceRefiners}, Ke 含 durationEditable
//     · event  级:  `ct()` = {...Ke, ...st, ...eventRefiners}, 同一个 Ke
//     · 建 event 时 `ui: tt(i, t)` —— tt() 直接从**事件输入 i 自己**上读
//       `null != e.durationEditable ? e.durationEditable : e.editable`
//   ⇒ per-source 与 per-event **都生效**。V 测隔离矩阵实测: 按 source 配 → 课块 0 手柄 /
//     预留 2 手柄; 只按 per-event 配 → 结果完全相同。
//
//   那为什么**仍然**保留两条 source? 因为真正挡住「课块长出手柄」的不是"拆分"这件事本身,
//   而是课块那条上**显式写死的** `durationEditable: false`。实测 E 组: 把全局
//   `eventDurationEditable` 打开而不限定, 课块立刻长出 2 个手柄 ⇒ 那条 false 是唯一防线。
//   用 source 级写法的好处只是「显式」: 防线的位置贴着课块、意图写在事件源旁边, 不会被
//   人顺手合并掉。**若日后有人改成单 source + per-event, 必须先确认课块也显式写了 false。**

/**
 * CalendarTab — 真 FullCalendar v6.1.19 (修复 r.1 FAIL CRITICAL 2 + r.2 FAIL "this.apply is not a function")
 *
 * v0.5.0c SCD 升级:
 *   - 进入课表时 GET /api/ziiven/scheduler/schools/{school_id}/time-schedule
 *   - 用返回的 lesson_duration_minutes / break_duration_minutes / open_time / close_time
 *     算 slotDuration, slotMinTime, slotMaxTime
 *   - 没配 business_hours: 兜底用默认 08:00-22:00 / 30min slot
 *
 * 5 重碰撞检测后端完整跑通 (LessonConflictChecker, v0.5.0c SCD 查询)
 *   - 前端 POST /api/ziiven/scheduler/lessons/check-conflict 防抖检测
 *   - 冲突事件加 .conflict class 红色高亮
 *
 * ⚠️ 注意: 不要用 `this.view = ...` (会覆盖 mithril prototype 上的 view() 方法!)
 *           改用 `this.calendarView` 避免破坏 mithril ClassComponent lifecycle
 *           Component 基类用 this.element (不是 this.dom) 存根 DOM
 *
 * v0.6.7 (辉哥 9/26 20:44 拍板):
 *   - 桌面 dblclick 时间格 → 打开 LessonEditorModal 自动填: 日期 + 开始时间 + 教室
 *   - 移动 2-tap (300ms 内同槽) → 同样打开 LessonEditorModal
 *   ⚠️ v0.8.12 (辉哥 10/7 16:22 拍板) — 上面两条双击/双触入口**已移除**。
 *     打开约课/编辑约课现在只有: 桌面右键菜单 (气泡) / 移动长按菜单 (底部面板)。
 *     LessonEditorModal 本身、mode=create/edit 语义、start/end/room 预填逻辑一律没动。
 *   - 隐藏 view switcher (只展示"日(按教室)" resourceTimeGridDay)
 */
export default class CalendarTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.calendarView = 'resourceTimeGridDay';
    this.currentDate = new Date();
    this.lessons = [];
    this.rooms = [];
    this.conflicts = [];
    // ── v0.17.57 课表节点 (新接口 GET /schools/{id}/schedule) ─────────────
    //   ⚠️ v0.17.85 — 「当前生效」= **日历当前展示那一天**生效的那一版, 不再恒等于「今天」。
    //     load() 现在带 `?date=` 去取 (见下面 tasks.push 那段), 而切日期一定重走 load()
    //     (flatpickr onChange / setDate 两条路都调它), 所以本字段永远跟 currentDate 对齐。
    //   scheduleNodes: 该校区**那一天生效**的课表的时间段清单, 已按 sort_order 升序。
    //     每项 = 后端 ScheduleResolver::serializeNodes() 的原样:
    //       { id, sort_order, start_time: 'HH:MM', end_time: 'HH:MM' }
    //     null = **那天没有可用课表** (接口 404 / reason: 'not_configured' / 'stale')。
    //     ⚠️ v0.17.61 起这**不再是兜底渲染态**: hasSchedule() 为 false 时整个 fc 都不建,
    //       改渲染一块「去配置课表」的提示 (见 scheduleState() 与 utils/ScheduleMissingNotice.js)。
    //       改前是退回「真实时间刻度 + 老节拍 (30min) + 08:00-22:00 轴」(v0.17.56 之前的样子),
    //       那条路看着「正常」但左列刻度跟真实排课规则毫无关系, 比明确报错更误导人。
    this.scheduleNodes = null;
    //   课表版本元信息 ({ id, version_name, effective_from }), 左列标题/提示用, 没有也不影响渲染。
    this.scheduleVersion = null;
    // ── v0.17.61 「还没问出结论」与「问过了, 就是没有」必须分开 ──────────────
    //   改前 scheduleNodes === null 同时表示这两件事 (首帧 / 换校区 / 请求在途 / not_configured
    //   / stale / fetch 失败), 任何拿它当「有没有课表」判据的地方都会在加载完成**之前**
    //   先闪一次「没配课表」—— 那是误报, 不是状态。
    //   scheduleResolved 只由**课表请求自己**翻转 (见 load()), 不跟外层 load() 的成败走:
    //   lessons / rooms 任一失败都不该让「这个校区没配课表」变成误报。
    this.scheduleResolved = false;
    // ── v0.17.72 「问出结论了, 但结论是**问失败了**」 ──────────────────────
    //   改前 `.catch(() => null)` 把 403/500/断网/超时一律压成 null, 而 null 正是
    //   「该校区没配课表」那个值 ⇒ **后端说「你没权限」, 前端显示「你没配置」**。
    //   学员诺诺 10/16 的现象就是这么来的 (数据全在位, 是课表端点 403 了)。
    //   这个旗只由**课表请求自己的失败分支**置位, 与 lessons/rooms 无关 ——
    //   它们失败不该让「课表」这一项变成 error, 那是另一件事。
    this.scheduleFailed = false;
    // v0.8.4 — 时段预留 (辉哥 10/6 拍板, 课表按格吸附的预留块)
    this.slotReservations = [];
    this.loading = true;
    this._fcRetryTimer = null;
    // ── v0.19.00 静默轮询 (课表日历自动更新) ────────────────────────────────
    //   _fp:        指纹基准 (null = 还没有基准)。null 与「基准已建立」是**两件事**,
    //               别合并 —— 见 _pollOnce() 第 ⑧ 步的注释。
    //   _polling:   并发保护 (一轮没跑完就又到点了 ⇒ 直接跳过这一轮)。
    //   _dragging:  S2 「用户正在拖拽/拉伸 event」标志 (由**已有的** eventDragStart/Stop
    //               + eventResizeStart/Stop 回调置/清, ⛔ 不新增任何 FC 回调)。
    //   _pollTimer / _pollVisHandler: 定时器 + visibilitychange 监听句柄 (onremove 必须清)。
    //   _removed:   组件已卸载 (onremove 置位) ⇒ 轮询在途时不再碰界面。
    //   _pollRefreshing: 「当前这次 load() 是轮询触发的」⇒ load() 据此**不清**指纹基准。
    //   _pendingViewRestore: 待还原的「视图日期 + 滚动位置」(静默刷新会重建 FC ⇒ 会丢)。
    this._fp = null;
    this._polling = false;
    this._dragging = false;
    this._pollTimer = null;
    this._pollIntervalMs = 0;
    this._pollVisHandler = null;
    this._removed = false;
    this._pollRefreshing = false;
    this._pendingViewRestore = null;
    // v0.19.00 F3 — 「待还原」的清场 timer (还原窗口到了就把残留值清掉)
    this._pendingViewRestoreTimer = null;
    // v0.6.10 — flatpickr 实例 (单 chip 触发日期选择器, 辉哥 9/27 B 方案)
    this._flatpickrInstance = null;
    // ── v0.10.1 日期选择器挂载状态 (Bug: 切模式回来后彻底点不动, 无任何报错) ──
    //   _datePickerMounting: 正在 await mountDatePicker 的"在途"标记。
    //     v0.10.0 只有 `if (this._flatpickrInstance) return;` 一个守卫, 而这行在 await **之前**,
    //     await 期间实例仍是 null → 并发调用能同时过守卫 (V 测实测切模式时确实并发)。
    //   _datePickerInputEl: 实例当前绑定在哪个 DOM 节点上。
    //     mithril 重绘可能换掉这个 input (切模式必然换), 只看"实例非空"会漏判"实例已失效"。
    this._datePickerMounting = false;
    this._datePickerInputEl = null;
    // v0.10.1 — 在途 mount 期间别处请求对账时, 记下它看到的 input, 由在途那次收尾时复查
    this._datePickerPendingEl = null;
    // v0.10.1 — onremove 发生在一次 mount 在途期间时置位, 防止实例挂到已卸载的组件上
    this._datePickerDestroyedDuringMount = false;
    this.activeSchoolId = vnode.attrs.activeSchoolId;
    // ── v0.9.0 固定课双模式 (辉哥 10/7 拍板 B9: 移动端同一套切换, 不另开 tab) ──
    //   calendarMode: 'normal' 正常约课 / 'fixed' 固定课
    //   fixedDayOfWeek: 固定课模式下选中的星期几, **0=周日..6=周六** (跟后端 day_of_week 同口径,
    //     这里必须跟后端一致 —— 换成 ISO 1..7 会整体错一天, 且 UI 上看不出来, 只表现为
    //     「选了周三却显示周二的课」, 极难自查)
    this.calendarMode = 'normal';
    this.fixedDayOfWeek = new Date().getDay();
    this.fixedLessons = [];
    // v0.8.4 — 预留渲染器实例 (v0.15.0 后只剩 _checkConflict + 右键菜单入口两件事在用它,
    //   自绘 DOM / 四角手柄 / ghost 那部分已整体删除)
    this.slotReservationRenderer = new SlotReservationRenderer({
      calendarTab: this
    });
    // v0.15.0 — 本次 resize 时记的「抓的是上边还是下边手柄」(eventDidMount 的 pointerdown 写,
    //   showResizeDirectionFlag 读)。见该方法注释里的 vendor 出处。
    this._rsvResizeEdge = null;
    this._rsvDirFlagEl = null;
    this.load();
  }

  // r.3 FIX: 父异步换校区时同步 attrs
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    if (vnode.attrs.activeSchoolId !== this.activeSchoolId) {
      this.activeSchoolId = vnode.attrs.activeSchoolId;
      // v0.17.57 — 换校区必须同时清课表, 否则会用**上一个校区**的时间段画新校区的左列
      this.scheduleNodes = null;
      this.scheduleVersion = null;
      // v0.17.61 — 同理清「已问出结论」这个标记, 否则切到没配课表的校区会**沿用上一个校区
      //   的结论**, 直接把提示块藏起来 (加载态与缺失态都串台)。
      this.scheduleResolved = false;
      // v0.17.72 — 上一个校区的**失败**结论同样不能带到新校区 (理由同上, 串台)
      this.scheduleFailed = false;
      this.lessons = [];
      this.rooms = [];
      this.conflicts = [];
      this.slotReservations = []; // v0.8.4 — 切换校区清空
      this.fixedLessons = []; // v0.9.0 — 固定课模板同样按校区隔离, 换校区必须清
      this.load();
    }
  }

  /**
   * v0.8.10 — Date → 本地 'YYYY-MM-DD' (v0.6.39 修过一次, v0.8.10 抽成共用方法)
   *
   * 改前 (为什么要有这个方法):
   *   同一个文件里有两处需要"用户本地日历日"的字符串, 历史上是各写一份:
   *     ① load() L193-197  — v0.6.39 已修成 getFullYear/getMonth/getDate (本地分量)
   *     ② openBooking() L892 — 忘了改, 仍是 this.currentDate.toISOString().slice(0, 10) (UTC)
   *   toISOString() 会先转成 UTC 再 slice, 东八区 (+08:00) 本地 00:00-07:59 的时刻
   *   转成 UTC 就是前一天 → 建课 modal 里日期比表头少一天, 真机会把课约到前一天。
   *   v0.8.10 修 ②, 并把两处收敛到这一个方法 —— 少写一份就少漏改一次。
   *
   * ⚠️ 不要拿它去格式化要发给后端的时间戳:
   *   openBooking 里 startDate.toISOString() / endDate.toISOString() (L902-903)
   *   是**绝对时刻** (告诉后端"这个瞬间是几点"), 用 UTC 是对的, 不要改成本地分量,
   *   改了会引入新的跨时区偏差。只有"用户选了哪一天"这种日历日语义才用本方法。
   */
  localDateStr(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  // ─── v0.9.0 固定课模式 ───────────────────────────────────────────────

  /**
   * v0.17.76 (辉哥 10/16 拍板) — 「课表日历只读化」的唯一入口判定
   *
   * 【它是什么】用户 zpc 角色 == 'student' 的**派生**标记, 由后端
   *   `Api/Attributes/UserSchedulerPermissions::isStudent` 用
   *   `RoleResolver::resolve($actor) === 'student'` **严格相等**算出来, 不是权限位。
   *   ⚠️ 判据绝不能换成 SchoolContext::detectRole() —— 那个内部是 `resolve() ?? 'student'`,
   *     Mod (group 4) 不在任何 role 映射里 ⇒ 返回 null ⇒ 被 fallback 成学员,
   *     **Mod 会当场失去全部编辑能力**。本仓最大的回归风险就押在这一行上。
   *
   * 【为什么只认 app.session.user】后端这个字段取的是 **actor**(当前登录者自己),
   *   不是被序列化的那个 user model ⇒ 拿它去判断「别人是不是学员」是错的。
   *
   * 【session 缺失时为什么 fail-open (返 false = 按员工处理)】
   *   SchedulerIndexPage.normalView() 在 `!(app.session && app.session.user)` 时整个 tab 区
   *   都不渲染 (v0.17.27 加的加载态), 所以本方法实际跑不到「没有 session user」的分支。
   *   真跑到了, 返 false 意味着**保持 Mod/Admin 既有行为不变** ——
   *   宁可少收一次只读, 也不让任何一个人意外失去已有的能力。
   *   ⚠️ 别把它改成 fail-closed 来「更安全」: 那会在 session 慢的窗口里把 Mod 也收成只读,
   *     正是本轮明令禁止的回归。
   */
  isStudentView() {
    const u = app.session && app.session.user;
    if (!u || typeof u.isStudent !== 'function') return false;
    return !!u.isStudent();
  }

  /**
   * v0.17.77 (辉哥 10/16 拍板) — 「约课 / 编辑已约课程 / 取消约课」的前端权限位判据
   *
   * 【为什么改成权限位判定】
   *   v0.17.76 是按 `isStudentView()` 一刀切 (是学员就什么都看不见)。辉哥这轮要的是
   *   「**后台勾了权限的用户组**就能约课/编辑/取消」—— 判据必须换成权限位, 而不是角色。
   *   典型受益方: 滑雪教练 / 销售 / 店长这几个组现在一个 scheduler 位都没有,
   *   勾上 editBookedLessons 就能改自己校区里学员的约课, 而不用去碰 manageLessons
   *   (那会顺带放开课表版本 / 固定课模板 / 时段预留三大块)。
   *
   * 【_sessionPerm 为什么在 getter「不存在」时返 true (fail-open)】
   *   ⚠️ 这跟 isStudentView() 的 fail-open 是同一个理由, 方向一致:
   *     getter 缺失只有一种成因 —— 打进页面的这个包没注册这两个 attribute
   *     (发版没跟上 / 浏览器缓存了旧包)。那时候如果 fail-closed, 后果是
   *     **Mod 打开右键菜单发现一项都没有**, 也就是一次静默的全员能力回退。
   *     返 true 保住的是 v0.17.77 之前的既有行为, 最坏情况只是菜单项多了几个,
   *     而每一个动作后端 UpdateLessonController 都会独立再判一次权限 —— 菜单项只是入口,
   *     不是授权。**前端放行不等于后端放行**, 这是这里敢 fail-open 的全部底气。
   *   ⚠️ 别把它改成 fail-closed「看起来更安全」: 那会在发版窗口里把 Mod 锁死。
   *
   * 【注意与 isStudentView 的分工 —— 两者不是二选一】
   *   isStudentView 继续管「日历只读化」(不渲染模式切换条 / 钉死 normal 模式 /
   *   不发 fixed-lessons 请求 / 拖拽缩放双层拦截), 本轮一个字没动它。
   *   本轮只把「菜单里给不给某一项」换成权限位。
   */
  _sessionPerm(name) {
    const u = app.session && app.session.user;
    if (!u || typeof u[name] !== 'function') return true; // fail-open, 见上方说明
    return !!u[name]();
  }

  /** 约课 —— 复用既有 scheduler.bookLessons (辉哥没要求新增第三个位) */
  canBookLessons() {
    return this._sessionPerm('canBookLessons');
  }

  /** 编辑已约课程 —— scheduler.editBookedLessons */
  canEditBookedLessons() {
    return this._sessionPerm('canEditBookedLessons');
  }

  /** 取消约课 —— scheduler.cancelBookedLessons */
  canCancelBookedLessons() {
    return this._sessionPerm('canCancelBookedLessons');
  }

  /** 重权限位 —— scheduler.manageLessons (课表版本 / 固定课 / 时段预留) */
  canManageLessons() {
    return this._sessionPerm('canManageLessons');
  }

  /**
   * v0.17.76 — 模式恒定出口: 学员永远 'normal'。
   * 模式只有两个来源 (已实测: oninit 的 'normal' + setCalendarMode; 本组件没有
   * localStorage / hash / URL query 恢复路径, grep 确认), 所以把 setCalendarMode 钉死
   * 就等于堵死了**全部**能翻成 'fixed' 的路径 —— 包括将来新增的深链入口。
   *
   * ⚠️ v0.17.76 落地时删过一次一个 `effectiveCalendarMode()` getter (所有读取点都改走
   *    `this.calendarMode` 直读)。它在实现里**一个调用点都没有** —— 因为「模式恒为 normal」
   *    是靠**写入口钉死**保证的, 不是靠读出口兜底; 留着一个没人调的 getter 只会让人
   *    以为改它就能改行为。判据仍然只有 `setCalendarMode` / `setFixedDayOfWeek` 那两处。
   */

  /**
   * 固定课模式下实际要查的那一天 = 「currentDate 所在那一周里, 选中星期几的那天」
   *
   * 为什么保留 this.currentDate 作为「锚」而不是直接改它:
   *   切回正常模式时要把日期选择器还给用户, 锚点不变 → 用户看到的是他最后选的那天,
   *   不会因为在固定课模式待了一会儿就被莫名其妙切到别的日期。
   *
   * delta 用 getDay() 的 0..6 口径, 跟后端 fixed_lessons.day_of_week 完全一致 (0=周日)。
   * 跨周正确: 周三(3) 选周日(0) → delta = -3 → 上一周的周日。
   */
  fixedModeDateStr() {
    const base = this.currentDate instanceof Date ? this.currentDate : new Date();
    const baseDow = base.getDay();
    const delta = this.fixedDayOfWeek - baseDow;
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + delta);
    return this.localDateStr(d);
  }

  /** 周一→周日 显示顺序 (业务直觉), 值仍是 0..6 (0=周日) —— 唯一需要转换的一处 */
  fixedDowOptions() {
    return [['一', 1], ['二', 2], ['三', 3], ['四', 4], ['五', 5], ['六', 6], ['日', 0]];
  }

  /**
   * v0.9.2 (辉哥 10/7 拍板) — 当前模式下「时段预留」可不可见 / 可不可建
   *
   * 决策: 固定课模式下预留**完全不可见、不可创建, 但数据保留** (切回正常模式原样回来)。
   *   刻意**只 gate 渲染, 不动数据** —— 不 DELETE、不软删、不加任何字段标记,
   *   因为预留是别的业务 (占位/维护/会议) 占的, 固定课模式无权替别人删。
   *
   * 为什么隐藏之后**必须**保留冲突判定 (这是该决策的核心, 不是附带条件):
   *   如果只隐藏、而冲突检查也一起忽略预留, 就会造出「看不见但存在」的陷阱 ——
   *   管理员看着 slot 空着, 实际被预留占着, 另一个学员来正常约课时会被**神秘拒绝**且无从排查。
   *   所以固定课建模板 / 单次课 / 检查排课时, 后端仍按预留算占用, 并且**指名道姓**报错
   *   (见 FixedLessonService::assertNoReservationOverlap), 让用户知道该去正常课表取消它。
   *
   * 单一事实源: 两个渲染点 (load() 收尾 / tryMountCalendar) 都走 _syncReservationBlocks(),
   *   不各写一份判断 —— 两处各写一份是这类 gate 最常见的漏点。
   */
  reservationsVisibleInCurrentMode() {
    return this.calendarMode !== 'fixed';
  }

  /**
   * v0.8.4 — 把当前该渲染的预留交给渲染器 (固定课模式传空数组; 渲染器内部先清旧 DOM, 不会残留)
   *
   * v0.15.0 (辉哥 10/10 拍板) — 预留块已迁成 FC event, 这里**不再**做任何 DOM 渲染。
   *   现在唯一该干的事是: 数据变了就用最新数据把日历重建一遍。
   *   tryMountCalendar() 是本组件唯一把事件源喂给 FC 的地方, 所以这里只做转发 ——
   *   两处各写一份同步逻辑正是 v0.9.2 注释里点名要避免的漏点。
   *   ⚠️ 调用点只剩 unreserveSlot() 一处 (删掉一条预留后立刻从日历上消失);
   *      load() 收尾和 tryMountCalendar() 末尾那两个旧调用点已删 ——
   *      保留 tryMountCalendar 里这一处会变成它调用自己 → 无限重建。
   */
  _syncReservationBlocks() {
    if (!this._calendar) return; // 日历还没挂上: tryMountCalendar 挂上时会自己带最新数据
    this.tryMountCalendar();
  }
  setCalendarMode(mode) {
    // v0.17.76 — 学员只读: 模式恒为 'normal'。这不只是「把按钮藏起来」,
    //   这里是**唯一**能把 calendarMode 翻成 'fixed' 的入口, 钉死它等于堵死全部路径
    //   (含日后新增的深链 / 状态恢复)。UI 侧另外还在 render 里不渲染模式切换条。
    if (this.isStudentView()) mode = 'normal';
    if (this.calendarMode === mode) return;
    this.calendarMode = mode;

    // ⚠️ flatpickr 实例跟 DOM 节点绑死 (v0.9.0 实测必须处理):
    //   切到固定模式后 .ZS-date-input-text 被 mithril 重绘摘掉, 但 _flatpickrInstance
    //   还指着一个**已经从 document 里移除的节点**。若不销毁, 切回正常模式时
    //   幂等守卫会直接跳过, 结果就是「日期选择器再也不弹了」—— 而且不报任何错。
    // v0.10.1 — 统一走 _destroyDatePicker(): 顺手把 _datePickerInputEl 清掉,
    //   否则下次 _syncDatePicker 拿到新 input 时会以为"实例还绑着老节点"而重挂 (其实该重挂),
    //   或者反过来实例非空但节点已废而被误判为正常。
    this._destroyDatePicker();

    // 切模式时把 FC 的可见日期也拨到对应那天, 否则表头日期和内容对不上
    const target = mode === 'fixed' ? this.fixedModeDateStr() : this.localDateStr(this.currentDate);
    if (this._calendar) {
      try {
        const parts = target.split('-').map(Number);
        this._calendar.gotoDate(new Date(parts[0], parts[1] - 1, parts[2]));
      } catch (e) {/* noop */}
    }
    // v0.10.1 — 这里**不再**用 setTimeout(0) 抢时机 (实测那个 timer 比 DOM 早 ~20ms, 必然扑空)。
    //   现在是"先对账一次(通常还扑空, 无副作用), 真正的补挂交给 onupdate / load 收尾"。
    //   两个调用点都在同一个 redraw 周期内, 不引入额外延迟。
    if (mode === 'normal') {
      this._syncDatePicker();
    }
    this.load();
  }
  setFixedDayOfWeek(dow) {
    // v0.17.76 — 学员永远进不了固定模式 (setCalendarMode 已钉死),
    //   这里再挡一道: 万一将来有人加了别的入口, 也不会让 fixedDayOfWeek 跟着动。
    if (this.isStudentView()) return;
    this.fixedDayOfWeek = dow;
    if (this.calendarMode === 'fixed') {
      const target = this.fixedModeDateStr();
      if (this._calendar) {
        try {
          const parts = target.split('-').map(Number);
          this._calendar.gotoDate(new Date(parts[0], parts[1] - 1, parts[2]));
        } catch (e) {/* noop */}
      }
      this.load();
    } else {
      m.redraw();
    }
  }

  /** 这节课是不是固定课的某一次 */
  isFixedLesson(l) {
    return !!(l && l.fixed_lesson_id);
  }

  // ─── v0.9.0 固定课模式 ───────────────────────────────────────────────

  /**
  * v0.9.0 — **课表当前实际展示的那一天** (唯一的日期口径出口)
  *
  * 正常模式 = 用户用日期选择器选的那天
  * 固定模式 = 「currentDate 所在那一周里, 选中星期几的那天」
  *
  * ⚠️ 为什么必须收敛成这一个方法 (而不是各处各算一遍):
  *   load() / openBooking() / openReserveModal() / openAddFixedLesson() 都各自要日期。
  *   固定模式下它们**全都**得用固定模式的那个日期, 漏改一处就会出现
  *   「表头显示 10/14, 约课弹窗却预填 10/07」这种对不上的 bug ——
  *   而且用户点提交之前完全看不出来, 约到错的那天。
  *   收敛成一处之后, 新增调用点默认就是对的。
  */
  activeDateStr() {
    return this.calendarMode === 'fixed' ? this.fixedModeDateStr() : this.localDateStr(this.currentDate);
  }

  /**
   * v0.18.72 — `activeDateStr()` 的 **Date 版** (网格日期与数据日期同一个出口)
   *
   * ## 为什么要有这个方法 (bug 根因)
   *   改前: FC 的 `initialDate` 传的是 `this.currentDate`, 而**数据**取的是 `activeDateStr()`。
   *   固定课模式下这两个口径**分家**了 ——
   *     · 数据 = 本周选中星期几的那天 (fixedModeDateStr)
   *     · 网格 = this.currentDate (今天)
   *   而且 `setFixedDayOfWeek()` 里的 `gotoDate(周三)` 会被 `load()` → `tryMountCalendar()`
   *   的「先 destroy 再 new FC」**整个冲掉** (构造参数 initialDate 赢) ⇒ 网格永远停在今天。
   *   实测: 选「三」→ 网格 `data-date=2026-10-24`(周六)、class `fc-day-sat`、事件 0;
   *         选「六」→ 同一天、事件 2 ⇒ **选周三时周六当天的课反而看不见**。
   *
   * ## 口径
   *   本方法**从 `activeDateStr()` 反解**, ⛔ 不另算一遍 (两处各算一次 = 迟早分叉)。
   *   `activeDateStr()` 返回 'YYYY-MM-DD', 这里按**本地时区**还原成 Date
   *   (照抄本文件 `localDateStr()` 的做法: ⛔ **不要用 toISOString()** ——
   *    本地转 UTC 再 slice 会把 CST 用户的前一天算成今天, 见 localDateStr 注释)。
   *
   * ## 正常模式必须一字不变
   *   正常模式 `activeDateStr()` = `localDateStr(this.currentDate)` ⇒ 反解回来是**同一天**。
   *   FC v6 默认 `timeZone: 'local'`, 实测 `initialDate` 传「当前时刻」与传「当天零点」
   *   渲染出的 `data-date` / `fc-day-*` class **逐字相同** ⇒ 正常模式行为零变化。
   */
  activeDateObject() {
    const parts = String(this.activeDateStr() || '').split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) {
      return this.currentDate; // 兜底: 解析不出来就沿用改前那个值
    }
    // 本地时区当天零点 (与 setCalendarMode / setFixedDayOfWeek 里 gotoDate 的写法同款)
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  /**
   * v0.18.75 (D2) — 固定课模式「日期锚点」的四件套 (网格上方的日期条)
   *
   * ## ⛔ 同源硬约束
   *   锚点日期**直接用网格用的同一个出口** `activeDateObject()`
   *   (= `activeDateStr()` 的 Date 版, 而 `activeDateStr()` 正是网格数据用的那个日期)
   *   ⇒ ⛔ **不另写一套**日期算法 (两处各算一次 = 迟早出现
   *     「锚点写周三、网格画周六的课」这种分家)。
   *
   * ## 各字段口径
   *   · 周几  = 锚点日期的 `getDay()` (0=周日) ⇒ 文案「一二三四五六日」
   *   · 第 N 周 = **ISO 周** (`isoWeekNumber()` —— 浏览器有 `getISOWeek()` 就用它,
   *     没有用标准 ISO 算法兜底; ⛔ 不用 `(dayOfYear+6)/7` 那种近似, 跨年会错)。
   */
  fixedAnchorDate() {
    return this.activeDateObject();
  }
  fixedAnchorDowLabel() {
    return ['日', '一', '二', '三', '四', '五', '六'][this.fixedAnchorDate().getDay()];
  }

  /**
   * v0.18.75 (D2) — 锚点条上**显示**用的周几文案 (「周三」)。
   *
   * · `fixedAnchorDowLabel()` 给的是「一二三四五六日」里的**一个字** (= getDay() 的直译),
   *   而条上要显示的是「周 + 那个字」(徽标 `[周三]` / 说明「本视图只显示周三的课」)。
   * · 两处显示**同一个**周几 ⇒ 收敛到这一个出口 (⛔ 不许两处各拼一遍)。
   */
  fixedAnchorDowText() {
    return '周' + this.fixedAnchorDowLabel();
  }
  fixedAnchorWeek() {
    // ⚠️ ISO 周: 走 isoWeekNumber() —— 浏览器有 getISOWeek() 就用它, 没有用标准 ISO 算法兜底
    //   (⛔ 不要用简单的 (dayOfYear+6)/7 —— 跨年会错)。详见该方法注释。
    return this.isoWeekNumber(this.fixedAnchorDate());
  }

  /**
   * v0.18.75 (D2) — **ISO-8601 周号** (第 N 周)
   *
   * ## ⚠️ 为什么不是直接 `getISOWeek()`
   *   `Date.prototype.getISOWeek` **不是 ECMAScript 标准** (和 `getWeek()` 一样属于历史遗留扩展),
   *   实测 **Chrome 148 上根本不存在** (`typeof Date.prototype.getISOWeek === 'undefined'`)
   *   ⇒ 直接调它抛 `TypeError: ...getISOWeek is not a function`,
   *     **整个日历 tab 的渲染直接挂掉** (连星期 chip 都出不来, 整页白)。
   *   ⇒ **口径一个字不变** (仍然是 ISO-8601 周号: 2026-10-21 = W43 / 2026-10-24 = W43 /
   *     2026-10-28 = W44), 只是实现走: **浏览器有 `getISOWeek()` 就用它,
   *     没有就用标准 ISO 算法兜底**。
   *   ⛔ 兜底**不是** `(dayOfYear+6)/7` 那种近似 (那个跨年会错);
   *     标准算法已对拍: 2015-01-01 ~ 2030-12-31 每天与 `date.isocalendar()[1]` **完全一致**。
   */
  isoWeekNumber(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (typeof d.getISOWeek === 'function') return d.getISOWeek();
    // 标准 ISO-8601 算法 (等价于 moment.js 的 isoweek):
    //   ① 定位本周的**星期四** —— ISO 周里星期四永远属于同一周 (含跨年那两天)
    //   ② 该周四所在年 1 月 1 日 = 第 1 周的第 1 天
    const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = utc.getUTCDay() || 7; // 周一=1 … 周日=7
    utc.setUTCDate(utc.getUTCDate() + 4 - dayNum); // 本周的星期四
    const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
    const days = Math.round((utc.getTime() - yearStart.getTime()) / 86400000);
    return Math.floor(days / 7) + 1;
  }
  fixedAnchorIsToday() {
    const a = this.fixedAnchorDate();
    const t = new Date();
    return a.getFullYear() === t.getFullYear() && a.getMonth() === t.getMonth() && a.getDate() === t.getDate();
  }
  async load() {
    // ── v0.19.00 — 非轮询触发的 load() = **用户动作** ⇒ 丢掉上一轮「待还原的视图状态」 ──
    //   (用户切日期 / 切模式 / 切校区 / 手动刷新都会走到这里;
    //    若不丢, 后面某次 FC 重建会把**旧日期**又拨回去。)
    //   轮询自己触发的 load() 由 _pollRefreshing 标记豁免 (见 _pollRefresh)。
    if (!this._pollRefreshing) {
      this._pendingViewRestore = null;
      // v0.19.00 F3 — 连 timer 一起清 (否则 timer 还活着, 到点又去清一个已经没了的值)
      if (this._pendingViewRestoreTimer) {
        clearTimeout(this._pendingViewRestoreTimer);
        this._pendingViewRestoreTimer = null;
      }
    }
    try {
      this.loading = true;
      // v0.6.39 — 用本地日期组件拼 'YYYY-MM-DD' (辉哥 9/30 1:03 拍板方案 B)
      //   改前 this.currentDate.toISOString().slice(0, 10) 会把 local 转 UTC 再 slice,
      //   CST (+08:00) 用户 9/29 0:00 → UTC 9/28 16:00 → slice 得 '2026-09-28' → API 拿到前一天数据
      //   改后 getFullYear/getMonth/getDate 直接读本地, 'YYYY-MM-DD' 跟用户选的日期一致
      //   后端 ?date= 走本地日期逻辑 (storage 也是本地时间, 见 controller 注释)
      // v0.8.10 — 抽成 localDateStr() 共用 (openBooking L892 同款场景, 见该方法注释)
      //
      // ⚠️ v0.9.0: 固定课模式下 date 不是「用户选的日期」而是「本周选中星期几的那一天」
      //   (口径收敛在 activeDateStr(), 理由见该方法注释)
      const dateStr = this.activeDateStr();
      const params = {
        date: dateStr,
        // ← 优先用 date 参数 (后端优先级最高, 见 ListLessonsController v0.6.39)
        // 保留 start/end (additive) 给其他可能传 start/end 的 component 兼容,
        //   后端 ?date= 优先于 start/end (本次只 CalendarTab 自己用 date)
        start: dateStr + ' 00:00:00',
        end: dateStr + ' 23:59:59'
      };
      if (this.activeSchoolId) params.school_id = this.activeSchoolId;

      // v0.5.0c SCD: 同时 GET time-schedule 当前生效版
      const tasks = [Api.list('lessons', params), Api.list('rooms', this.activeSchoolId ? {
        school_id: this.activeSchoolId
      } : {}),
      // v0.8.4 — 加载当天预留 (辉哥 10/6 拍板, 课表时段预留)
      Api.list('slot-reservations', this.activeSchoolId ? {
        school_id: this.activeSchoolId,
        date: dateStr
      } : {
        date: dateStr
      })];
      if (this.activeSchoolId) {
        // v0.17.57 — 读新课表 (旧 SCD time-schedule 端点已下线, 见 Api.js 注释)
        //   reason === null 才是「有课表」; not_configured / stale / 404 一律当**没有**处理。
        // v0.17.61 — 「没有」不再是兜底渲染态 (不再退回真实时间刻度), 而是整块提示
        //   「去配置课表」, 所以这里要**如实**表达: 三种「没有」都落成 scheduleNodes = null,
        //   由 scheduleState() 统一翻译成提示块。
        //   ⚠️ scheduleResolved 在**这条链自己的收尾**里翻, 不在 load() 的 finally 里翻:
        //   同一次 load 里 lessons / rooms / reservations 任一失败 (整条 Promise.all reject),
        //   都不能让「这个校区没配课表」变成误报 —— 那是另一件事, 不该由它们代言。
        // v0.17.72 — **失败不再是「没有」**。
        //   改前 `.catch(() => null)` 把 403 / 500 / 断网 / 超时全压成 null, 而 null 正是
        //   「没配课表」的值 ⇒ 后端报权限不足, 前端显示「你没配置」。学员诺诺 10/16 就是这个。
        //   改后失败走 { ok:false } 这条独立出口, 由 scheduleFailed 标记, 渲染**自己的**错误文案。
        //   ⚠️ Promise.all 的元素类型变了 (从 res/null 变成 {ok,res}), 所以下面解构也跟着改;
        //   成功路径的 res 语义**逐字未变** —— 'ready' 分支一个属性都没动。
        this.scheduleResolved = false;
        this.scheduleFailed = false;
        // v0.17.85 (辉哥 10/17 拍板) — **带上 date**, 让课表跟着日历翻到的那天走。
        //   改前这一句不传日期 ⇒ 后端恒按「今天」返回版本 ⇒ `this.scheduleNodes` 永远是今天那版,
        //   于是看 10-16 时左列画的是 B 的时段, 而那一天的课挂着 A 的 schedule_node_id ⇒
        //   `scheduleRowIndexOfLesson()` 的 findIndex 必然扑空 → 静默走「按 start_at 反推」的
        //   回落路径 → 课块落到猜出来的那一行。这就是辉哥说的「约课展示错乱」。
        //   带上 date 后: 10-16 拿到 A 的节点 → findIndex 命中 → 落行唯一且正确。
        //   ⚠️ date 用 activeDateStr() (与 lessons / rooms / reservations 完全同一个口径出口),
        //     别在这里另算一遍 —— 另算就会出现「表头 10-16、课表却是今天那版」的分叉。
        //   ⚠️ 「请求失败」与「那天没配课表」仍走 v0.17.72 各自独立的出口 (ok:true / ok:false),
        //     本轮一个字没改 —— 按日期取回课表**不能**把这两者重新混成 null。
        tasks.push(Api.getCurrentSchedule(this.activeSchoolId, {
          date: dateStr
        }).then(res => ({
          ok: true,
          res: res && res.reason === null && res.data && Array.isArray(res.data.nodes) && res.data.nodes.length > 0 ? res : null
        })).catch(() => ({
          ok: false,
          res: null
        })).then(out => {
          // 成功 / not_configured / stale 都算「问出结论了」; **失败也算出结论** ——
          // 但结论内容是「没问到」, 由 scheduleFailed 单独表达, 不与 missing 混同。
          this.scheduleResolved = true;
          this.scheduleFailed = !out.ok;
          return out.res;
        }));
      } else {
        // v0.17.61 — 连校区都没选时压根不会发课表请求。若不显式翻旗, scheduleResolved 会永远
        //   停在 false ⇒ 日历永远卡在「加载课表时间段中...」。这种情况按「确认没有」处理,
        //   提示块里的「去「校区」配置课表」按钮正好把人带到能选校区的那个 tab。
        this.scheduleResolved = true;
        // v0.17.72 — 同理清失败旗: 没发过请求 = 没有失败可言, 不该沿用上一个校区的错误块
        this.scheduleFailed = false;
      }
      // v0.9.0 — 固定课模板列表 (固定课模式/检查排课/右键菜单都要用; 正常模式也拉,
      //   这样模式切换是同步的, 不会出现「切过去还要等一次请求」的空白)
      // ⚠️ v0.17.76 (辉哥 10/16「只能看到正常约课的课表」) — 学员**不发**这个请求。
      //   ⚠️⚠️ 这只是「前端不请求」, **不是**后端收紧了权限:
      //     ListFixedLessonsController 第 30 行要的 `scheduler.viewLessons` 学员本来就有,
      //     也就是说学员直接调 GET /fixed-lessons 今天照样拿得到全量模板。
      //     要不要在后端把这条路堵上, 是**另一个决定**, 本轮明确不改 (别顺手改)。
      if (this.activeSchoolId && !this.isStudentView()) {
        tasks.push(Api.listFixedLessons({
          school_id: this.activeSchoolId
        }).catch(() => []));
      }
      const [lessonsRes, roomsRes, rsvsRes, schedRes, fixedRes] = await Promise.all(tasks);
      this.lessons = lessonsRes || [];
      this.rooms = roomsRes || [];
      this.slotReservations = rsvsRes || []; // v0.8.4
      // ⚠️ v0.17.57 — 这里原来还有一句 `this.timeSchedule = tsRes || null`, 连同 oninit /
      //   onbeforeupdate 里的初始化一起删了: 老 SCD 数据源下线后, **全仓库再没有一处读它**
      //   (grep `.timeSchedule` 只剩这三处赋值), 留着就是一个只写不读的字段, 而它装的还是
      //   新课表响应 —— 名字与内容对不上, 下一个人照着名字推理必然走错。
      // v0.17.57 — 课表节点按 sort_order 升序落位。**必须**排, 不能信后端返回顺序:
      //   左列「从上到下」的行序完全由这里的数组下标决定 (第 N 个时间段 = 第 N 行),
      //   顺序一旦乱了, 段与行的对应就整体错位 —— 而这类错位在页面上看着「只是标签顺序不对」,
      //   极难自查。sort_order 相同时用 id 兜底, 保证同分也有确定次序 (跟后端
      //   ScheduleResolver 的 orderBy('sort_order')->orderBy('id') 同口径)。
      this.scheduleNodes = null;
      this.scheduleVersion = null;
      if (schedRes && schedRes.data) {
        const nodes = (schedRes.data.nodes || []).slice().sort((a, b) => Number(a.sort_order) - Number(b.sort_order) || Number(a.id) - Number(b.id));
        if (nodes.length) {
          this.scheduleNodes = nodes;
          this.scheduleVersion = {
            id: schedRes.data.id,
            version_name: schedRes.data.version_name || '',
            effective_from: schedRes.data.effective_from || ''
          };
        }
      }
      this.fixedLessons = fixedRes || []; // v0.9.0
      // 跑 5 重碰撞 (异步, 不阻塞)
      this.checkConflicts().catch(() => {});
      // ── v0.19.00 静默轮询: 指纹基准 ──────────────────────────────────────
      //   · **用户动作**触发的 load (切日期/切校区/切模式/手动刷新) ⇒ **清空**
      //     ⇒ 下一轮轮询只**重建基准**、不触发刷新 (用户什么都没干, 日历不该自己闪一下)
      //   · **轮询自己**触发的 load ⇒ ⛔ **不清**
      //     ⇒ 基准仍是轮询刚算出来的那一份 ⇒ 下一轮若数据又变了 ⇒ **能立刻检出**
      //   ⛔ 只在**成功**路径清: load() 失败时数据没换, 基准仍然有效。
      //
      // ⚠️⚠️ v0.19.00 F4 (生产实测复现的「数据被吞」缺陷) —— 改前这里是**无条件**清空:
      //   每次「刷新」之后 _fp 都变成 null ⇒ 下一轮轮询走的是「只重建基准、不触发刷新」
      //   那条分支 ⇒ **刷新完成 → 下一轮轮询**这一小段窗口里若数据又变了, 那次变化会被
      //   当成「新基准」**吸收掉** ⇒ 屏幕停在旧数据上, 要等下一次改动才纠正。
      //   ⇒ 直接打中辉哥的场景: B 电脑在 1~2 分钟内改了两次课表, A 电脑只会更新到第一次,
      //     第二次被**静默吞掉**。
      //   ⇒ 修法 = 用已有的 `_pollRefreshing` 标志 (轮询触发的 load() 期间它是 true) 分流。
      //   ⚠️ 这样一来, 基准的两次比较**永远来自「轮询自己算的指纹」**(不引入「load 的指纹」
      //     与「轮询的指纹」跨源比较) ⇒ 不会出现「服务器两次响应不稳定 ⇒ 每 60s 白刷一次」。
      if (!this._pollRefreshing) this._fp = null;
    } catch (e) {
      console.error('[ziven-scheduler] load calendar failed', e);
    } finally {
      this.loading = false;
      m.redraw();
      this.tryMountCalendar();
      // v0.15.0 — 原先这里紧跟一句 _syncReservationBlocks(), 已删:
      //   预留块是 FC event, tryMountCalendar() 建事件源时就把最新 slotReservations 喂进去了。
      //   再调一次等于同一次 load 里把日历销毁重建两遍 (v0.8.4 时代需要, 因为那时是外挂 DOM)。
      // v0.10.1 — load 收尾再对账一次日期选择器。
      //   实测: 切回正常模式时 input 要到 load() 的 finally 里 m.redraw() 之后才出现
      //   (点击后 ~21ms), 而 setCalendarMode 里那个 setTimeout(0) 在 ~1ms 就跑完了。
      //   这里 + onupdate 两处兜住"晚到"的挂载, 时序快慢都能补上。
      this._syncDatePicker();
    }
  }

  // ═══ v0.17.57 课表驱动的网格节拍 (名义时间轴) ═══════════════════════════
  //
  // 【为什么需要「名义时间」】
  //   FullCalendar timeGrid 要求 slot **均匀映射真实时间**: slotDuration 一个值, 所有 slot
  //   等长, slotMinTime/slotMaxTime 是真实钟点。它**没有**「一行一段、每段长短不一」这个
  //   概念 —— 而那正是老板拍板的排版规则 (第 4/5 条)。硬撞这堵墙只有两条路: 劫持 FC 内部
  //   DOM/布局, 或改 vendor。两条都明令禁止, 所以换一条**不碰 FC 内部**的路。
  //
  // 【本轮做法: 把「真实分钟」换成「名义分钟」】
  //   取一个基准值 nominalSlotMinutes() 当 slotDuration, 让**第 N 个时间段**映射到名义区间
  //   [N×基准, (N+1)×基准)。于是:
  //     · 行数 == 时间段数 (课表排几段, 左列就几行)
  //     · 每段 1 行、**等高** (所有 slot 时长相同 ⇒ FC 算出的行高也相同)
  //     · 段与段之间**不留任何空隙** (真实的 12:00-13:00 空档不再占一行)
  //   左列文字走 slotLabelContent 渲染**真实**区间 ('09:00 – 12:00'); 课块内部走 eventContent
  //   补一行**真实**起止。名义时间从头到尾只活在渲染层。
  //
  // ⚠️⚠️ 名义时间**绝不写库**: 任何落库路径 (拖拽 eventDrop / 右键建课 / 建预留 / 建固定课)
  //   一律先经 realTimeForSlotTime() 或 scheduleNodeForSlotTime() 换回真实钟点。
  //   漏掉一处 = 写进一条 01:00 的课, 而 01:00 不在任何时间段内, 后端 ScheduleResolver 必拒。

  /** 本校区有没有可用课表 —— 有: 左列画「时间段顺序清单」; 无: 见 scheduleState()。 */
  hasSchedule() {
    return Array.isArray(this.scheduleNodes) && this.scheduleNodes.length > 0;
  }

  /**
   * v0.17.61 — 课表三态。**fc 到底建不建, 只看这一个方法。**
   *
   *   'loading' 请求还没落地 (首帧 / 换校区 / 在途) —— 不建 fc, 显示「加载课表时间段中...」
   *   'ready'   确有课表                              —— 照常建 fc (本轮改动**不碰**这条分支)
   *   'missing' 已确认没有 (not_configured / stale / 没选校区) —— 不建 fc,
   *            显示「去配置课表」的提示块
   *
   * ⚠️ 「loading」与「missing」必须分开: 合在一起时, 用户每次打开日历/弹窗都会先闪一下
   *   「该校区还没有配置课表」再跳走 —— 请求慢一点就是一次**误报**。
   *   所以本方法读的是 scheduleResolved (只有课表请求自己会翻它), 不是 scheduleNodes。
   *
   * ⚠️ 'ready' 分支的渲染结构本轮**逐字未改** —— 判据「有课表分支的 fc 几何与改动前一致」
   *   依赖的就是这一点, 别顺手重构 view() 里 ready 那条路径。
   *
   * ── v0.17.72 三态不够, 是**四态** ──────────────────────────────────────
   *   'error'   **问失败了** (403 / 500 / 断网 / 超时) —— 不建 fc, 显示**自己的**错误文案。
   *
   * 为什么必须把 'error' 从 'missing' 里拆出来 (这正是 10/16 事故的根子):
   *   改前 `.catch(() => null)` 让「失败」和「真没有」落成同一个值, 于是
   *   **后端说「你没权限」, 前端显示「你没配置课表」** —— 用户照着提示去校区 tab 配课表,
   *   配完还是这句, 因为问题根本不在配置上。错误文案与「没配」文案必须**互不借用**,
   *   否则等于让前端替后端撒谎。
   *
   * 判据顺序 ⚠️ scheduleFailed 必须**先于** scheduleResolved 查, 但实际上二者互斥
   *   (失败分支也把 scheduleResolved 翻成 true), 所以这里按 resolved 先判加载态更安全:
   *   请求在途中 scheduleFailed 必然还是 false ⇒ 不会闪错误块, 仍是加载态。
   */
  scheduleState() {
    if (!this.scheduleResolved) return 'loading';
    // ⚠️ 必须在 hasSchedule() **之前**判: 失败时 scheduleNodes 被清成 null,
    //   若先判 hasSchedule() 会一路落到 missing —— 那就是我们要修的那个谎。
    if (this.scheduleFailed) return 'error';
    return this.hasSchedule() ? 'ready' : 'missing';
  }

  /**
   * v0.17.72 — 错误态的「重新加载」出口。
   *   直接重跑 load(): 它会把 scheduleResolved / scheduleFailed 一起翻回初始态,
   *   所以点完之后立刻回到「加载中」, 不会拿上一轮的结论冒充这一轮的结果。
   */
  retryScheduleLoad() {
    if (typeof this.load === 'function') this.load();
  }

  /** 'HH:MM' | 'HH:MM:SS' → 当天零点起的分钟数 */
  _hhmmToMinutes(s) {
    const p = String(s == null || s === '' ? '0:0' : s).split(':').map(Number);
    return (p[0] || 0) * 60 + (p[1] || 0);
  }

  /** 分钟数 → 'HH:MM' (负数/跨天归一, 与既有 %24 口径一致) */
  _minutesToHHMM(min) {
    const m = (Math.round(min) % 1440 + 1440) % 1440;
    return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
  }

  /** 分钟数 → 'HH:MM:SS' (给 FC 的 slotMinTime / slotMaxTime 用) */
  _minutesToHHMMSS(min) {
    return this._minutesToHHMM(min) + ':00';
  }

  /** Date → 当天零点起的分钟数 (含秒的小数, 与 FC slot 边界对齐) */
  _minutesOfDay(date) {
    return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
  }

  /**
   * 一格的**名义**分钟数。
   *   有课表 → 取**最短**的那一段 (设计稿 §6 的原话「取最短的那段」)。基准越小名义轴总长
   *     越短, 但行高由 FC 按 slot 数均分, **与基准值无关** ⇒ 取最短不会让行变矮。
   *   没课表 → 兜底 30 分钟 (= v0.17.56 及更早的实际生效值, 兜底渲染零变化)。
   */
  nominalSlotMinutes() {
    if (this.hasSchedule()) {
      let min = Infinity;
      for (const n of this.scheduleNodes) {
        const d = this._hhmmToMinutes(n.end_time) - this._hhmmToMinutes(n.start_time);
        if (d > 0 && d < min) min = d;
      }
      if (min !== Infinity && min > 0) return min;
    }
    return 30;
  }

  /** nominalSlotMinutes() → 'HH:MM:SS', 给 FC 的 slotDuration */
  slotDurationFromSCD() {
    const m = this.nominalSlotMinutes();
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}:00`;
  }

  /**
   * 一格分钟数。v0.17.57 起 = 名义节拍 (见本段顶部注释)。
   * 既有调用方 (isUltraShortLesson / canDropOverReservations / canPlaceReservation /
   * buildReservationContentNodes / openBooking / handleEventDrop / openAddFixedLesson /
   * openReserveModal) 全部沿用这个口径, 一处不漏 —— 任何一处另写一份都会让「一格」
   * 在同一个日历里有第二种定义。
   */
  slotMin() {
    return this.nominalSlotMinutes();
  }

  /**
   * FC 的 slotMinTime / slotMaxTime —— 也就是「左列画多少行」。
   *   有课表 → 零点起 N×名义分钟 ⇒ **恰好 N 行**: 一段一行、等高、中间无空隙、尾部无半行。
   *   没课表 → 老口径 {08:00:00, 22:00:00} (v0.17.56 的实际生效值)。
   *
   * ⚠️ maxTime 必须**正好**是 N×基准: 多一分钟 FC 就多渲染一行空白 label,
   *   少一分钟最后一行的课块会被截掉半格。这两个值都要跟着行数走, 不能写死。
   */
  slotRangeFromSCD() {
    if (this.hasSchedule()) {
      // 上限夹在 24h: 一份课表排超过 24 段属于配置异常, 不让它把 maxTime 绕回 00:00
      const total = Math.min(this.nominalSlotMinutes() * this.scheduleNodes.length, 1440);
      return {
        min: '00:00:00',
        max: this._minutesToHHMMSS(total)
      };
    }
    return {
      min: '08:00:00',
      max: '22:00:00'
    };
  }

  /**
   * v0.17.57 — 网格轴「按天分档」这件事**已经不存在了**。
   *
   * 改前本方法优先 dayRangeFromSCD(date) (当天那档营业时间), 退回并集。那是 v0.15.3 /
   * v0.15.4 为 business_hours 加的 —— business_hours 已随 v0.17.55 整体下线,
   * dayRangeFromSCD() 恒返 null, 这个「按天分档」实际早就是死代码, 本轮连同
   * businessHoursViolation / _warnBusinessHoursOnce / _businessHoursMessage 一并删除。
   * 新的课表**也没有按星期几分档**这回事 (schedule_nodes 只有一份, 对所有日期生效),
   * 所以轴就是 slotRangeFromSCD(), 不再需要看日期。
   *
   * 保留这个方法的唯一理由: _syncAxisToDate (datesSet 兜底) 还在调它。
   */
  axisRangeForDate(date) {
    // eslint-disable-line no-unused-vars
    return this.slotRangeFromSCD();
  }

  // ─── 课表节点 ↔ 名义行 ↔ 真实钟点 的三向换算 ─────────────────────────────

  /**
   * 归属 (老板拍板第 3 条): 一节课的开始时刻落在**哪个时间段**。
   * `timeMinutes` = start_at 的「当天零点起分钟数」。
   * 落在某段 [start, end] 内 → 该段下标; **结束时间不管** (允许比段长, 也允许比段短)。
   * 夹在两段之间 / 早于第一段 / 晚于最后一段 → null (这一档不开放, 后端会拒)。
   *
   * ## ⚠️ 必须是**闭区间**, 且**取 sort_order 第一个命中的** (v0.17.62)
   *   与后端 ScheduleNode::containsMinuteOfDay() (`minute >= start && minute <= end`)
   *   和 ScheduleResolver::nodeIn() (遍历命中即 return) **逐字同口径**。
   *   区间相交是合法的 (10:15-11:00 接 10:30-11:15), 一节课常同时命中好几段,
   *   这时一律归到**排在前面的那一行**。
   *
   *   ⚠️ v0.17.62 前这里是左闭右开 [start, end)。那在**不重叠**的排版下看不出差别
   *     (边界那一分钟会落到 scheduleIndexForRender 的就近兜底, 结果一样),
   *     但一旦允许重叠, 两边就会**真的算出不同的行**: 例 节点1 = 10:15-11:00,
   *     节点2 = 10:30-11:15, 一节 11:00 的课 —— 后端闭区间归节点1,
   *     左闭右开会精确命中节点2 并画到第 2 行, 而 checkSlotOccupancy 用的是节点1 的窗口。
   *     结果: 课块画在一行, 占用判定算另一行。改闭区间就是为了消掉这个分叉。
   */
  scheduleIndexOf(timeMinutes) {
    if (!this.hasSchedule()) return null;
    for (let i = 0; i < this.scheduleNodes.length; i++) {
      const n = this.scheduleNodes[i];
      const s = this._hhmmToMinutes(n.start_time);
      const e = this._hhmmToMinutes(n.end_time);
      if (timeMinutes >= s && timeMinutes <= e) return i;
    }
    return null;
  }

  /**
   * **渲染用**的归属兜底: 严格归属判不出来时**就近**归一段, 保证课块仍然可见。
   *
   * ⚠️ 这只决定「画在哪一行」, **不改落库时间** —— 落库仍走 scheduleIndexOf 的严格归属。
   *   为什么不干脆不画: 「夹在空档里的课」是存量数据, 画出来运营至少看得见这天有这么一节;
   *   不画就是静默消失, 比「画在最近一行但数据没变」更难排查。
   *   平手取**靠前**的那一段 (与已删的 snapStartTimeToSlot 的「取靠后」相反: 那是改用户
   *   填的值, 这个只是画的位置, 取靠前更符合「它还在上一段没散」的直觉)。
   */
  scheduleIndexForRender(timeMinutes) {
    const exact = this.scheduleIndexOf(timeMinutes);
    if (exact != null) return exact;
    if (!this.hasSchedule()) return null;
    let best = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < this.scheduleNodes.length; i++) {
      const d = Math.abs(this._hhmmToMinutes(this.scheduleNodes[i].start_time) - timeMinutes);
      if (d < bestDiff) {
        best = i;
        bestDiff = d;
      }
    }
    return best;
  }

  /**
   * 一节课画在**第几行** —— v0.17.62 的统一口径入口 (读侧)。
   *
   * ## 优先用课**存下来的节点 id**, 没有才按 start_at 反推
   *   辉哥 10/16: 「用户选哪个, 就被排在哪个时间节点上」。
   *   存 schedule_node_id 就是为了让这一句在**渲染层**也成立:
   *   时间段允许重叠后, 一节 10:45 的课在 A、B 两段里都成立,
   *   反推只能猜一个 (sort_order 取第一条), fc 就会把存在 B 的课画在 A 行上。
   *
   *   ⚠️ **读侧一律宽松**: node id 悬空 (节点被删 / 课表被改过) 就**静默回落**到
   *     start_at 反推, 绝不让整页课表白屏。严格校验在写侧 (ScheduleResolver::assertResolves)。
   *
   * ## v0.17.85 — 这条回落路径**只该在「节点 id 真悬空」时**走
   *   改前 `scheduleNodes` 恒为「今天那版」, 于是看 10-16 (课挂在 A 版的节点上) 时
   *   findIndex 必然扑空, **每一节历史课都在走回落** —— 时间段一改就会静默落到猜错的那一行,
   *   也就是辉哥报的「约课展示错乱」。load() 带上 `?date=` 之后, 看哪天就取哪天的版本,
   *   findIndex 必然命中; 回落恢复成它本来的身份: 兜底, 而不是主路径。**别删。**
   *
   * @returns {number|null} 行下标; 取不到返 null (调用方按「没课表」兜底渲染)
   */
  scheduleRowIndexOfLesson(lesson) {
    if (!this.hasSchedule() || !lesson) return null;
    const nodeId = lesson.schedule_node_id != null ? +lesson.schedule_node_id : null;
    if (nodeId) {
      const byId = this.scheduleNodes.findIndex(n => n && +n.id === nodeId);
      if (byId >= 0) return byId;
    }
    const base = zsParseLocal(lesson.start_at);
    if (!base) return this.scheduleIndexForRender(this._minutesOfDay(new Date(NaN)));
    return this.scheduleIndexForRender(this._minutesOfDay(base));
  }

  /**
   * 第 idx 行在 date 那天的**名义**起点 (FC 坐标系)。
   *   课块的 start/end 都由它算 —— 每块恰好 [N×基准, (N+1)×基准] = 填满整格,
   *   这就是「30 分钟的课和 90 分钟的课一样高」的实现点。
   */
  nominalRowDate(date, idx) {
    const base = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    return new Date(base.getTime() + idx * this.nominalSlotMinutes() * 60000);
  }

  /** 名义 slot 的 Date → 行下标 (越界返 null)。slotLabelContent / eventAllow 用。 */
  scheduleRowIndexOfDate(date) {
    if (!this.hasSchedule()) return null;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const idx = Math.floor(this._minutesOfDay(date) / this.nominalSlotMinutes());
    return idx >= 0 && idx < this.scheduleNodes.length ? idx : null;
  }

  /** 名义格子时间 ('HH:MM' / 'HH:MM:SS') → 时间段节点, 越界返 null */
  scheduleNodeForSlotTime(dataTime) {
    if (!this.hasSchedule()) return null;
    const idx = Math.floor(this._hhmmToMinutes(String(dataTime || '').slice(0, 5)) / this.nominalSlotMinutes());
    const nodes = this.scheduleNodes;
    if (idx < 0 || idx >= nodes.length) return null;
    return nodes[idx];
  }

  /**
   * 约课 / 预留 / 固定课 三条「从格子建记录」的入口都要读格子的 data-time ('HH:MM:SS')。
   *   有课表时 DOM 里的格子时间是**名义**的 (第 2 行 = '01:00:00'), 直接拿去建记录会写进
   *   一条 01:00 的课 —— 01:00 不在任何时间段内, 后端必定拒。
   *   这里把名义格子时间换回**真实**时间段的 start_time ('HH:MM')。
   *   换不到 (兜底渲染模式 / 越界) → 原样返回, 这三条入口保持既有行为不变。
   */
  realTimeForSlotTime(dataTime) {
    const raw = String(dataTime || '').slice(0, 5);
    if (!this.hasSchedule() || !raw) return raw;
    const node = this.scheduleNodeForSlotTime(raw);
    return node ? String(node.start_time).slice(0, 5) : raw;
  }

  /**
   * 拖拽落点的**真实**时间范围 —— 有课表时 FC 给的 start/end 是名义时间, 必须换回真实钟点。
   *
   * 换算口径: 第 N 行 ⇒ 该时间段的 [start_time, end_time]。这是本轮唯一一条拖拽规则
   * (设计稿 §10「拖到第 N 行 ⇒ 开始时间 = 第 N 段的开始时间」), 跟归属同一套下标。
   * 换不出 (兜底渲染模式 / 行下标越界) → **原样返回**, 由调用方自己判非法, 不在这里瞎猜。
   *
   * ⚠️ 用它的地方必须**成对**: canPlaceReservation (eventAllow, 拖拽中) 与
   *   persistReservationMove (落库前) 各换一次, 两处都调这一个方法 —— 口径分叉过一次,
   *   结果就是「拖的时候判合法、存下去却是另一个时间」。
   *
   * ── v0.18.44 — 跨度 (扩/缩上下边界) ─────────────────────────────────────
   *   改前**只看 start**, end 被写死成「起始那一档的末尾」⇒ resize 改 end 时算出来的区间
   *   跟库里存的**一模一样** ⇒ 落库前的 noop 判定成立 ⇒ 块被弹回原位
   *   (辉哥实测报障:「预约课块向上或向下扩展区域时, 松手后预留区又恢复原状了」)。
   *   改后: 格数 cells = (end - start) / 基准, 最后一格 = 起始行 + cells - 1,
   *   真实区间 = **起始那一档的 start_time → 最后一格那一档的 end_time**
   *   (= 这几档真实区间的**并集**, 例: 10:30-11:15 往下扩一格 ⇒ 10:30 → 12:05)。
   *   ⚠️ **1 格时 (cells=1) 结果与改前逐字相同** —— 拖到别的档位、原地小拖这两条既有行为不变。
   *   ⚠️ 越界 (最后一格超出课表最后一行) ⇒ overflow=true, 由调用方弹回 (不发请求)。
   */
  realRangeForNominalRange(start, end) {
    // v0.18.43 — 一并返回**目标节点的 id** (nodeId)。
    //   拖拽落点的行下标 ⇒ 目标节点 是这里唯一能算出来的地方 (换算入口就这一个),
    //   所以 node id 必须从这里出 —— 别处再算一遍必然分叉。
    if (!this.hasSchedule()) return {
      start,
      end,
      nodeId: null
    };
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) return {
      start,
      end,
      nodeId: null
    };
    const idx = this.scheduleRowIndexOfDate(start);
    if (idx == null) return {
      start,
      end,
      nodeId: null
    };
    // v0.18.44 — 格数: FC 的 timegrid resize 吸附到 slot 边界 ⇒ (end - start) 是基准的整数倍;
    //   round() 兜住万一没吸附的情况 (拖拽时 FC 给的 start/end 同样是整格边界)。
    const slotMs = this.nominalSlotMinutes() * 60000;
    const endMs = end instanceof Date && !Number.isNaN(end.getTime()) ? end.getTime() : start.getTime() + slotMs;
    const cells = Math.max(1, Math.round((endMs - start.getTime()) / slotMs));
    const lastIdx = idx + cells - 1;
    // 越界: 覆盖到了课表最后一行之外 (再往下扩一格就没档了) ⇒ 调用方弹回
    const overflow = lastIdx >= this.scheduleNodes.length;
    const node = this.scheduleNodes[idx];
    // 越界时末格取课表最后一行 (只为算出一个可比较的 end; overflow 已单独标记出来)
    const lastNode = this.scheduleNodes[Math.min(lastIdx, this.scheduleNodes.length - 1)];
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
    return {
      start: new Date(day.getTime() + this._hhmmToMinutes(node.start_time) * 60000),
      end: new Date(day.getTime() + this._hhmmToMinutes(lastNode.end_time) * 60000),
      nodeId: node && node.id != null ? +node.id : null,
      cells,
      overflow
    };
  }

  /**
   * v0.15.4 — `datesSet` 兜底: 把轴同步到 FC 当前展示的那一天。
   *
   * 为什么要有这条 (主路径不是已经按天构造了吗): 实测 `setOption('slotMinTime'/'slotMaxTime')`
   * **不会**触发 datesSet, 只有 gotoDate 会 —— 所以 options 变化和日期变化是两条独立的路, 不能指望
   * datesSet 兜住全部。凡是走到「FC 实例活着但轴没跟着当天走」的漏路径, 这条都接得住。
   *
   * ⚠️ 必须**幂等**: datesSet 在初次 render、view 切换、refetch 时都会触发。不比对就反复
   *   setOption ⇒ 每次都重排整张网格 (闪烁 + 掉性能)。所以拿 _appliedAxisMin/_appliedAxisMax
   *   记「当前已生效的窗口」, 相同就不动。
   *
   * ⚠️ 初次 render 期间本方法必然早退: calendar.render() 触发 datesSet 时 this._calendar 还是 null
   *   (第 1651 行才赋值), 而此时构造参数已经带上了正确的 range, 本来就不需要补。
   */
  _syncAxisToDate(date) {
    if (!this._calendar) return; // 初次 render: 构造参数已经是对的
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    const target = this.axisRangeForDate(date);
    if (target.min === this._appliedAxisMin && target.max === this._appliedAxisMax) return; // 幂等: 无变化不动
    this._calendar.setOption('slotMinTime', target.min);
    this._calendar.setOption('slotMaxTime', target.max);
    this._appliedAxisMin = target.min; // setOption 成功后再记账 (顺序反了会留下脏账)
    this._appliedAxisMax = target.max;
  }

  /** [start,end) 是否落进给定窗口 (窗口用 HH:MM:SS, 按 start 那天的自然日折算成时间戳) */
  _withinRange(start, end, range) {
    const parse = s => {
      const p = String(s || '00:00:00').split(':').map(Number);
      return new Date(start.getFullYear(), start.getMonth(), start.getDate(), p[0] || 0, p[1] || 0, 0, 0);
    };
    return start.getTime() >= parse(range.min).getTime() && end.getTime() <= parse(range.max).getTime();
  }
  async checkConflicts() {
    const conflictMap = [];
    for (const l of this.lessons) {
      try {
        // POST /api/ziiven/scheduler/lessons/check-conflict (不接受 GET, 405)
        const r = await app.request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/lessons/check-conflict',
          body: {
            data: {
              attributes: l
            }
          },
          serialize: x => JSON.stringify(x),
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (r && r.conflict) {
          conflictMap.push({
            lesson: l,
            conflict: r.conflict
          });
        }
      } catch (e) {}
    }
    this.conflicts = conflictMap;
    m.redraw();
    this.tryMountCalendar();
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.tryMountCalendar();
    // v0.6.10 — flatpickr 单 chip 初始化 (等 DOM + flatpickr 全局 ready)
    // v0.10.1 — 改成调 _syncDatePicker (对账式, 幂等且与时机无关);
    //   这里保留 setTimeout 只是为了不挡首帧, 真正的保证来自 onupdate/load 收尾的重复调用。
    setTimeout(() => this.initDatePicker(), 0);
    // ── v0.19.00 — 启动静默轮询 (定时器 + visibilitychange 监听) ──────────
    //   ⚠️ onremove 里**必须**成对清掉 (否则组件销毁后定时器还在跑 ⇒ 请求 + 内存双泄漏)。
    this._startSchedulePoll();
  }

  // v0.10.1 — onupdate 也参与日期选择器对账。
  //   这是本 bug 的关键缺口: 改前 onupdate 只调 tryMountCalendar(),
  //   于是"静默 return 之后没人再来" —— DOM 重绘完成了, 却没人重挂。
  //   现在每次重绘都重新对账一次, 挂载时机早于 DOM 落地也不再是问题。
  onupdate(vnode) {
    super.onupdate(vnode);
    this.tryMountCalendar();
    this._syncDatePicker();
  }
  onremove(vnode) {
    super.onremove(vnode);
    // ── v0.19.00 — 停轮询 (定时器 + 监听) ⇒ ⛔ 不许泄漏 ──────────────────
    //   组件销毁后定时器还在跑的话, 每 60 秒还会发一轮请求 (而且这个组件已经不在屏幕上了)
    //   ⇒ 请求泄漏 + 定时器句柄泄漏。两条都要断。
    this._stopSchedulePoll();
    this._removed = true;
    this._pendingViewRestore = null;
    // v0.19.00 F3 — 清掉「待还原」的清场 timer (组件都没了, 没必要再回调一次)
    if (this._pendingViewRestoreTimer) {
      clearTimeout(this._pendingViewRestoreTimer);
      this._pendingViewRestoreTimer = null;
    }
    // v0.7.9e — 关掉可能还开着的约课菜单 (挂 body, 不随 tab DOM 一起销毁)
    SlotContextMenu.close();
    if (this._fcRetryTimer) {
      clearTimeout(this._fcRetryTimer);
      this._fcRetryTimer = null;
    }
    if (this._calendar) {
      this._calendar.destroy();
      this._calendar = null;
    }
    // v0.6.10 — flatpickr destroy (chip 销毁, 防泄漏)
    // v0.10.1 — 组件卸载时如果还有一次 mount 在途, 打一个标记:
    //   _syncDatePicker 回来后会看到它, 把自己刚建出来的实例销毁, 而不是挂到一个死组件上。
    this._datePickerDestroyedDuringMount = this._datePickerMounting;
    this._datePickerPendingEl = null;
    this._destroyDatePicker();
    // v0.8.4 — 销毁预留渲染器
    if (this.slotReservationRenderer) {
      this.slotReservationRenderer.destroy();
      this.slotReservationRenderer = null;
    }
    // v0.15.0 — 方向锁定徽标是挂 body 的浮层, 不随组件 DOM 一起没, 必须显式清
    this.hideResizeDirectionFlag();
    this._rsvResizeEdge = null;
  }

  // ═══ v0.19.00 课表日历「静默轮询」(自动更新) ═══════════════════════════
  //
  // 【它是什么】
  //   每 60 秒, 在**页面可见**时拉一次课表相关数据 (4 个端点), 算一个**指纹**:
  //     · 指纹没变 (99% 的情况) ⇒ **什么都不做** —— 不重绘、不动视图、不动滚动位置;
  //     · 指纹变了            ⇒ 走**现有的** `load()` 完整刷新。
  //
  // 【为什么复用 load() 而不是自己换事件】
  //   load() 里 `scheduleResolved` / `scheduleFailed` / `scheduleVersion` 那套语义**极微妙**
  //   (v0.17.72 / v0.17.85 各踩过一次坑) ⇒ 重写一遍必然再踩 ⇒ **原样复用, 一字不改**。
  //   代价: load() 收尾会 `tryMountCalendar()` ⇒ **重建 FC 实例** ⇒ 当前视图与滚动位置会丢
  //   ⇒ 所以必须自己存 + 自己还原 (见 _captureCalendarView / _restoreCalendarView)。
  //
  // 【⛔ 轮询全程不许碰 this.loading】
  //   设了它整个 tab 会闪成 loading 骨架 ⇒ 静默轮询必须**完全无感**。
  //   (load() 内部自己会置 loading —— 那是 load() 的既有语义, 本批一个字都不动。)

  /**
   * v0.19.00 — 轮询间隔 (ms)。
   *
   * ⛔ **默认 60000** (辉哥拍板 60s) —— 生产行为就是这个值。
   * ⚠️ 之所以从 `window.ZS_SCHEDULE_POLL_MS` 读, 是为了让**验收脚本**能把间隔调到
   *   1500ms 之类的小值 (否则一个断言要等一分钟)。这是**唯一的**可覆盖入口。
   */
  _schedulePollIntervalMs() {
    const v = typeof window !== 'undefined' ? window.ZS_SCHEDULE_POLL_MS : undefined;
    const n = Number(v);
    return isFinite(n) && n > 0 ? n : 60000;
  }

  /** S1 — 页面是否可见 (`document.visibilityState`)。 */
  _isPageVisible() {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible';
  }

  /**
   * S3 — 是否有 modal 打开。
   * ⛔ Flarum 的 `ModalManager.activeDialogElement` 是 **private** (core ModalManager.tsx:174)
   *   ⇒ 外部查不到 ⇒ 只能查 DOM。
   */
  _hasOpenModal() {
    if (typeof document === 'undefined') return false;
    return !!document.querySelector('.Modal');
  }

  /** 启动轮询定时器 + visibilitychange 监听 (oncreate 调用)。 */
  _startSchedulePoll() {
    this._stopSchedulePoll();
    const ms = this._schedulePollIntervalMs();
    this._pollIntervalMs = ms;
    // ⚠️ 首次**不要**立刻执行 ⇒ 等第一个间隔 (避免与首屏 load() 抢)。
    this._pollTimer = setInterval(() => {
      this._pollOnce();
    }, ms);
    // ⚠️ 页面从隐藏变可见 ⇒ **立刻补一次** (用户刚回来就该看到最新的)。
    //   这一轮的指纹基准可能已经过期 ⇒ 照 _pollOnce() 的 ⑧⑨⑩ 走即可
    //   (基准是 null 就只建基准, 不会误触发刷新)。
    this._pollVisHandler = () => {
      if (this._isPageVisible()) this._pollOnce();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._pollVisHandler);
    }
  }

  /** 停轮询定时器 + 摘掉监听 (onremove 调用) —— ⛔ 必须成对, 否则泄漏。 */
  _stopSchedulePoll() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
    if (this._pollVisHandler) {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this._pollVisHandler);
      }
      this._pollVisHandler = null;
    }
  }

  /**
   * v0.19.00 — 一轮静默轮询 (brief §2.3 逐字照做)。
   *
   * ⚠️ 任何一步失败都**静默** return: 不弹错、不改 this.* 状态、不抛异常、不 console.error。
   *   下一轮到了自动重试。
   */
  async _pollOnce() {
    // ④ 组件已被移除 ⇒ 直接返回 (别去碰已经不存在的界面)
    if (this._removed) return;
    // ① 「此刻不该轮询」四种情况 (S1 可见性 / S2 拖拽中 / S3 modal 打开 / S4 load 中)
    if (!this._isPageVisible()) return;
    if (this._dragging) return;
    if (this._hasOpenModal()) return;
    if (this.loading) return;
    // ③ 日历还没挂上 (FC vendor 脚本还没到 / 这天没配课表) ⇒ 没得比, 直接返回
    if (!this._calendar) return;
    // ⑤ 并发保护: 上一轮还没跑完就又到点了 ⇒ 跳过这一轮 (别把请求叠起来)
    if (this._polling) return;
    this._polling = true;
    try {
      // ── 拉 5 个端点 (口径与 load() **完全一致**) ────────────────────────
      //   ⚠️ dateStr 一律用 activeDateStr() —— ⛔ 不另算 (load() L698 的注释写死了
      //     「另算就会出现『表头 10-16、课表却是今天那版』的分叉」)。
      const dateStr = this.activeDateStr();
      const params = {
        date: dateStr,
        start: dateStr + ' 00:00:00',
        end: dateStr + ' 23:59:59'
      };
      if (this.activeSchoolId) params.school_id = this.activeSchoolId;
      // ⚠️⚠️ **静默的关键**: Flarum 2.0 的 `app.request` 请求失败时, **默认会弹一条全局
      //   错误 Alert** (core Application.tsx 的 requestErrorCatch → requestErrorDefaultHandler)。
      //   ⇒ 直接用默认行为的话, 一次网络抖动就会在用户屏幕上弹红条 ⇒ 违背「轮询必须完全静默」。
      //   ⇒ core 的约定: errorHandler **返回 false 才会回落到默认处理器** ⇒ 想安静就别返回 false。
      //   ⇒ 这里给每个请求各挂一个「什么都不做」的 errorHandler (见 Api.list 的注释)。
      const silentErrorHandler = () => {/* 静默: 轮询失败不打扰用户, 下一轮自动重试 */};
      const tasks = [
      // ⚠️ lessons / slot-reservations / rooms 在 load() 里**没有** catch
      //   ⇒ 它们失败时 load() 整个失败 ⇒ 轮询这里也**保持 reject**:
      //   本轮指纹算不出来 ⇒ 跳过本轮 (等端点恢复, 下一轮自然能比出变化)。
      Api.list('lessons', params, {
        errorHandler: silentErrorHandler
      }), Api.list('slot-reservations', this.activeSchoolId ? {
        school_id: this.activeSchoolId,
        date: dateStr
      } : {
        date: dateStr
      }, {
        errorHandler: silentErrorHandler
      }),
      // v0.19.00 F2 — **教室** = 日历的**列** (load() L711 拉它, 轮询也拉)
      //   ⇒ 教室改名 / 新增教室 ⇒ 列头也能自动更新 (辉哥 10/26 拍板「全包」)。
      Api.list('rooms', this.activeSchoolId ? {
        school_id: this.activeSchoolId
      } : {}, {
        errorHandler: silentErrorHandler
      })];
      // ⚠️ 固定课 / 课表刻度两个端点的**存在条件**跟 load() 一样:
      //   · 固定课: 要有校区, 且**学员不发**这个请求 (v0.17.76);
      //   · 课表:   要有校区 (没校区时压根不发请求, 见 load() 的 else 分支)。
      if (this.activeSchoolId) {
        // ⚠️⚠️ v0.19.00 F1 — 这两个端点的**失败语义必须与 load() 逐字对齐**:
        //   · load() L774: `Api.listFixedLessons(...).catch(() => [])`
        //   · load() L744-750: `Api.getCurrentSchedule(...).catch(() => ({ok:false, res:null}))`
        //   ⇒ 它们在 load() 里是「**可以失败但不影响整体**」的 ⇒ 轮询也必须这样。
        //   ⛔ **不**对齐的后果 (真会咬人): `fixed-lessons` / `schedule` 只要对某个角色
        //     **长期**失败 (403 权限不足 / 500 / 校区没配课表), Promise.all 整轮 reject ⇒
        //     **指纹根本算不出来** ⇒ 这个用户的**课块变化永远检测不到**, 而且完全静默
        //     (用户只会觉得「这功能没生效」, 没有任何线索) —— 而 load() 是能成功的。
        //   ⚠️ 兜底值必须**稳定** (固定 [] / null, 不用时间戳之类) ⇒ 否则指纹每轮都变
        //     ⇒ 每 60s 白刷一次。
        //   ⚠️ 兜底后这轮指纹会与上一轮不同 ⇒ 会**多触发一次 load()** —— 这是**对的**:
        //     课表/固定课刚刚从「拿不到」变成「拿得到」, 本来就该刷新一次。
        tasks.push(Api.listFixedLessons({
          school_id: this.activeSchoolId
        }, {
          errorHandler: silentErrorHandler
        }).catch(() => []));
        tasks.push(Api.getCurrentSchedule(this.activeSchoolId, {
          date: dateStr
        }, {
          errorHandler: silentErrorHandler
        }).catch(() => null));
      }
      // ⑥ 任一端点失败 ⇒ Promise.all reject ⇒ 落到 catch ⇒ 静默 return
      //   ⚠️ 只有 lessons / slot-reservations / rooms 走到这里 (它们在 load() 里也没有
      //     catch ⇒ load() 同样会失败 ⇒ 轮询跳过本轮是对的)。
      const res = await Promise.all(tasks);
      // ── 算指纹 ────────────────────────────────────────────────────────
      //   5 个端点的**原始返回值**各 stringify 一次, 拼成一个字符串。
      //   ⚠️ 为什么不自己挑字段: 课次/预留/固定课可改的字段太多 (时间/教室/教练/状态/备注…)
      //     ⇒ 挑字段必然漏一个 ⇒ 全量 stringify 最可靠; 数据量极小 (生产课次总共 4 行)。
      const fp = res.map(v => JSON.stringify(v === undefined ? null : v)).join('\u0000');
      // ⑧ 还没有基准 ⇒ **只建基准, 不触发刷新** (第一次轮询永远不刷新)
      if (this._fp === null) {
        this._fp = fp;
        return;
      }
      // ⑨ 没变 ⇒ 什么都不做 (99% 的情况)
      if (fp === this._fp) return;
      // ⑩ 变了 ⇒ 更新基准 + 复用现有的 load() 完整刷新
      this._fp = fp;
      await this._pollRefresh();
    } catch (e) {
      // ⑥ 失败 ⇒ 静默。不弹错、不改状态、不抛、不 console.error (brief §6)。
      //   ⚠️ 也不动 this._fp —— 基准保持上一轮的值, 下一轮继续拿它比。
    } finally {
      this._polling = false;
    }
  }

  /**
   * v0.19.00 — 「变了 ⇒ 刷新」, 但要保住用户当前的视图与滚动位置。
   *
   * ⚠️ 为什么必须存还: load() 收尾会 `tryMountCalendar()` ⇒ 那里有 `this._calendar.destroy()`
   *   ⇒ **重建 FC 实例** ⇒ 当前视图日期与滚动位置会丢 (用户可能正翻到别的时段)。
   */
  async _pollRefresh() {
    // ① 先把用户当前的「视图日期 + 滚动位置」存下来
    const saved = this._captureCalendarView();
    if (saved) {
      // ⚠️ v0.19.00 F3 — 给这份「待还原」设一个**明确的寿命**。
      //   为什么现在不咬人: FC 是 `headerToolbar: false` ⇒ 没有 prev/next 导航 ⇒ 日历的
      //   视图日期恒等于 activeDateObject(); 而唯一能改日期的入口 (日期选择器) 会走
      //   load() ⇒ load() 开头会把待还原值清掉 ⇒ 残留值拨不回任何东西。
      //   什么时候会咬人: ① 日后有人给 FC 加了导航按钮; ② 新增了**不走 load()** 的改日期
      //   入口 ⇒ 那时残留的旧日期会把用户的视图**拨回旧日期** ⇒ 必须清干净。
      //   ⇒ 还原窗口结束后 (下面那个 timer) 主动清掉, 宁可少还原一次, 也不能拨错日期。
      saved.until = Date.now() + ZS_POLL_VIEW_RESTORE_WINDOW_MS;
    }
    // ② 挂上「待还原」⇒ 之后**每一次** FC 重建都会把还原值应用上去
    //   (见 tryMountCalendar() 末尾的 _applyPendingViewRestore)。
    //   ⚠️ 必须在 load() **之前**挂 —— 因为 load() 的 finally 里就会重建一次。
    this._pendingViewRestore = saved;
    if (this._pendingViewRestoreTimer) {
      clearTimeout(this._pendingViewRestoreTimer);
      this._pendingViewRestoreTimer = null;
    }
    if (saved) {
      this._pendingViewRestoreTimer = setTimeout(() => {
        this._pendingViewRestore = null;
        this._pendingViewRestoreTimer = null;
      }, ZS_POLL_VIEW_RESTORE_WINDOW_MS);
    }
    this._pollRefreshing = true;
    try {
      await this.load();
    } finally {
      this._pollRefreshing = false;
      // ③ load() **真正完成之后**再还原 (brief §4: ⛔ 不许在 load 调用前就 gotoDate,
      //    那时旧实例还在, 拨过去会被随后的重建冲掉)。
      this._restoreCalendarView(saved, true);
    }
  }

  /**
   * v0.19.00 — 取「当前视图 + 滚动位置」的快照。
   * ⚠️ 取不到就返回 null (调用方据此只做「不还原」, 不猜、不硬编)。
   */
  _captureCalendarView() {
    const cal = this._calendar;
    if (!cal) return null;
    const out = {
      date: null,
      fcScrollTop: null,
      pageScrollY: null
    };
    try {
      const d = cal.getDate();
      if (d && !Number.isNaN(d.getTime())) out.date = new Date(d.getTime());
    } catch (e) {/* 取不到就留 null */}
    const el = this._fcScrollerEl(cal);
    if (el) {
      try {
        out.fcScrollTop = el.scrollTop;
      } catch (e) {/* noop */}
    }
    try {
      out.pageScrollY = typeof window !== 'undefined' ? window.pageYOffset || document.documentElement && document.documentElement.scrollTop || 0 : null;
    } catch (e) {/* noop */}
    return out;
  }

  /**
   * v0.19.00 — 找出「FC 内部真正在滚的那个元素」。
   *
   * ⚠️ brief §4 写的是 `view.scrollerEl` —— **实测 FC v6.1.21 没有这个字段**
   *   (vendor index.global.min.js 里只有 `scrollerElRefs`, 是个 RefSet)。
   *   ⇒ 这里按「先 brief 指定的字段、再 FC v6 实际的字段」的顺序取, 两个都不行就返回 null
   *   (退化 = 只还原日期, 如实上报, ⛔ 不改 FC 源码 / 不 monkey patch)。
   */
  _fcScrollerEl(cal) {
    if (!cal) return null;
    try {
      const v = cal.view;
      if (!v) return null;
      if (v.scrollerEl) return v.scrollerEl;
      const refs = v.scrollerElRefs;
      if (refs && typeof refs.getAll === 'function') {
        const all = refs.getAll() || [];
        for (let i = 0; i < all.length; i++) {
          if (all[i]) return all[i];
        }
      }
    } catch (e) {/* noop */}
    return null;
  }

  /**
   * v0.19.00 — 还原「视图日期 + 滚动位置」。
   * @param {boolean} withPageScroll 是否顺带还原**页面**滚动位置。
   *   ⚠️ 本扩展 FC 用 `height: 'auto'` ⇒ **FC 内部根本不滚**, 真正在滚的是**页面**
   *     ⇒ 用户看得见的「滚动位置」其实是 window.scrollY。
   *   ⚠️ 页面滚动**只在 load() 之后还原一次**: 若每次 FC 重建都还原, 用户刚滚一下
   *     就被某个重绘拨回去 ⇒ 反而更糟。
   */
  _restoreCalendarView(saved, withPageScroll) {
    if (!saved) return;
    const cal = this._calendar;
    if (cal) {
      // ① 视图日期 —— 只在**真的不一样**时才 gotoDate (避免无谓的日期切换 + datesSet 抖动)
      try {
        if (saved.date) {
          const cur = cal.getDate();
          if (!cur || cur.getTime() !== saved.date.getTime()) {
            cal.gotoDate(saved.date);
          }
        }
      } catch (e) {/* noop */}
      // ② FC 内部滚动位置
      const el = this._fcScrollerEl(cal);
      if (el && typeof saved.fcScrollTop === 'number') {
        try {
          if (el.scrollTop !== saved.fcScrollTop) el.scrollTop = saved.fcScrollTop;
        } catch (e) {/* noop */}
      }
    }
    // ③ 页面滚动位置
    if (withPageScroll && typeof saved.pageScrollY === 'number' && typeof window !== 'undefined') {
      try {
        const now = window.pageYOffset || 0;
        if (Math.abs(now - saved.pageScrollY) > 1) window.scrollTo(0, saved.pageScrollY);
      } catch (e) {/* noop */}
    }
  }

  /**
   * v0.19.00 — FC **每次**重建之后, 把「待还原」的视图状态应用到**新实例**上。
   *
   * ⚠️ 为什么不能只在 load() 之后还原一次:
   *   load() 的 finally 里 `m.redraw()` 是**异步**的 (mithril 2 的 m.redraw 走 rAF,
   *   不是 sync) ⇒ 它引发的 `onupdate()` 会**再重建一次** FC; `checkConflicts()` 收尾
   *   还会再重建一次 ⇒ 只还原一次的话, 还原值会被后来的重建整个冲掉。
   */
  _applyPendingViewRestore() {
    const saved = this._pendingViewRestore;
    if (!saved) return;
    // ⚠️ v0.19.00 F3 — 还原窗口已过 ⇒ 这份还原值作废 (宁可少还原, 也不能拨错日期)
    if (saved.until && Date.now() > saved.until) {
      this._pendingViewRestore = null;
      if (this._pendingViewRestoreTimer) {
        clearTimeout(this._pendingViewRestoreTimer);
        this._pendingViewRestoreTimer = null;
      }
      return;
    }
    // ⚠️ 这里**不**还原页面滚动 (见 _restoreCalendarView 的注释):
    //   页面滚动只在 load() 之后还原一次, 否则用户滚一下就被某个重绘拨回去。
    this._restoreCalendarView(saved, false);
  }

  /**
   * v0.8.9 — 约课事件块 4 行内容 (辉哥 10/7 12:25 拍板)
   *
   * 改前 (v0.6.45 起, 4 行纯文字, 无头像 / 无余额 / 无卡名):
   *   1 学员名
   *   2 教练名
   *   3 分类 · 类型
   *   4 备注
   * 改后 (本次):
   *   1 学员头像 + 学员名 + ' · ' + 学员卡余额   (余额 '5/5节' / 无限次卡 '不限次')
   *   2 教练头像 + 教练名                       (纯姓名, 辉哥明确要求不带课程类型)
   *   3 分类 · 类型 · 学员卡名
   *   4 备注
   *
   * 设计要点:
   *   · 返回 **纯文本行数组 + 用户对象** 而不是 DOM —— 同一份内容要被两处消费:
   *       (a) eventContent (FC 事件块 4 行渲染, 在此基础上再包头像 DOM)
   *       (b) title 属性 (hover tooltip / 移动端长按)
   *     改前这两处各写一份字面量 (L411-425 与 L606-636), 是必然漂移的重复代码:
   *     本次加余额/卡名时改一处忘另一处, tooltip 就会缺信息。
   *     收敛成这一个方法后, 两处永远同源 (验收第 12 条之外的隐含要求)。
   *   · 空值降级: 每段独立 filter(Boolean), 任何缺失都不会渲染出 "undefined"/"null" 字面量,
   *     也不会留下孤立的 ' · ' 分隔符 (v0.8.9 验收第 6 条)。
   *   · 无限次判定跟后端 StudentCard::isUnlimited() (src/Model/StudentCard.php L83-86)
   *     同口径: total_lessons === 0 || remaining_lessons === -1。
   *     接口虽也返 is_unlimited 布尔值, 但以本地判定为准 —— 以模型方法为唯一事实源,
   *     避免后端改判定而前端读陈旧布尔值导致两端不一致 (v0.8.5 注释里记的历史坑)。
   */
  buildLessonLines(l) {
    const card = l.student_card || null;

    // 学员: 无 student 对象 / 无名字时退回 '学员 #<id>' (跟 v0.6.40 起的老行为一致)
    const studentName = l.student ? l.student.display_name || l.student.username || '' : '';
    // ── v0.17.0 — 体验课标记 (辉哥 10/12 拍板) ──────────────────────────
    //   判据**必须在 studentText 之前**算出来: 体验课的 student_id 是 NULL
    //   (客户还没注册成 Flarum 用户), 兜底文案 `'学员 #' + l.student_id`
    //   会渲染成「学员 #null」/「学员 #0」—— 一个根本不存在的学员。
    //   体验课改显「体验客户」。
    //   ⚠️ 严格判 true/1/'1', 不用 truthy: 后端这一列是 int, 但不同序列化路径下
    //     可能落到字符串 "0" —— 而 "0" 在 JS 里是 **truthy**,
    //     用 truthy 会让**每一节正课**都挂上「体验」角标。
    //   ⚠️ 最后一段 (is_trial 为 null/undefined 时回落到 sales_followup_id 非空)
    //     是给老缓存兜底的: 早于本轮的缓存响应里没有 is_trial 这一列。
    const isTrial = !!(l && (l.is_trial === true || l.is_trial === 1 || l.is_trial === '1' || (l.is_trial === null || l.is_trial === undefined) && !!l.sales_followup_id));
    const studentText = studentName || (isTrial ? '体验客户' : '学员 #' + l.student_id);

    // 教练: 无教练 → 空串, 第 2 行整体不渲染 (辉哥拍板: 不显示占位文案)
    const coachName = l.coach ? l.coach.display_name || l.coach.username || '' : '';
    const typeLabel = l.lesson_type_id ? LessonOptions.labelFor('type', l.lesson_type_id) : '';
    const catLabel = l.lesson_category_id ? LessonOptions.labelFor('category', l.lesson_category_id) : '';
    const cardName = card && card.name ? String(card.name) : '';

    // 第 1 行尾部余额: 无卡不显示; 无限次卡显示 '不限次'; 否则 '剩余/总节'
    let balanceText = '';
    if (card) {
      const total = Number(card.total_lessons);
      const remaining = Number(card.remaining_lessons);
      const unlimited = total === 0 || remaining === -1;
      if (unlimited) {
        balanceText = '不限次';
      } else if (!isNaN(total) && !isNaN(remaining)) {
        balanceText = remaining + '/' + total + '节';
      }
    }

    // 余额拼进行 1 —— 没有余额时行 1 就是纯姓名, 不留分隔符
    //
    // ── v0.9.0 固定课标签 (决策 B4 + B2「已过期」位置) ──────────────────
    //   「固定课」: 明示来源 (辉哥拍板 B4: 固定课块上明示, 不做菜单顶部来源标注)
    //   「已过期」: **紧跟余额之后**, 绝不顶掉卡名行 (任务书明确要求)
    //
    //   ⚠️ 为什么不把「已过期」塞进行 3/4:
    //     行 3 是「分类 · 类型 · 卡名」, 行 4 是备注 —— 那是学员最需要看的信息,
    //     拿掉一行去放一个状态标签, 是拿信息密度换状态可见性, 不划算。
    //   ⚠️ 为什么行 1 放得下: 规格图 (design/fixed-class-spec.html) 定死三档块高
    //     99px / 49px / 33px, 行高由 20px 头像决定, 标签做成 16px 以内 (矮于 20px)
    //     就不会撑高任何一档。宽度不够时走 ellipsis 截断 (LESS 里处理)。
    const isFixed = !!(l && l.fixed_lesson_id);
    // v0.12.0 — 半节课时标记 (辉哥 10/9 拍板)
    //   ⚠️ 用 Number() 比较而不是 === 0.5: 后端 list 接口这一列走 JSON,
    //      老缓存/不同序列化路径下可能是字符串 "0.5", 字符串比较会永远不相等,
    //      角标就静默不显示 (典型的「以为加了功能其实没生效」)。
    //   严格只认 0.5: 1 / 1.0 / 缺失 一律 false, 一节课绝不打这个角标。
    const isHalf = Number(l && l.lesson_charge) === 0.5;
    const cardExpired = !!(l && l.fixed && l.fixed.card_expired);
    // 单次例外: 仅在固定课模式下才显示红角标 (正常模式不该用红块吓人, 用户不需要关心)
    const isSingleException = isFixed && !!l.is_overridden;
    const tags = [];
    if (isFixed) tags.push('固定课');
    if (cardExpired) tags.push('已过期');
    const tagsText = tags.length ? tags.join('·') : '';
    const line1Parts = [studentText];
    if (balanceText) line1Parts.push(balanceText);
    if (tagsText) line1Parts.push(tagsText);
    const line1 = line1Parts.join(' ');

    // 第 3 行: 卡名接在分类/类型之后, 全部 filter(Boolean) 兜住任一段缺失
    const line3 = [catLabel, typeLabel, cardName].filter(Boolean).join(' · ');

    // ── v0.17.57 — 课块自己的**真实**起止时间 (eventContent 第三行 / tooltip 首行) ──
    //   为什么必须有这一行: 课表态下每个时间段固定 1 行、课块填满整格, 于是**块高不再
    //   体现时长** —— 30 分钟和 90 分钟的课长得一模一样。高度这条信息通道一断, 就得用
    //   文字补回来 (老板拍板第 6 条), 否则截图分享出去对方根本看不出这节课几点到几点。
    //
    //   ⚠️ 只在**有课表**时产出。兜底态 (没课表的校区) 块高仍然体现时长 (30 分钟的课就是半格高),
    //     左列也是真实时间刻度, 补这一行纯属噪音 —— 而且那些校区有 20min/33px 的矮块档,
    //     多塞一行会直接溢出。所以「什么时候要显示」是条件, 不是无脑加。
    //
    //   数据源是 lesson 自己的 start_at/end_at (FC 的 event.start/end 在课表态是名义时间,
    //   绝不能拿它们当真实时间用)。
    const timeLine = this.hasSchedule() && l.start_at && l.end_at ? (() => {
      const s = zsParseLocal(l.start_at);
      const e = zsParseLocal(l.end_at);
      if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
      return this._minutesToHHMM(this._minutesOfDay(s)) + ' – ' + this._minutesToHHMM(this._minutesOfDay(e));
    })() : '';
    return {
      line1: line1,
      line2: coachName,
      line3: line3,
      line4: l.notes ? String(l.notes).slice(0, 20) : '',
      // v0.17.57 — 真实起止 (第三行)。空串 = 本校区没有课表, 不渲染这一行。
      timeLine: timeLine,
      student: l.student || null,
      coach: l.coach || null,
      // ── v0.9.0: 给渲染层用 (classNames / 角标 / tooltip) ──
      isFixed: isFixed,
      // v0.12.0 — 半节课时 (eventContent 据此打「半节」角标)
      isHalf: isHalf,
      // v0.17.0 — 体验课 (eventContent 据此打「体验」角标)
      isTrial: isTrial,
      cardExpired: cardExpired,
      isSingleException: isSingleException,
      // 「每周重复」副标题: 固定课模式下悬停/长按要能看出这是每周几的课
      fixedHint: isFixed && l.fixed ? '固定课 · 每' + String(l.fixed.day_label || '').replace(/^周/, '') + ' ' + String(l.fixed.start_time || '').slice(0, 5) + '-' + String(l.fixed.end_time || '').slice(0, 5) : ''
    };
  }

  /**
   * v0.8.9 — 超短课 (第二档紧凑) 判定, 供 events.map 打 zs-ev-tiny class
   *
   * 阈值 20min 的来历 (60min/slot @ .fc-timegrid-slot 100px, forum.less L1611):
   *   · 30min → 50px: 紧凑档 3 行 (学员+余额 / 教练 / 分类·类型·卡名) ≈ 40.55px, 装得下
   *   · 20min → 33px: 紧凑档 40.55px **装不下** → 必须再砍第 3 行,
   *     tiny 档 2 行 ≈ 29.75px 才装得下
   *   按 slot 时长等比换算而不是写死像素: SCD 的 lesson_duration_minutes 是可配的,
   *   换成 30min/slot 时 20min 课反而是满格 100px, 这时它不该被判超短 ——
   *   所以这里比的是"时长 ≤ slotDuration 的三分之一"这种相对量, 像素换算留在 LESS 段。
   */
  isUltraShortLesson(l) {
    if (!l || !l.start_at || !l.end_at) return false;
    const durMin = (new Date(l.end_at.replace(' ', 'T')) - new Date(l.start_at.replace(' ', 'T'))) / 60000;
    if (!(durMin > 0)) return false; // 脏数据 (end <= start) 不当超短处理, 免得误砍信息
    const slotMin = this.slotMin(); // 复用 slotMinFromSCD 口径, 跟 slotDuration 同源
    return durMin <= slotMin / 3;
  }

  /**
   * v0.8.9 — 事件块内 16px 圆形头像 DOM (辉哥 10/7 12:25 拍板: 学员/教练各带一个头像)
   *
   * 改前: 事件块 4 行全是纯文字, 没有头像。
   * 改后: 第 1/2 行各自前置一个 16px 圆形头像, 排在名字**前面**。
   *
   * 头像兜底 (验收第 6 条: 不允许破图):
   *   ListLessonsController 返回的 avatar_url 可能是 null (本地实测学员 'dsl' 就是 null),
   *   此时渲染 Flarum 惯用的字母头像 (首字母 + user.color() 主题色), 而不是 <img> 破图。
   *   背景色取自 Flarum User 模型的 color() (username 哈希 → 稳定主题色),
   *   跟论坛其他字母头像同色系, 视觉不突兀; store 没命中就退默认色。
   *   不直接用 <Avatar> 组件: eventContent 返回 domNodes (原生 DOM), 不是 mithril vnode,
   *   挂不了组件 —— 这里手搓最小 DOM 结构。
   */
  buildEventAvatarNode(user, fallbackLetter) {
    const span = document.createElement('span');
    span.className = 'zs-ev-avatar';
    const name = user && (user.display_name || user.username) || '';
    const letter = fallbackLetter || name.charAt(0) || '?';
    // v0.17.52 — 底色改走 Flarum 原生 stringToColor(display_name), **不再依赖 app.store**
    //
    //   改前 (bug): 只在 app.store 里已经有该 User model 时才拿得到 color()。
    //     首次进日历 tab 时, 日历自己不做任何用户预热 —— 全站只有 LessonsTab.prefetchUsers
    //     (js/src/forum/components/LessonsTab.js L316-327) 会 app.store.find('users', uid)。
    //     ⇒ model 为 null ⇒ bg = '' ⇒ span.style.background = '' ⇒ 掉回 LESS
    //     (less/forum.less .zs-ev-avatar 的 background: #374151) 深灰蓝。
    //     「切去约课管理再切回就变米色」不是玄学, 正是那次切换把 store 预热了。
    //
    //   改后: LessonSerializer 已经把 display_name 带过来了 (student / coach 两块都是
    //     nickname ?? username, 与服务端 User displayName 同一算法 —— 实测全站 209 个用户
    //     逐字相等), 于是**同步**算色即可: 零请求、首帧即正确, 与 store 预热顺序完全无关。
    //     ✓ stringToColor 返回的是**不带 #** 的 hex, 这里自己补前缀。
    //     ✓ import 必须走子路径拿 default export —— common.ts 那行
    //       `import './utils/stringToColor'` 是 side-effect import, 不 re-export, 从
    //       'flarum/common' 拿不到具名导出。
    //   拿不到名字 (name 为空) 时保持 bg='', 让 LESS 的 #374151 继续做最后兜底 ——
    //     那条默认值仍然有用, 不能删。
    let bg = '';
    try {
      if (name) bg = '#' + stringToColor(name);
    } catch (e) {
      bg = ''; // 拿不到名字/算色异常一律退默认色, 绝不让头像把整个事件块渲染打断
    }
    // 有真实头像 → <img>, 但仍挂 onerror: avatar_url 指向的文件可能已被删 (404) → 换字母头像
    if (user && user.avatar_url) {
      const img = document.createElement('img');
      img.className = 'zs-ev-avatar-img';
      img.src = user.avatar_url;
      img.alt = '';
      img.draggable = false;
      img.addEventListener('error', () => {
        span.textContent = letter;
        span.style.background = bg || '';
        span.style.color = 'var(--zs-on-brand-fff, #fff)';
      });
      span.appendChild(img);
    } else {
      span.textContent = letter;
      span.style.background = bg || '';
      span.style.color = 'var(--zs-on-brand-fff, #fff)';
    }
    return span;
  }

  // FullCalendar 等 ready (vendor script 异步加载, 同 ziven-event setInterval 模式)
  tryMountCalendar() {
    // v0.6.8b — V 测 FAIL fix: FC eventClick handler 引用 self.lessons / self.openBooking,
    //   没有 self 会退到 window.self 全局变量 → pageerror "Cannot read properties of undefined"
    //   之前 self 只在 bindBookingTriggers 顶部定义, 那边的 native 事件闭包走通了,
    //   FC config eventClick (在 tryMountCalendar 内) 走不通
    //   (eventClick 已于 v0.7.9e 删除; 这里的 self 现在还服务 eventDidMount/eventAllow 等回调)
    const self = this;
    const dom = this.element || this.dom;
    if (this._fcRetryTimer) {
      clearTimeout(this._fcRetryTimer);
      this._fcRetryTimer = null;
    }
    if (typeof window.FullCalendar === 'undefined' || !dom) {
      this._fcRetryTimer = setTimeout(() => this.tryMountCalendar(), 100);
      return;
    }
    const FC = window.FullCalendar;
    if (!FC.Calendar) {
      this._fcRetryTimer = setTimeout(() => this.tryMountCalendar(), 100);
      return;
    }
    const el = dom.querySelector('.ZS-calendar-fc-mount');
    if (!el) {
      // v0.17.61 — mount 点不存在 = 本轮不渲染 fc (无课表 / 加载中, 见 scheduleState())。
      //   这里**必须**顺手销毁可能残留的实例: 从「有课表的校区」切到「没配课表的校区」时,
      //   mithril 已经把挂 fc 的那个 div 从 DOM 上摘了, 但 this._calendar 仍指向一个
      //   挂在**已脱离文档**的节点上的实例。不销毁的后果是: 事件源还在跑、resize/scroll
      //   监听还挂着 (FC destroy 时才解绑), 而且 _syncAxisToDate / datesSet 之类回调
      //   还能摸到一个不该存在的日历。
      if (this._calendar) {
        this._calendar.destroy();
        this._calendar = null;
      }
      return;
    }
    if (this._calendar) {
      this._calendar.destroy();
      this._calendar = null;
    }

    // r.1 FAIL CRITICAL 2 修复: 从 FC.globalPlugins 按 name 找对应 plugin
    const plugins = (FC.globalPlugins || []).filter(p => p && p.name);
    const findPlugin = name => plugins.find(p => p.name === name);
    // v0.6.43a — 加 @fullcalendar/interaction (辉哥 9/30 9:27+ 反馈, 拖拽不动 root cause)
    //   vendor file 已在 index.js L57-65 loadScriptsSequential 末尾加载 (interaction.global.min.js v6.1.21)
    //   但 FC v6 架构: 脚本加载仅注册到 FC.globalPlugins, Calendar 实例必须显式列在 plugins: []
    //   否则 interaction plugin 不会被这个 Calendar 实例激活, editable/eventDrop 全部失效
    //   必须在 resource-timegrid 之后 (interaction 注册事件依赖 timegrid 列布局)
    const calendarPlugins = [findPlugin('@fullcalendar/timegrid'), findPlugin('@fullcalendar/resource-timegrid'), findPlugin('@fullcalendar/resource'), findPlugin('@fullcalendar/resource-daygrid'), findPlugin('@fullcalendar/daygrid'), findPlugin('@fullcalendar/premium-common'), findPlugin('@fullcalendar/interaction'),
    // v0.7.8p — scrollgrid (dayMinWidth 横向滚动的硬依赖, 缺则白屏)
    findPlugin('@fullcalendar/scrollgrid')].filter(Boolean);

    // v0.5.0e Glassmorphism: room.color hex -> 4 色 class (blue/purple/orange/green)
    const colorMap = {
      '#2d7be5': 'blue',
      '#8b5cf6': 'purple',
      '#f59e0b': 'orange',
      '#10b981': 'green',
      '#34c759': 'green'
    };
    const roomColorClass = {};
    const roomHeaderClass = {}; // v0.5.0f: 列头 zs-room-* className 给 LESS ::before 用
    this.rooms.forEach(r => {
      const c = (r.color || '#2D7BE5').toLowerCase();
      const name = colorMap[c] || 'blue';
      roomColorClass[r.id] = 'zs-ev-' + name;
      roomHeaderClass[r.id] = 'zs-room-' + name;
    });
    // v0.8.10 — 教练色 -> zs-ev-c-<name> class (辉哥 10/7 13:03 报: 事件块按教练上色)
    //   ⚠️ 这份 hex 列表必须跟 src/Helpers/CoachColorPalette.php 的 PALETTE 完全一致,
    //     以及 AssignStaffModal.js 的 COACH_PALETTE。不一致 → 后端挑出的色映射不到
    //     → 事件块静默回落教室色, 表现为"改了教练颜色没反应"。
    //   找不到映射时返回 null (而不是兜底某个颜色) ——
    //   让下面的回落逻辑走教室色, 这是验收第 4 条要求的老数据行为, 静默给个默认色反而掩盖问题。
    // v0.10.1 — 10 色扩到 22 色, 新增的 12 个 name 必须与 less/forum.less 里
    //   本段末尾新增的 .zs-ev-c-* class 一一对应 (少一个 = 该色选中后事件块无色)。
    const COACH_COLOR_MAP = {
      '#2d7be5': 'blue',
      '#f59e0b': 'orange',
      '#10b981': 'green',
      '#8b5cf6': 'purple',
      '#ef4444': 'red',
      '#06b6d4': 'cyan',
      '#ec4899': 'pink',
      '#84cc16': 'lime',
      '#f97316': 'deeporange',
      '#6366f1': 'indigo',
      // ── v0.10.1 新增 ──
      '#0ea5e9': 'sky',
      '#14b8a6': 'teal',
      '#22c55e': 'spring',
      '#65a30d': 'olive',
      '#eab308': 'gold',
      '#d946ef': 'fuchsia',
      '#7c3aed': 'violet',
      '#e11d48': 'rose',
      '#0891b2': 'darkcyan',
      '#059669': 'emerald',
      '#c084fc': 'lavender',
      '#b45309': 'bronze'
    };
    // 教练色 → class 的查表: 每节课的 coach_id + school_id 决定取哪个人的色
    //   (ListLessonsController v0.8.10 已经按该课自己的 school_id 取好了 coach.coach_color,
    //    这里不再关心校区, 直接读 l.coach.coach_color)
    const coachColorClassOf = l => {
      const raw = l && l.coach && l.coach.coach_color;
      if (!raw) return null; // 老数据 / 没设色 → null
      const name = COACH_COLOR_MAP[String(raw).toLowerCase()];
      return name ? 'zs-ev-c-' + name : null;
    };
    const conflictIds = new Set(this.conflicts.map(c => c.lesson.id));
    // v0.15.0 — 变量名 lessons → lessonEvents: 现在 events 传的是「两条 source」,
    //   这个数组只装课块, 名字带上 Events 后缀是为了跟下面的 buildReservationEventDefs() 对齐读法。
    //   内容一字未改。
    const lessonEvents = this.lessons.map(l => ({
      id: String(l.id),
      resourceId: 'r_' + l.room_id,
      // v0.6.40 — 用真实姓名替代 '学员 #N' (辉哥 9/30 8:10 反馈, 后端 ListLessonsController L58 加 user 预加载)
      //   优先 display_name (nickname ?? username), fallback 到 id (跟 ListSalesFollowupsController L70 同款)
      // v0.6.41 — 之前决策 "保持简洁, FC title 只显示学员名, 避免 cell 爆框" — 已被 v0.6.45 推翻
      // v0.6.45 — 辉哥 9/30 9:59+ 反馈拍板丰富展示, title 字段保留作 hover/long-pressed tooltip
      //   4 行结构: 学员 / 教练 / 分类·类型 / 备注 (跟 FC eventContent 渲染同款, 保持一致)
      // v0.8.9 — 内容改由 buildLessonLines() 统一产出 (辉哥 10/7 12:25 拍板):
      //   改前这里跟 eventContent 各写一份字面量 (学员/教练/分类·类型/备注),
      //   本次事件块加了余额 + 卡名 + 头像, tooltip 必须同步跟上, 否则 hover 看到的是旧信息。
      //   两处收敛到同一个方法 → 结构上不可能再漂移。
      // ⚠️ v0.8.9 实测更正: FC v6 **不会**把 title 字段写进 DOM (元素也不是 <a>, 无 title 属性),
      //   原生 tooltip 在 v6 已移到 @fullcalendar/tooltip 插件, 本扩展没挂 —— 所以下面这个字段
      //   目前只是 FC event 对象的元数据, 真正让 hover 出字的是 eventDidMount 里的
      //   info.el.setAttribute('title', ...)。两处内容同源 (都用 buildLessonLines), 不会打架。
      //   保留字段: 一旦日后挂了 tooltip 插件, 这里就是现成数据源。
      title: (() => {
        const lines = self.buildLessonLines(l);
        return [lines.timeLine, lines.line1, lines.line2, lines.line3, lines.line4].filter(Boolean).join('\n');
      })(),
      // ── v0.17.57 课表态: 课块按「第几段」定位, 填满整格 ────────────────────
      //   有课表 → start/end 取**名义**行区间 [N×基准, (N+1)×基准], 与真实时长**无关**。
      //     这就是「30 分钟的课和 90 分钟的课一样高」的实现点: 两者都恰好占满一格。
      //     真实起止不丢 —— extendedProps 里原样留着 l.start_at / l.end_at,
      //     eventContent 的第三行与 tooltip 都从那儿读 (见 buildLessonLines 的 timeLine)。
      //   没课表 → 保持真实 start/end, 兜底渲染逐像素不变。
      // ⚠️ 这里**必须**用 Date 对象而不是 'YYYY-MM-DDTHH:mm' 字符串: 字符串走 FC 会按
      //   本地时区解析, 而 zsParseLocal() 也按本地时区构造 —— 两边同口径才不会出现差一天。
      start: (() => {
        if (!self.hasSchedule()) return l.start_at.replace(' ', 'T');
        const idx = self.scheduleRowIndexOfLesson(l);
        if (idx == null) return l.start_at.replace(' ', 'T');
        return self.nominalRowDate(zsParseLocal(l.start_at) || new Date(), idx);
      })(),
      end: (() => {
        if (!self.hasSchedule()) return l.end_at.replace(' ', 'T');
        const base = zsParseLocal(l.start_at);
        const idx = base ? self.scheduleRowIndexOfLesson(l) : null;
        if (idx == null) return l.end_at.replace(' ', 'T');
        return new Date(self.nominalRowDate(base, idx).getTime() + self.nominalSlotMinutes() * 60000);
      })(),
      classNames: [
      // v0.8.10 — 改前这里是纯教室色 `roomColorClass[l.room_id] || 'zs-ev-blue'`:
      //   同一个教室里所有课颜色都一样, 看不出是谁带的。
      //   改后优先用**教练色**; 教练色为 null (老数据 / 没配过色) 时回落教室色,
      //   保证老课表视觉不变、也不会白屏 (验收第 3/4 条)。
      //   注意列头 (roomHeaderClass → zs-room-*) 仍然是教室色: 头是教室维度, 不该跟着教练变。
      coachColorClassOf(l) || roomColorClass[l.room_id] || 'zs-ev-blue', conflictIds.has(l.id) ? 'fc-event-conflict' : '',
      // v0.8.9 — 第二档紧凑 (tiny): 时长 <= 20min 的超短块。
      //   为什么这档不用 FC class, 而是这里按时长算:
      //     FC 的 .fc-timegrid-event-short 只有**一档** (isShort: spanPx < eventShortHeight),
      //     而 20min 块 (≈33px) 连紧凑档的内容 (3 行 ≈ 40.55px) 都装不下, 只靠 FC 那一个 class
      //     仍会溢出 → 需要第二档再砍掉第 3 行 (分类·类型·卡名), 只留 学员+教练 两行 (≈29.75px)。
      //     20min 阈值的来历: 60min/slot @100px → 20min ≈ 33px, tiny 档 2 行 29.75px 刚好装下。
      //   这里用**数据时长**而不是量像素, 原因同 eventShortHeight 处的注释:
      //     eventDidMount 在 FC 布局 pass 之前跑, 量到的 offsetHeight 是错的;
      //     而 classNames 在构造 event 对象时就定好了, 时序上绝对可靠。
      this.isUltraShortLesson(l) ? 'zs-ev-tiny' : '',
      // ── v0.9.0 固定课视觉 (Block 5) ─────────────────────────────────
      //   zs-ev-fixed     斜纹 + 琥珀左边条 (正常模式/固定课模式都有, 决策 B4 明示来源)
      //   zs-ev-expired   卡过期停排 → 「已过期」标签 (B2)
      //   zs-ev-once      单次例外 → 红块 + 「仅本次」角标 (只在固定课模式打)
      //   ⚠️ 背景色**不能**拿来区分: 背景已经被教练色 (10 色) 占满,
      //      改背景会跟教练上色打架 (调研报告 §6.5 同款提醒)。
      //      所以一律走「左边条 + 角标 + 斜纹叠加」。
      //   ⚠️ 超短档 (zs-ev-tiny) 只剩第 1 行, 角标绝对不能再占高度 ——
      //      角标用绝对定位贴在行 1 右上, 不参与流式高度计算 (LESS 里处理)。
      this.isFixedLesson(l) ? 'zs-ev-fixed' : '', l.fixed && l.fixed.card_expired ? 'zs-ev-expired' : '', this.calendarMode === 'fixed' && this.isFixedLesson(l) && l.is_overridden ? 'zs-ev-once' : ''].filter(Boolean),
      extendedProps: l
    }));
    // v0.14.2 — 教室列顺序对齐教室 tab (辉哥 10/9 需求)
    //
    // 【根因】FC v6 的 resource 列顺序**不是**按喂进去的数组顺序, 而是按 resource id 排。
    // 反编译 vendor `resource.global.min.js` 拿到的默认值:
    //     const j = s.parseFieldSpecs("id,title");   // ← DEFAULT_RESOURCE_ORDER
    //     const w = { ... resourceOrder: s.parseFieldSpecs, ... }
    //     // resource-timegrid.global.min.js render():
    //     const order = options.resourceOrder || DEFAULT_RESOURCE_ORDER;
    //     const flat  = this.flattenResources(resourceStore, order);
    // 排序比较器 `compareByFieldSpecs` → `flexibleCompare` (index.global.min.js):
    //     function T(e,t){ return e||t ? (null==t?-1 : null==e?1 :
    //       ("string"==typeof e || "string"==typeof t)
    //         ? String(e).localeCompare(String(t))   // ← 只要有一边是字符串就走字典序!
    //         : e-t) : 0 }
    // 本扩展的 resource id = `'r_' + 数字id` 是**字符串** ⇒ localeCompare 字典序。
    // 生产 4 个教室 id=1/2/3/19 ⇒ `'r_19' < 'r_2'` ⇒ #19 被顶到第 2 位,
    // 而教室 tab 里它是第 4 位 → 两边对不上。
    //
    // 【为什么本地早期测不出来】本地只有 r_96/r_97 两个**连续** id, 字典序恰好等于
    // sort_order 顺序, 看着一直是对的; 直到生产出现 id 不连续 (删除过教室) 才暴露。
    //
    // 【修法 = FC 官方 option `resourceOrder`】
    // 不改 resource id 格式 (改成定长补零也能治, 但要同步改 SlotReservationRenderer /
    // _findColByRoomId / eventDrop 的 `replace(/^r_/,'')` / data-resource-id 选择器,
    // 影响面反而更大)。resourceOrder 是 vendor 里真实被消费的官方扩展点
    // (辉哥 10/7 拍板: 优先框架原生扩展点, 不劫持框架内部), 一个 option 解决。
    //
    // 【resourceOrder 取 'sort_order,roomId' 的理由】
    // parseFieldSpecs 按逗号切, 逐个字段比, 第一个有差就停 —— 跟后端
    // ListRoomsController L24 `orderBy('sort_order')->orderBy('id')` **逐字对齐**。
    //   sort_order: 后端 (int) 序列化 → JSON number → flexibleCompare 走数值比较 ✅
    //   roomId    : 需要**显式的数字字段**当 tiebreak。原因: buildResourceFields 展开顺序是
    //               {...extendedProps, ...ui, ...resourceDef} —— FC resourceDef 的 `id`
    //               在最后, 会把 extendedProps 里的数字 `id` **覆盖成 'r_19' 字符串**,
    //               直接写 'sort_order,id' 的话 tiebreak 又退回字典序, 同 sort_order
    //               的两个教室又会错 (跨校区看全部教室时 sort_order 会重复)。
    //               roomId 是自己加的数字字段, 谁也覆盖不掉它。
    // ⚠️ 不加 roomId 时 JS sort 稳定 + 输入数组本来就是后端序, 也不会错;
    //    显式补第二键是跟后端 orderBy 逐字对齐, 不依赖"排序稳定"这个隐含前提。
    const resources = this.rooms.map(r => ({
      id: 'r_' + r.id,
      // ⚠️ 格式不变: SlotReservationRenderer / _findColByRoomId /
      title: r.name,
      //    eventDrop 的 replace(/^r_/,'')、data-resource-id 选择器全靠它
      eventColor: r.color || '#2D7BE5',
      roomId: r.id,
      // v0.14.2 新增: resourceOrder 的数字 tiebreak, 见上
      extendedProps: r //    sort_order 从这里被 buildResourceFields 摊平出来
    }));

    // v0.17.57 — 用课表节点算 slot 范围 (见 slotRangeFromSCD)
    // v0.15.4 — 网格轴改成**按当天**营业时间 (见 axisRangeForDate 注释)。
    //   主路径: 每次切日期都会重建 FC (tryMountCalendar 先 destroy 再 new), 所以改构造参数就生效,
    //   根本不需要 setOption。datesSet 那条兜底路径见 _syncAxisToDate。
    // ⚠️ v0.18.72 — 改走 activeDateObject() (与 activeDateStr() 同一个出口)。
    //   改前这里传的是 `this.currentDate` ⇒ **固定课模式下网格日期与数据日期分家**
    //   (数据 = 本周选中星期几那天 / 网格 = 今天), 见 activeDateObject() 注释。
    //   ⚠️ axisRangeForDate() 当前**忽略入参** (恒返 slotRangeFromSCD()),
    //     所以这一处改法**零行为变化** —— 改它只是为了让「网格日期」这两个出口同源。
    const range = this.axisRangeForDate(this.activeDateObject());
    const slotDuration = this.slotDurationFromSCD();
    const calendar = new FC.Calendar(el, {
      plugins: calendarPlugins,
      schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
      initialView: this.calendarView,
      // ⚠️ v0.18.72 — 改前是 `this.currentDate` (永远是今天) ⇒ 固定课模式选「星期几」
      //   之后, 网格停在今天, 选周三时周六当天的课反而看不见 (见 activeDateObject 注释)。
      //   正常模式 = localDateStr(currentDate) 反解 ⇒ 与改前**同一天**, 行为零变化。
      initialDate: this.activeDateObject(),
      slotMinTime: range.min,
      slotMaxTime: range.max,
      // v0.6.65 — height 'auto' 让 FC 完全展开所有 slot (辉哥 10/1 9:11 反馈 mobile 看不到超出部分)
      //   旧 v0.6.46 硬编码 height: 700, FC 内部滚动条 + 内容截断 (营业 13h × 2 slot × 64px = 1664px > 700)
      //   expandRows: true 已设, 但被 fixed height 限制, 改 'auto' 才生效
      //   浏览器页面自然滚 (FC 内部不再额外滚动), 简单可靠
      height: 'auto',
      // v0.7.8p — 教室列横向滚动 (辉哥 10/5 拍板 B 方案, 官方 fullcalendar.io/demos#resource-columns 方案)
      //   官方 demo 只靠这一个 option 拿到"时间列常驻 + 教室列横向滑动", 零自定义 CSS/JS。
      //   反编译 vendor resource-timegrid.global.min.js 确认二选一分支:
      //     const { dayMinWidth } = options;
      //     return dayMinWidth ? renderHScrollLayout(...) : renderSimpleLayout(...)
      //   无 dayMinWidth → SimpleScrollGrid: 所有列 squeeze 填满, 结构上不可能滚动。
      //   有 dayMinWidth → ScrollGridImpl (scrollgrid plugin 提供): axis 独立列 + 资源列 minWidth, 超宽滚动。
      //   线上 puppeteer 实测 (8 教室 @900px 容器): 列宽 200px×8, scroller 1600/856 可横向滚动, 零报错。
      //   注意: 必须与 @fullcalendar/scrollgrid plugin 同时存在, 否则抛 'No ScrollGrid implementation'。
      dayMinWidth: 200,
      nowIndicator: true,
      headerToolbar: false,
      allDaySlot: false,
      slotDuration: slotDuration,
      // v0.8.9 — 短课块阈值 (辉哥 10/7 12:25 拍板: 30min 短课不能溢出/截断)
      //   FC v6 自带 .fc-timegrid-event-short class, 由 isShort 决定, 判定式 (vendor 实测):
      //     isShort = segPixelHeight < eventShortHeight
      //   eventShortHeight 默认 30px —— 而本地 SCD 是 60min/slot @ 100px,
      //   30min 课 = 50px > 30px 默认阈值 → **默认配置下 FC 根本不会打这个 class**,
      //   靠默认值防不住 v0.6.46 踩过的 "矮块装不下 4 行" 坑。所以这里显式抬到 60px。
      //   60px 的来历: 事件块 4 行文字 12/11/10/10px + line-height 1.25/1.2/1.2/1.2
      //   = 52.2px, 加 .fc-event padding 上下 4px = 60.2px。低于 60px 就必然装不下 4 行,
      //   LESS v0.8.9 段据此切紧凑档 (缩字号 + 隐第 4 行备注)。
      //   1h 块实测 99px > 60px → 不打 class → 完整显示 4 行。
      //   比在 eventDidMount 里自己量 offsetHeight 更可靠: eventDidMount 在 FC 布局 pass
      //   **之前**触发, 此时元素还没有 inline 定位尺寸, 量出来是错的
      //   (实测 99px 的正常块被误判成 <40px, 第 3/4 行被隐藏 —— 这是 v0.8.9 返工的根因)。
      //   FC 的 class 在渲染阶段就算好了, 没有这个时序问题。
      eventShortHeight: 60,
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      // v0.7.8q — 时间标签显示"开始-结束" (辉哥 10/5 00:31 反馈)
      //   FC 默认只渲染 slot 开始时间 (09:00), 辉哥要看区间。
      //
      //   ┌ 五轮踩坑记录 (为什么最终用 slotLabelContent 而不是加宽 axis) ──────────┐
      //   │ hScroll + ScrollGrid 的 shrink 机制: axis 列宽由                        │
      //   │   computeShrinkWidth = cushion宽 - frame宽 算出, 每帧 handleSizing 重算。 │
      //   │ 1. CSS .fc-timegrid-axis{width}     → 被覆盖回 5px                        │
      //   │ 2. CSS 只设内层 table{width}         → 外层 chunk 仍 5px, 溢出裁切        │
      //   │ 3. JS setProperty 改 td width        → 抛 "needs fc-scrollgrid-shrink-frame"│
      //   │ 4. FC option slotMinWidth            → timegrid 里根本没引用, 死 option   │
      //   │ 5. 加不可见撑手 span 78px            → 桌面端 shrink 仍算到 5px            │
      //   │ 结论: axis 宽度不可控, 唯一能稳的是让文字本身适配 41px。                  │
      //   └──────────────────────────────────────────────────────────────────────────┘
      //
      //   slotLabelContent 是 FC core 官方 option (只读渲染, 不参与任何宽度计算),
      //   输出紧凑区间 "09-10" / "09:30-10" (5-6 字符), 41px 内一行放下, 零副作用。
      //   整点用 "09-10", 半点用 "09:30-10" 保持信息完整。
      slotLabelContent: arg => {
        const start = arg.date;
        if (!(start instanceof Date)) {
          return {
            html: arg.text
          };
        }
        // ── v0.17.57 课表态: 左列 = 课表节点顺序清单 ─────────────────────────
        //   这一行就是「左列从真实时间刻度换成时间段清单」的全部实现, 其余全是渲染机制:
        //   · arg.date 是 FC 给的**名义** slot 起点 (第 N 行 = N×基准, 见本文件顶部注释);
        //   · 反算出它是第几行 → 就是第几个时间段 → 打印该段的**真实**区间;
        //   · 取不到行号 (越界) → 返回空串, 那是个不该出现的空行。
        //   为什么只在这一处换: 真实刻度 '09:00 / 10:10 / 11:20' 是 slotDuration 的产物,
        //   去掉之后左列就只剩标签一个来源 —— 不必动 axis 宽度、不必动背景、不必劫持 DOM。
        if (self.hasSchedule()) {
          const row = self.scheduleRowIndexOfDate(start);
          if (row == null) return {
            html: ''
          };
          const node = self.scheduleNodes[row];
          return {
            html: String(node.start_time).slice(0, 5) + ' – ' + String(node.end_time).slice(0, 5),
            classNames: ['ZS-slot-range-label']
          };
        }
        // ── 兜底态 (本校区没有可用课表): 原样保留 v0.7.8q 的真实刻度两行区间 ──
        const durParts = this.slotDurationFromSCD().split(':');
        const durMs = (Number(durParts[0]) * 60 + Number(durParts[1])) * 60 * 1000;
        const end = new Date(start.getTime() + durMs);
        const p2 = n => String(n).padStart(2, '0');
        const sh = p2(start.getHours());
        const sm = p2(start.getMinutes());
        const eh = p2(end.getHours());
        const em = p2(end.getMinutes());
        // v0.7.8q — 两行显示完整区间 (辉哥 10/5 00:58 拍板: "要完整 09:00-10:00 格式,
        //   如果一行显示不下, 可以分成两行展示")
        //
        //   关键洞察: 拆成两行后每行只有 5 个字符 ("09:00" / "10:00"),
        //   宽度需求跟单行的 "09:00" 完全一样 (37px 足够), 所以**不需要加宽 axis**,
        //   绕开了 ScrollGrid shrink 机制这个死结 (见上方五轮踩坑记录)。
        //   slot 行高 100px, 两行 11px 字体 (line-height 1.35) 占用约 30px, 绰绰有余。
        const startText = sh + ':' + sm;
        const endText = eh + ':' + em;
        return {
          html: startText + '<br>' + endText,
          classNames: ['ZS-slot-range-label']
        };
      },
      expandRows: true,
      resources: resources.length ? resources : [{
        id: 'r_default',
        title: '默认教室'
      }],
      // v0.14.2 — 教室列顺序 = 教室 tab 卡片顺序 (辉哥 10/9 需求)
      //   不写这一行时 FC 用 DEFAULT_RESOURCE_ORDER = parseFieldSpecs('id,title'),
      //   即按 `'r_'+id` 字典序排 → id 不连续时 (生产 #19) 顺序跟教室 tab 对不上。
      //   完整根因与字段选择理由见上面 resources.map 的注释块。
      resourceOrder: 'sort_order,roomId',
      // v0.15.0 — **两条独立 event source** (迁移前这里是单个课块数组)
      //
      //   为什么是两条: 见文件顶部 ZS_RSV_PREFIX 的注释。⚠️ 顶部那段 v0.15.1 已订正过 ——
      //   per-event 写法在 6.1.21 **也生效**, 「必须拆 source」这个理由不成立。
      //   真正的原因是 source 级写法**显式**: 课块那条上写死的 `durationEditable: false`
      //   是唯一挡住全局 eventDurationEditable 泄漏的防线, 位置贴着课块、不易被顺手删掉。
      //   **若日后改成单 source + per-event, 必须先确认课块也显式写了 false。**
      //
      //   ⚠️⚠️ 为什么是 `eventSources` 而不是 `events` (实测踩坑, 别改回去):
      //   vendor `index.global.min.js` 的初始事件源构建函数 Go() 是这么写的:
      //     let r = [].concat(e.eventSources||[]);
      //     e.initialEvents && r.unshift(e.initialEvents);
      //     e.events        && r.unshift(e.events);      // ← 整个数组当成**一个** source
      //     for (let e of r) { let r = So(e, t, n); ... }  // So = parseEventSource
      //   `events` 走的是 **unshift 整个数组**, 不是展开 —— 于是
      //     events: [ {events:[课块], durationEditable:false}, {events:[预留], durationEditable:true} ]
      //   会被 So() 当成**一个** source, 且因为它是数组 → r = {events: 该数组},
      //   即「把这两个 source 对象本身当成两条 event 定义」—— 它们没有 start 字段, 被 FC 全部丢弃。
      //   实测: `events: 纯课块数组` → 渲染 1 个块; `events: [{events:[同一条]}]` → 渲染 **0** 个块。
      //   `eventSources` 走的是 `[].concat(...)`, 会展开一层 → 每条独立解析 → 正常。
      //   （FC 官方多数据源 API 本来也是 eventSources / addEventSource。）
      //
      //   ⚠️ 课块那条的 `durationEditable:false` 是**唯一**挡住全局 eventDurationEditable 的防线,
      //   别删也别"顺手简化"。实测 E 组: 全局打开且不限定 → 课块立刻长出 2 个手柄,
      //   而课块"只能移动、不能改时长"是辉哥明确要的行为。
      //   预留块那条 durationEditable:true + startEditable:true —— 可拖可改上下边界。
      //   (base refiner `Ke` 里确有 editable/startEditable/durationEditable,
      //    且 event 级 refiner `ct()` 与 source 级 `To()` 共用同一个 Ke —— 两级都生效。)
      eventSources: [{
        events: lessonEvents,
        durationEditable: false,
        // v0.17.76 — 学员只读: 课块不可拖。
        //   ⚠️⚠️ **光把全局 editable 设成 false 挡不住这里**, 必须改 source 级:
        //     vendor `index.global.min.js` 的 tt() 把每个 event/source 的 ui 算成
        //     `startEditable: e.startEditable ?? e.editable`, 合并时 `rt()` 取
        //     「后者非 null 就用后者」—— 本 source 显式写了 startEditable,
        //     它会**盖掉**全局 editable:false。这是本轮实测确认过的真实行为,
        //     不是预防性写法。(三条都写是同一层防御, 删任何一条都会开一个洞。)
        startEditable: !this.isStudentView()
      }, {
        events: this.buildReservationEventDefs(),
        // v0.17.76 — 预留块对学员同样只读 (学员视图里不能有任何拖拽操作)。
        //   ⚠️ 同上: 这条 source 原本显式 durationEditable:true + startEditable:true,
        //     正好是「全局 editable:false 盖不住」的那一类, 必须在这里关。
        durationEditable: !this.isStudentView(),
        startEditable: !this.isStudentView()
      }],
      // v0.5.0f B 方案修复: FC v6 不自动把 resource.classNames 应用到 TH,手动加
      resourceLabelDidMount: info => {
        const r = info.resource;
        const cls = roomHeaderClass[String(r.id).replace(/^r_/, '')] || 'zs-room-blue';
        info.el.classList.add(cls);
      },
      // v0.6.8 — 给每个时间格加 data-resource-id 属性, 槽位解析直接读
      //   ⚠️ v0.8.12: 双击/2-tap 已删, 但 _resolveSlotTarget 仍在读这个属性 (右键 + 长按菜单靠它
      //     定位教室列), **不要**因为"双击没了"就清掉 slotLabelDidMount 里的赋值。
      //   FC v6 resourceTimeGridDay: .fc-timegrid-col (列, 含 data-resource-id) 跟 .fc-timegrid-slots (slot 层) 平行
      //   slot lane 本身没有 data-resource-id, 通过 slotLabelDidMount 给其父 tr 加
      slotLabelDidMount: info => {
        // info.el 是 .fc-timegrid-slot-label-cushion, 父是 .fc-timegrid-slot-label
        // 祖父 .fc-timegrid-slot-lane 在同 tr 内, tr 的 cellIndex 给出列位置
        // 但更可靠: 直接读 info.isPast / info.date / info.view, 用这些计算列
        // 简化: 拿 info.el 的父 tr, 计算 cellIndex
        const cell = info.el;
        const tr = cell.closest('tr');
        if (!tr) return;
        const cellIndex = cell.cellIndex;
        // 找同一索引的 .fc-timegrid-col
        const colCells = document.querySelectorAll('.fc-timegrid-col[data-resource-id]');
        if (cellIndex < colCells.length) {
          const resourceId = colCells[cellIndex].getAttribute('data-resource-id');
          if (resourceId) {
            // 给 slot lane 加 data-resource-id (供 _resolveSlotTarget 读 → 右键/长按菜单定位教室列)
            const lane = tr.querySelector('.fc-timegrid-slot-lane');
            if (lane && !lane.hasAttribute('data-resource-id')) {
              lane.setAttribute('data-resource-id', resourceId);
            }
          }
        }
      },
      // v0.6.8a — V 测 FAIL fix: 把 lesson.id 挂到 .fc-event DOM dataset,
      //   槽位解析直接读 data-lesson-id, 不再用 .fc-event-title textContent 反查
      //   ⚠️ v0.8.12: 双击/2-tap 已删, 但 _resolveSlotTarget + openBooking 仍在读 data-lesson-id
      //     (右键/长按菜单定位已约课), **不要**清掉 eventDidMount 里的赋值。
      //   (v0.6.8 用 title 反查 + slot top 匹配 错位, fc-event 9:00-10:00 命中 10:00 而不是 9:00,
      //   dataTime 错位, lesson 找不到, mode 退回 'create' → title="约课" 错)
      eventDidMount: info => {
        // ── v0.15.0: 预留块事件 (走 FC 自己的 DOM, 不再自绘) ───────────────
        //   它跟课块共用同一个 eventDidMount, 所以这里先按 id 前缀分流。
        //   预留块只需要两件事: ① 挂 data-rsv-id (右键菜单 _resolveSlotTarget 靠它反查)
        //   ② 记住 pointerdown 落在哪个手柄上 (方向锁定徽标要用)。
        //   刻意**不**套课块那套 (cursor:move inline style / buildLessonLines tooltip):
        //   课块的 tooltip 内容对预留毫无意义, inline cursor 也会盖掉 LESS 里 grab/grabbing。
        if (String(info.event.id || '').indexOf(ZS_RSV_PREFIX) === 0) {
          const rsv = info.event.extendedProps || {};
          info.el.setAttribute('data-rsv-id', String(rsv.id));
          info.el.setAttribute('title', rsv.notes ? '已预留 · ' + rsv.notes : '已预留');
          // 方向锁定徽标要知道用户抓的是上边还是下边手柄。
          //   为什么用 pointerdown 而不是 eventResizeStart: vendor 的 eventResizeStart 载荷只有
          //   {el, event, jsEvent, view} —— el 是**整段 harness**, 没有 startDelta/endDelta,
          //   也没有「按的是哪个 resizer」。判定点只在 interaction 内部:
          //     subjectEl.classList.contains("fc-event-resizer-start")
          //   那个 subjectEl 我们拿不到, 但 pointerdown 的 e.target 正是它 —— 这是 DOM 层的等价物。
          info.el.addEventListener('pointerdown', e => {
            if (e.pointerType === 'touch') {
              self._rsvResizeEdge = null;
              return;
            }
            if (e.target && e.target.classList && e.target.classList.contains('fc-event-resizer-start')) {
              self._rsvResizeEdge = 'start';
            } else if (e.target && e.target.classList && e.target.classList.contains('fc-event-resizer-end')) {
              self._rsvResizeEdge = 'end';
            } else {
              self._rsvResizeEdge = null;
            }
          });
          return; // 课块那套一律不跑
        }
        info.el.setAttribute('data-lesson-id', String(info.event.id));
        // v0.7.9e — 事件块自带 data-resource-id
        //   实测踩坑: slotLabelDidMount 用 cellIndex 把 axis 格的列号映射到 .fc-timegrid-col,
        //   这套映射在 v0.7.8q 引入 @fullcalendar/scrollgrid (dayMinWidth) 之后**全部失效**,
        //   实测 28 个 .fc-timegrid-slot-lane 里 0 个带 data-resource-id。
        //   后果: 右键/长按打已约课块时 _resolveSlotTarget 找不到对应 lane,
        //   hit.resourceId 为 null (教室名靠 lesson.room_id 兜底才没露馅),
        //   但「修改约课」时教室预选会丢。
        //   修法: 事件块直接从自己所在列读 resourceId, 不依赖 lane 上的属性。
        const col = info.el.closest ? info.el.closest('.fc-timegrid-col[data-resource-id]') : null;
        if (col) info.el.setAttribute('data-resource-id', col.getAttribute('data-resource-id'));
        // v0.6.43 — 提示可拖拽 (辉哥 9/30 9:00+ 反馈)
        //   改前 fc-event 没 cursor 提示, 用户不知道能拖; 改后 inline style cursor:move 立刻感知
        //   不动 LESS (SOP 103 红线), inline style 兜底; FC event 视觉 (颜色/边框) 也保持不变
        info.el.style.cursor = 'move';
        // v0.8.9 — hover tooltip (原生 title 属性)。
        //   改前: events.map 里给了 title 字段, 但 FC v6 **不会**把它写进 DOM
        //     (实测 titleOnEvent / titleOnMain / titleOnA 全 null, 元素也不是 <a>)。
        //     原生 tooltip 在 v6 已移到 @fullcalendar/tooltip 插件, 本扩展没挂该插件,
        //     所以 v0.6.45 注释里"title 保留作 hover tooltip"其实一直没生效。
        //     本次顺手补上: 直接写 DOM title 属性, 无插件依赖, 桌面 hover / 移动长按都可见。
        //   内容走 buildLessonLines → 与事件块 4 行同源, 加了余额/卡名自动跟上, 不会漂移。
        //   原生 title 只支持纯文本 (塞不进头像 DOM), 文字行与事件块完全一致。
        const tip = self.buildLessonLines(info.event.extendedProps || {});
        info.el.setAttribute('title', [tip.line1, tip.line2, tip.line3, tip.line4].filter(Boolean).join('\n'));
      },
      // v0.6.8a — eventClick 也走 openBooking 路径 (V 测建议: 复用 eventClick)
      //   单击 fc-event → mode='edit' 弹 modal, 跟 dblclick 同体验
      // v0.7.9e (辉哥 10/5 拍板) — **移除单击响应**:
      //   新交互下菜单是主入口 (桌面右键 / 移动长按), 单击必须静默, 否则:
      //     ① 移动端长按会先触发 eventClick 弹 modal, 菜单永远出不来
      //     ② 移动端横滑滚动时误触发
      //   改约课/查看已约课统一走: 双击 (保留) / 右键菜单 (桌面) / 长按菜单 (移动)。
      //   ⚠️ v0.8.12 (辉哥 10/7 16:22 拍板) — 上句里的"双击 (保留)"**已作废**, 双击 + 2-tap 都删了。
      //     现在唯一入口: 右键菜单 (桌面) / 长按菜单 (移动)。
      //   完整删除 handler 而非注释 — 留着空函数反而让下一个人以为还有行为。
      // eventClick: 已于 v0.7.9e 移除, 见上方说明
      // v0.6.43 — FullCalendar 课程拖拽移动 (辉哥 9/30 9:00+ 反馈)
      //   拖拽结束 → self.handleEventDrop(info) → Api.update PATCH start_at + end_at + room_id
      //   → 后端 LessonConflictChecker 校验 (UpdateLessonController L58), 422 冲突 → info.revert() 回滚
      //   成功 → 静默同步 this.lessons 本地数据 + flashSuccess 提示
      // editable: 全局开关 (FC v6 eventStartEditable 默认 true, 这里显式设避免 FC 升级行为变化)
      // eventStartEditable: true 允许拖到新时间 (snap 到 slotDuration 30 分钟, 跟 SCD 一致)
      // eventDurationEditable: false 不允许 resize 改时长 (辉哥只要"移动", 不让改时长)
      // v0.17.76 (辉哥 10/16「fc 课表里不能进行任何拖拽操作」) — 学员三项全关。
      //   ⚠️ 这三个全局开关**单独关不够**: 两条 event source 上都显式写了 startEditable,
      //     按 vendor 的 tt()/rt() 合并规则, source 级的非 null 值会盖掉全局 editable
      //     (见上方 eventSources 的注释)。所以「全局 + source」两层一起关, 缺一层就漏。
      editable: !this.isStudentView(),
      eventStartEditable: !this.isStudentView(),
      eventDurationEditable: false,
      // v0.15.0 — 上边手柄的总开关 (辉哥 10/10 拍板: 四角 → 只往上扩 / 只往下扩)
      //
      //   vendor `index.global.min.js` 实测, timegrid 只有两个手柄, 判定式逐字是:
      //     isStart 手柄: e.isStart && e.eventRange.ui.durationEditable && options.eventResizableFromStart
      //     isEnd   手柄: e.isEnd   && e.eventRange.ui.durationEditable
      //   → **eventResizableFromStart 不设就是 falsy, 上边手柄根本不渲染**, 只有下边手柄。
      //     它在 optionRefiners 里注册为 `eventResizableFromStart: Boolean`, 即**全局 option**,
      //     没有 per-event 版本 —— 所以必须显式设 true。
      //   → 但它只对 `ui.durationEditable` 为真的事件生效; 课块那条 source 是 false, 不受影响。
      //     换句话说: 这是一个全局旋钮, 但被 source 级的 durationEditable 精确圈在预留块上。
      //
      //   ⚠️ FC 升级注意: 「可从起始边 resize」这个能力在 vendor 里是**私有实现细节**。
      //     日后升 FC 大版本时, 必须回归验证预留块上下两个手柄都还在 (方法: hover 预留块数
      //     `.fc-event-resizer` 节点, 应当正好 2 个, 见 CHANGELOG v0.15.0 升级注意段)。
      eventResizableFromStart: !this.isStudentView(),
      // ─── v0.10.3 (辉哥生产报障「移动端已约课块不能拖拽移动位置」) ───────────
      //
      // 【报障症状】辉哥用「好像」, 上一轮 V 测也把「移动端拖拽」列为未能验证。
      //   本轮 CDP 真机实测 (iPhone 390x844 + Input.dispatchTouchEvent) 的真实形态:
      //     按住 <1000ms 再移动 → **页面先滚动 75px, 课块纹丝不动** (不是"完全没反应")
      //     按住 >=1000ms 再移动 → 拖拽正常, eventDrop 触发, DB 落库
      //   即用户看到的是"一按就滑走", 主观描述就是"拖不动"。
      //
      // 【真根因 —— 两段 vendor 源码, 不是猜的】
      //   ① FC 长按阈值默认 1000ms:
      //      interaction.global.min.js 的 EventDragging.handlePointerDown:
      //        s.minDistance = t.isTouch ? 0 : o.eventDragMinDistance
      //        s.delay = isTouch ? (eventLongPressDelay ?? longPressDelay) : null
      //      index.global.min.js 的 BASE_OPTION_DEFAULTS: longPressDelay: 1e3
      //      → **代码里从没配过 eventLongPressDelay**, 移动端吃的就是 1000ms 默认值。
      //   ② 阈值内一旦发生滚动, **整次手势的拖拽被永久判死**:
      //      EventDragging 构造里 `s.touchScrollAllowed = !1`, 而 ElementDragging:
      //        tryStartDrag(){ this.isDelayEnded && this.isDistanceSurpassed &&
      //          (this.pointer.wasTouchScroll && !this.touchScrollAllowed || (...真正开始拖...)) }
      //      → 只要 1000ms 内发生过任何滚动 (wasTouchScroll=true), 就算手指后来停稳了,
      //        拖拽也起不来。这是"按住 500/800ms → 页面滑动 → 课块不动"的完整链路。
      //      本项目 height:'auto' (L921) → FC 自身不滚, 滚动的是**整个页面**,
      //      所以移动端课表必须靠页面滚动浏览 —— 这个滚动几乎必然发生。
      //
      // 【为什么选 eventLongPressDelay 这一个官方 option】
      //   它是 interaction plugin 的**正式配置项**, 直接决定 `s.delay`, 不碰任何事件流。
      //   符合辉哥 10/7 拍板的「优先框架原生扩展点」原则:
      //     · 不改 vendor、不 monkey patch FC 内部、不劫持 pointer/touch 事件
      //     · 不重复 v0.8.11 已被否决的「劫持 pointer 事件」方案 (拖不过去 + 松手回弹)
      //     · 桌面端完全不受影响 (delay 只在 isTouch 时才被读取)
      //   ⚠️ 桌面端不要动: `t.isTouch && ...` 决定了鼠标拖拽根本不读这个值。
      //
      // 【400ms 的取值依据 —— 实测, 不是拍脑袋】
      //   实测 (每档 1 次长按拖拽, CDP 真触摸):
      //     500ms / 800ms 按住 → 页面滚动 7 次, 课块未移动, DB 未变 (拖拽没起来)
      //     950ms              → 拖拽成功 (边界, 刚好压过 1000ms 定时器的实际触发点)
      //     1000ms / 1200ms    → 拖拽成功
      //   选 400ms 而不是更小:
      //     · 400ms 仍是"按住不动"的语义, 跟人手指自然抖动 (<15px 实测不触发滚动) 不冲突
      //     · 实测 P1-P4: 按住期间 2px/5px/8px/15px 的轻微抖动**不会**弄死拖拽,
      //       只有 40px 级别的真滚动位移才会 (P5 失败) —— 400ms 窗口留得够用
      //     · 不能再短: 与 bindBookingTriggers 的移动端双击 (300ms 窗口, L1653) 会打架 ——
      //       双击的第二次 touchstart 若已过 400ms, FC 会先把课块拖起来, 菜单永远出不来。
      //       400 > 300 保证「双击出菜单」优先, 且 400ms 内按住不动也不会误触发拖拽。
      //
      // ─── v0.12.1 (辉哥 10/9 拍板) — **有意回收移动端课块拖拽** ────────────
      //
      // 【决定】辉哥原话「课表日历tab中的fc课表, 移动端中的『课块』与『预留块』
      //   不再支持拖拽移动以及调整预留块范围 / 嗯 是关掉移动端拖拽和resize」。
      //   即 **只关移动端, 桌面端全部保留**。
      //
      // 【为什么 400 → 本值, 而不是换别的开关】
      //   ① vendor 实测: EventDragging.handlePointerDown 里
      //        s.delay = t.isTouch && <不是 eventSelection>
      //                 ? (eventLongPressDelay ?? longPressDelay)
      //                 : null
      //      而 FC 的 `isTouch` **不是设备能力判断**, 是**事件来源**判断 ——
      //      createEventFromTouch 造出来的 isTouch:true, createEventFromMouse 恒 false。
      //      → 鼠标路径 delay 直接是 null, 这个 option 在桌面上**根本不会被读到**。
      //   ② ElementDragging.startDelay:
      //        typeof delay === 'number' ? setTimeout(...) : handleDelayEnd()
      //      桌面 delay=null 走 else 分支 → 立刻 isDelayEnded=true → **零延迟直接拖**。
      //      桌面上把 400 改成多大都不可能影响它 (不是"影响很小", 是读都不读)。
      //   ③ 为什么不用 eventStartEditable 的函数形式按设备判断:
      //      index.global.min.js 的 optionRefiners 里写死了 `eventStartEditable:Boolean`,
      //      函数形式不是合法输入 —— 这条路本来就不通 (已 grep vendor 确认, 不是猜的)。
      //   ④ 不选 eventDurationEditable / eventOverlap / editable 全局关:
      //      那些会连桌面端一起关掉, 直接违反「桌面端全部保留」。
      //   → 结论: eventLongPressDelay 是**唯一**既官方、又能只命中触屏的那个旋钮,
      //      符合辉哥 10/7 拍板的「优先框架原生扩展点, 不劫持框架内部」。
      //
      // 【为什么是 999999999 而不是"关掉"】
      //   FC 没有 "disableTouchDrag" 这种开关, 唯一能做到的就是把延迟推到不可达。
      //   tryStartDrag 要求 `isDelayEnded && isDistanceSurpassed`, 触屏下
      //   isDelayEnded 只在 setTimeout 回调里置 true → 定时器不响 → 拖拽**永远起不来**。
      //   ⚠️ 取值必须 < 2147483647 (setTimeout 的 32 位上限)。个别实现把超限的
      //     delay 当成 0/1ms 立即触发 —— 那会让拖拽**照常起来**, 等于没关。
      //     999999999 ≈ 11.6 天, 既远低于上限, 又远大于任何一次真实手势。
      //   ⚠️ 桌面端 cursor 仍显示 move (L1061 那个 inline style) —— 那是既有行为,
      //     桌面本来就该能拖; 移动端没有 hover, 不受影响, 故本次不动。
      //
      // 【副作用 —— 顺手解决了 v0.10.3 留下的一个包袱】
      //   v0.10.3 上面那段注释里, 400ms 的取值有一半理由是"要跟 bindBookingTriggers
      //   的移动端双击 300ms 窗口抢时间, 400 > 300 保证双击优先"。现在触屏拖拽**整个不存在**,
      //   这条约束自动消失, 双击菜单不再有任何竞争压力。
      //   触屏下的**页面滚动也不受影响**: ElementDragging 只在 `!isTouch` 时才
      //   preventDefault (vendor: `t.isTouch || t.origEvent.preventDefault()`),
      //   拖拽压根没起来 → FC 不 cancelTouchScroll → 浏览器照常滚。
      eventLongPressDelay: 999999999,
      // v0.8.11 (辉哥 10/7 14:44 拍板) — 约课卡拖拽遇到预留块必须停住
      //   根因: 预留块**不是 FC event**, 是 SlotReservationRenderer._renderOne 里
      //   document.createElement('div') 造的绝对定位块, 挂在
      //   .fc-timegrid-col[data-resource-id] > .fc-timegrid-col-frame 下。
      //   FC 自带的 eventOverlap 引擎只认识 eventStore 里的 event, 看不见这个块
      //   → 拖拽时 FC 眼里那格是空的, 直接放行, 约课卡能落在预留上。
      //   所以必须用 eventAllow 手动查预留数据。
      //   ⚠️ dropInfo 形状 —— **两条分支都不可删** (v0.8.11b 更正, 原注释判断方向写反了)
      //   V 测实测 (真实拖拽时 eventAllow 收到的 dropInfo 键):
      //     ["resource", "start", "end", "startStr", "endStr", "allDay"]
      //   **只有 resource, 没有 resourceId** → 主形态是 dropInfo.resource.id (v5 形状)。
      //   逆向勘察 6.1.21 minified 结论一致: xr() 只取 range/allDay, 不 spread 整个 props;
      //   resource 插件注册的 dateSpanTransforms 产出 { resource: ResourceApi }。
      //   ⚠️⚠️ _roomIdFromDropInfo 里 dropInfo.resourceId 与 dropInfo.resource.id
      //   两条分支都不可删: 删任一条 → 当前 FC 下解析恒为 undefined → 返回 null →
      //   canDropOverReservations() 一律 return true 放行 → **拖拽阻塞功能静默失效且不报任何错**。
      //   (v0.8.11 原注释把主形态写成 resourceId, 照它删 resource.id 分支就会踩这个雷)
      //   实测另一条: eventAllow 返回 false 时 **eventDrop 根本不触发** (FC 直接判非法弹回)
      //   → 第 1 层是主防线; handleEventDrop 里的 overlapsReservation 兜底只是冗余保险,
      //   正常路径走不到, 它的价值是"哪天 eventAllow 空转了也不落脏数据"。
      eventAllow: (dropInfo, draggedEvent) => {
        // v0.17.76 — 学员只读: 任何落点一律判非法。
        //   正常路径下 editable:false + source 级 startEditable:false 已经让手势起不来,
        //   eventAllow 根本不会被调 —— 这道是**冗余防线**, 挡的是「哪天 FC 行为变了 /
        //   source 级被谁顺手改回 true」这两种回归。留着的代价是零 (一个布尔早退)。
        if (self.isStudentView()) return false;
        // v0.15.0 — 预留块也是 FC event 了, 所以这里有两条分支:
        //   · 拖的是**预留块** → 判「新范围撞不撞课 / 撞不撞别的预留 + 有没有跨天」
        //   · 拖的是**课块**   → 沿用 v0.8.11 的老逻辑 (不能落在预留上)
        //   ⚠️ 分流必须靠 event id 前缀, 不能靠 classNames —— 见 ZS_RSV_PREFIX 注释。
        if (self.isReservationEvent(draggedEvent)) {
          return self.canPlaceReservation(dropInfo, draggedEvent);
        }
        return self.canDropOverReservations(dropInfo, draggedEvent);
      },
      // v0.8.11 — 拖拽高亮 (第 3 层) **已按 brief 授权降级为不做**
      //   原计划用 eventDragOver 给拖过的预留块加高亮。两条实测结论:
      //     ① FC 6.1.21 **没有 eventDragOver 这个 hook** (grep 全部 vendor bundle 0 命中;
      //        有的是 v5 时代的名字, v6 只保留 eventDragStart / eventDragStop)。
      //     ② 改用 eventDragStart + document pointermove + 矩形相交判定的替代方案同样不可靠:
      //        实测拖拽过程中 `.fc-event-mirror` 多数时候**不存在**; 而 interaction 插件建的
      //        `.fc-event-dragging` 是 ElementDragging 的原始克隆, 它跟随指针但在实测里始终停在
      //        源元素矩形 (t:-42 b:57 l:643 r:1101) 整个拖拽过程不变 —— 也就是说拿不到"FC 吸附后
      //        的落点矩形", 拿到的矩形跟真实落点无关, 高亮会指错块。
      //   宁可不画, 也不画一个会骗人的高亮。核心"不能落在预留上"由上面 eventAllow +
      //   handleEventDrop 兜底保证, 已实测通过 (松手弹回 + 不发 PATCH + DB 不变)。
      //   (v0.8.11b 实测补注: eventAllow 返回 false 时 eventDrop 根本不触发, 所以真正拦住的是
      //    eventAllow 这一层, handleEventDrop 那道在正常路径走不到, 是冗余保险不是并列防线。)
      //   若日后要做, 可靠做法是用指针坐标 + FC lane 几何换算落点 slot, 而不是抄镜像矩形。
      eventDrop: info => {
        // v0.17.76 — 学员只读: 落库前最后一道闸。revert() 把事件放回原处,
        //   然后直接 return, 绝不碰 handleEventDrop / handleReservationDrop (不发 PATCH/PUT)。
        if (self.isStudentView()) {
          info.revert();
          return;
        }
        // v0.15.0 — 预留块拖拽落到这里; 课块走原来的 handleEventDrop。
        if (self.isReservationEvent(info.event)) {
          self.handleReservationDrop(info);
          return;
        }
        self.handleEventDrop(info);
      },
      // v0.15.4 — 轴按当天收窄的**兜底**钩子 (主路径已在构造参数里带上了)。
      //   本 tab 只用 resourceTimeGridDay (view switcher 隐藏, 见 202 行), 所以 arg.start
      //   就是**当天**那个日期, 直接拿来算窗口; 退一步用 view.currentStart。
      datesSet: arg => {
        const d = arg && arg.start || arg && arg.view && arg.view.currentStart;
        if (!d) return;
        try {
          self._syncAxisToDate(new Date(d.getTime()));
        } catch (e) {/* noop: 轴同步失败不该拖垮整页 */}
      },
      // ─── 跨教室拖拽的提示去重窗口 ─────────────────────────────────────────
      //   ⚠️ v0.17.57 — 这里原来还清 `self._businessHoursWarned` (营业时间提示的去重闸),
      //   随 businessHoursViolation / _warnBusinessHoursOnce 一起删掉了: business_hours
      //   已整体下线, 那个闸永远不会被置位, 再留着就是一段永远不会执行的死代码。
      //   剩下 _crossRoomWarned 一个闸, 但这四个钩子一个都不能少 (见下)。
      //   eventDragStart / eventDragStop 是 FC v6 保留的两个拖拽钩子
      //   (v0.8.11 注释实测过: v6 没有 eventDragOver, 只有 Start / Stop 这一对)。
      eventDragStart: () => {
        // 新手势开始, 解开闸。必须在这里清 (而不是只在 eventDragStop 清):
        // eventAllow 理论上可能先于 eventDragStart 被调, 只清 Stop 的话那种情况下
        // 闸会一直卡住, 之后再也弹不出提示 —— 那就退化成「静默拖不动」了。
        self._crossRoomWarned = false;
        // ── v0.19.00 — S2「用户正在拖拽」标志 (静默轮询的暂停条件之一) ──
        //   ⛔ 不新增任何 FC 回调, 只在**已有的** eventDragStart / eventDragStop 里置/清。
        //   为什么要暂停: 拖拽中途换事件 ⇒ 会把用户刚拖的结果冲掉。
        self._dragging = true;
      },
      eventDragStop: () => {
        self._crossRoomWarned = false;
        // v0.19.00 — 拖拽结束 ⇒ 解开 S2 (见 eventDragStart 的注释)
        self._dragging = false;
      },
      // ─── v0.15.0 预留块 resize (FC 原生) ─────────────────────────────────
      //   辉哥 10/10 拍板: 撞课**弹回原位**。这条不需要写任何代码 ——
      //   vendor `interaction.global.min.js` 实测, 落点非法时:
      //     isInteractionValid(...) 为假 → validMutation 被清成 null
      //     → handleDragEnd 里 `if (this.validMutation)` 不成立 → 既不 MERGE_EVENTS 也不触发 eventResize
      //     → 事件仓库从没被改过, 松手自然回到原位
      //   即「弹回」是 FC 自己做的, 我们只要保证非法落点**真的**被 eventAllow 判成非法。
      //   (v0.8.4 时代是「ghost 转红 + 按最后合法范围保存」, 那套自绘代码已整体删除。)
      // ─── 去重窗口 = 「一次手势」, 拖拽与 resize 是**两类独立**手势 ──────────
      //   ⚠️ 必须在 eventDragStart/Stop **和** eventResizeStart/Stop 四处各自解闸:
      //   实测 (Playwright + 真实 FC 6.1.21 手势, 2026-10-10 周六预留块) 跨教室提示在
      //   「先拖一次、再 resize 一次」时会静默弹回, 因为闸还卡在 true。
      //   落库兜底救不了: eventAllow 返回 false 时 FC 根本不触发 eventResize
      //   → persistReservationMove 压根不被调用 → 闸永远不会自己解开 (PUT 次数实测 = 0)。
      //   eventAllow 不区分手势类型 (预留块拖拽和 resize 都走 canPlaceReservation)。
      eventResizeStart: info => {
        // 先解闸再走下面的早退: 不管这次 resize 的是什么块, 新手势就是新窗口。
        self._crossRoomWarned = false;
        // v0.19.00 — S2「用户正在拉伸」标志 (与 eventDragStart 同一个标志位)
        //   ⚠️ 拉伸也是「正在操作课块」, 中途换事件同样会冲掉用户的结果 ⇒ 一并暂停轮询。
        self._dragging = true;
        if (!self.isReservationEvent(info.event)) return;
        self.showResizeDirectionFlag(info);
      },
      eventResizeStop: () => {
        self._crossRoomWarned = false;
        self.hideResizeDirectionFlag();
        // v0.19.00 — 拉伸结束 ⇒ 解开 S2 (见 eventDragStart 的注释)
        self._dragging = false;
      },
      eventResize: info => {
        // v0.17.76 — 学员只读: 同样在落库前 revert + return, 不碰 handleReservationResize。
        if (self.isStudentView()) {
          info.revert();
          return;
        }
        if (!self.isReservationEvent(info.event)) {
          // 课块不该走到这里 (它的 source durationEditable:false, 根本不会长出手柄)。
          // 万一走到了 (FC 行为变化) 一律回滚, 保住「课块不可改时长」这条既有行为。
          info.revert();
          return;
        }
        // 移动端二次闸门 (辉哥 v0.12.1 明确关掉的, 绝不能因为这次改造复活):
        //   FC 自己其实已经挡住了 —— `EventResizing.handlePointerDown` 对 touch 会
        //   setIgnoreMove(true), 于是 startDelay() 根本不被调用, isDelayEnded 恒 false,
        //   tryStartDrag 永不成立。但那依赖 `props.eventSelection === null` 这个 FC 内部前提,
        //   属于私有实现细节。这里再按 pointerType 兜一道, 跟 v0.12.1 同一个判据口径。
        if (info.jsEvent && info.jsEvent.pointerType === 'touch') {
          info.revert();
          return;
        }
        self.handleReservationResize(info);
      },
      // v0.6.45 — FC event 自定义 4 行内容 (辉哥 9/30 9:59+ 反馈, 推翻 v0.6.41 "title 简洁" 决策)
      //   第 1 行: 学员名
      //   第 2 行: 教练名
      //   第 3 行: 课程分类 + 课程类型 (中文 label via LessonOptions.labelFor)
      //   第 4 行: 备注 (notes, 超过 20 字 ellipsis)
      //   推翻 v0.6.41 决策 — 4 行丰富展示, 接受 FC cell 紧凑渲染风险
      //   LESS .fc-event-line-1/2/3/4 紧凑样式 (forum.less, 8 行 CSS) 跟 SOP 109 不冲突
      //   title 字段保留为 hover tooltip + 移动端长按 (4 行完整信息, 跟 eventContent 一致)
      // v0.6.45a — return 格式 fix (辉哥 9/30 9:59+ V 测 FAIL root cause, FC v7 docs 验证)
      //   改前 v0.6.45 用了 `[{html: '...'}, {html: '...'}, {html: '...'}, {html: '...'}]` (array of 4 个 {html} 对象)
      //   FC v6 vanilla JS eventContent 只接受 single object { html: '<string>' } 或 { domNodes: [Element] } 或 string 或 Element
      //   不接受 array → fc-event-main 渲染空 → 4 行内容不显示
      //   改后用 domNodes 包装 4 个 createElement('div') DOM nodes, escapeHtml 用 textContent (XSS 安全, 浏览器自动 escape)
      // v0.8.9 (辉哥 10/7 12:25 拍板) — 事件块信息增强:
      //   ① 第 1 行: 学员头像(16px, 排在名字前) + 学员名 + ' · ' + 学员卡余额
      //   ② 第 2 行: 教练头像(16px) + 教练**纯姓名** (辉哥明确要求不带任何课程类型)
      //   ③ 第 3 行: 分类 · 类型 · 学员卡名
      //   ④ 第 4 行: 备注 (不变)
      //   文字内容全部由 buildLessonLines() 产出 → 跟 title tooltip 永远同源。
      eventContent: info => {
        // ── v0.15.0: 预留块 (辉哥 10/10 补充: 黄色斜条等样式保持不变) ────────
        //   DOM 结构跟 v0.8.4 自绘时**逐字一致** (.zs-rsv-in / .zs-rsv-title /
        //   .zs-rsv-why / .zs-rsv-gran 四层), LESS 段也就一行都不用改 ——
        //   样式保持靠的是「class 名不变」, 不是重新设计一套。
        //   区别只有两处, 都是迁移必然带来的:
        //     ① 四角手柄 DOM 不再建 —— 改由 FC 的 .fc-event-resizer-start/end 提供
        //        (LESS v0.15.0 段给它们补定位; FC 原生只给 .fc-h-event 定位, timegrid 不给)
        //     ② 「占 N 格」从 'N = (end-start)/slotMs' 改成 'N = event.duration 的分钟数 / slotMin'
        //        —— FC 事件自带 duration, 不用再拿 start_at/end_at 反算。
        if (self.isReservationEvent(info.event)) {
          return {
            domNodes: self.buildReservationContentNodes(info.event)
          };
        }
        const l = info.event.extendedProps || {};
        const d = self.buildLessonLines(l);
        const line1 = document.createElement('div');
        line1.className = 'fc-event-line-1';
        line1.appendChild(self.buildEventAvatarNode(d.student));
        // 文字必须包一层 <span>, 不能直接 appendChild(textNode):
        //   LESS v0.8.9 段把行 1/2 改成 display:flex (头像与文字同一行居中),
        //   而 flex 容器里的裸 text node 会变成匿名 flex item,
        //   text-overflow:ellipsis 对它**不生效** → 长名字不再省略号, 会顶出事件块。
        //   包 span 后由 span 自己承担 ellipsis (对应 LESS 的 `.zs-ev-avatar + span` 规则)。
        const span1 = document.createElement('span');
        span1.textContent = d.line1;
        line1.appendChild(span1);

        // ── v0.9.0 角标 (Block 5) ──────────────────────────────────────────
        //   规格图 design/fixed-class-spec.html: 角标是**绝对定位**贴在行 1 右上,
        //   不参与行内流式布局 —— 这样它再宽也不会把行 1 撑高,
        //   三档块高 (99 / 49 / 33px) 全部不受影响, 也就不需要「按档位重算高度」那套逻辑。
        //   「固定课 / 已过期」这两个文字标签已经拼进 line1 文本里了 (buildLessonLines),
        //   这里只补**图形角标**: 固定课模式下的「每周」和「仅本次」。
        //   为什么只在固定课模式打角标: 正常模式里「每周」是噪音 (用户不需要关心来源),
        //   辉哥 B4 只要求「明示」, 靠 line1 的「固定课」文字 + 斜纹底就够明示了。
        const badges = [];
        if (d.isFixed) {
          const badge = document.createElement('span');
          badge.className = 'zs-ev-badge';
          if (self.calendarMode === 'fixed' && d.isSingleException) {
            badge.classList.add('is-once');
            badge.textContent = '仅本次';
          } else if (self.calendarMode === 'fixed') {
            badge.classList.add('is-weekly');
            badge.textContent = '每周';
          } else {
            // 正常模式: 不显示角标文字, 只用一个圆点做「固定课」的位置提示
            badge.classList.add('is-dot');
            badge.textContent = '';
          }
          if (d.cardExpired) badge.classList.add('is-expired');
          badges.push(badge);
        }

        // ── v0.12.0 「半节」角标 (辉哥 10/9 拍板) ────────────────────────
        //   规格: 半节课**课块大小不变**, 只在右上角加一个「半节」标签。
        //   所以这里**不碰** eventShortHeight / 块高分档逻辑, 也不加任何撑高度的样式 ——
        //   角标跟既有「每周 / 仅本次」共用同一套 .zs-ev-badge 定位 (absolute, 15px 高),
        //   天然矮于 20px 行高, 顶不开任何一档块高。
        if (d.isHalf) {
          const half = document.createElement('span');
          half.className = 'zs-ev-badge is-half';
          half.textContent = '半节';
          badges.push(half);
        }

        // ── v0.17.0 「体验」角标 (辉哥 10/12 拍板) ──────────────────────
        //   规格: 体验课课块**渲染方式与正课完全一样** —— 不加斜纹底、不加紫左边条,
        //   只在右上角加一个「体验」标签。区别只在这一枚角标。
        //   机制照抄上面「半节」角标: push 进 badges 数组, 由 .zs-ev-badge-row
        //   统一并排 —— **不能**直接 append 到 line1, 因为 .zs-ev-badge 是
        //   `position:absolute; right:0`, 直接 append 会跟已有角标**重叠叠印**
        //   (「半节体验课」「每周体验课」都是真实存在的组合)。
        //   高度沿用 .zs-ev-badge 的 15px (矮于 20px 行高), **不顶开课块**,
        //   也不加任何撑高度样式 —— 三档块高 (99/49/33px) 全部不受影响。
        if (d.isTrial) {
          const trial = document.createElement('span');
          trial.className = 'zs-ev-badge is-trial';
          trial.textContent = '体验';
          badges.push(trial);
        }

        // ⚠️ 为什么角标要套一层 .zs-ev-badge-row 而不是各自直接 append 到 line1:
        //   .zs-ev-badge 本身是 absolute right:0, 再直接 append 一个就会**重叠**在同一位置。
        //   「半节固定课」(isFixed && isHalf 同时成立) 是真实存在的组合, 不处理就两个标签叠印。
        //   套一层 absolute + flex 的容器后多个角标从右往左并排, 互不遮挡;
        //   **只有 1 个角标时视觉效果与改前完全一致** —— 容器自身同样贴在 line1 右上角,
        //   角标在容器内回到静态流, 单个时渲染结果逐像素相同 (已用实测截图比对, 见 CHANGELOG)。
        if (badges.length) {
          const row = document.createElement('span');
          row.className = 'zs-ev-badge-row';
          badges.forEach(b => row.appendChild(b));
          line1.appendChild(row);
        }

        // 教练行: 无教练时整行不渲染 (辉哥拍板, 不显示占位文案), 不留空行占高度
        const nodes = [line1];
        if (d.line2) {
          const line2 = document.createElement('div');
          line2.className = 'fc-event-line-2';
          line2.appendChild(self.buildEventAvatarNode(d.coach));
          const span2 = document.createElement('span');
          span2.textContent = d.line2;
          line2.appendChild(span2);
          nodes.push(line2);
        }

        // ── v0.17.57 — 课块第三行: 课自己的真实起止 (只在有课表时才有) ────────
        //   位置: 插在第 2 行 (教练) 之后, 即「新增的第三行」; 原来的分类/类型与备注
        //   顺延到第 4/5 行, **内容一个字没改**, 只是整体下移一行。
        //   为什么不是替换掉原第 3 行: 老板原话是「新增第三行」, 而且分类/类型/卡名是
        //   学员最需要看的信息, 拿它换时间不划算 (与 v0.9.0 「已过期不占第 3/4 行」同一条理由)。
        //   空串 (兜底态) ⇒ 整行不建, 课块行数与 v0.17.56 完全一致。
        if (d.timeLine) {
          const timeEl = document.createElement('div');
          timeEl.className = 'fc-event-line-time';
          timeEl.textContent = d.timeLine;
          nodes.push(timeEl);
        }
        const line3 = document.createElement('div');
        line3.className = 'fc-event-line-3';
        line3.textContent = d.line3;
        if (d.line3) nodes.push(line3);
        const line4 = document.createElement('div');
        line4.className = 'fc-event-line-4';
        line4.textContent = d.line4;
        if (d.line4) nodes.push(line4);
        return {
          domNodes: nodes
        };
      }
    });
    calendar.render();
    this._calendar = calendar;
    // ── v0.19.00 — 若有一轮「静默刷新」留下的待还原状态, 在**新实例建好之后**立即还原 ──
    //   ⚠️ 为什么必须放在这里 (而不是只在 load() 之后还原一次):
    //     load() 的 finally 里 `m.redraw()` 是**异步**的 ⇒ 它引发的 onupdate 会**再重建一次** FC,
    //     checkConflicts() 收尾还会再重建一次 ⇒ 只还原一次的话, 还原值会被后来的重建冲掉。
    //   ⚠️ 这里**不**还原页面滚动 (见 _restoreCalendarView 的注释) —— 页面滚动只在 load 之后还原一次。
    this._applyPendingViewRestore();

    // v0.15.4 — 记账: 构造时带上去的轴窗口就是「当前已生效的窗口」,
    //   _syncAxisToDate 拿它做幂等比对 (不记的话第一次 datesSet 会白白重排一次)。
    this._appliedAxisMin = range.min;
    this._appliedAxisMax = range.max;

    // v0.8.4 — 这里原本还有一句 _syncReservationBlocks() (FC 渲染完后把自绘预留块挂上去),
    //   v0.15.0 已删: 预留块改由上面的 events: 第二条 source 直接进 FC, 不再需要事后挂 DOM。
    //   留着它会让 tryMountCalendar() 销毁重建自己 → 无限循环。

    // v0.6.7 — 桌面 dblclick + 移动 2-tap 时间格 → 打开约课 modal
    //   FC v6 timegrid 渲染: .fc-timegrid-slot-lane[data-time="HH:MM:SS"]
    //                       .fc-timegrid-col[data-resource-id="r_<id>"]
    // v0.8.12 — 双击/2-tap 两条路径已移除, 这里只绑菜单入口:
    //   桌面 contextmenu → 气泡菜单, 移动长按 500ms → 底部面板。
    this.bindBookingTriggers(el);
  }

  // v0.6.8a — 打开约课/编辑 modal (从 bindBookingTriggers 闭包提到 self method,
  //   让 eventClick FC config 也能共享, V 测建议)
  //   resourceId/dataTime: slot 信息 (空格子必填)
  //   eventEl: fc-event DOM 节点 (可选)
  //   lessonArg: 已知的 lesson (可选, eventClick 路径传)
  openBooking(resourceId, dataTime, eventEl, lessonArg) {
    const fcEvent = eventEl || null;
    let lesson = lessonArg || null;

    // v0.6.8a — V 测 FAIL fix: 菜单路径如果没传 lessonArg, 从 fcEvent dataset 读
    if (!lesson && fcEvent) {
      const lessonId = fcEvent.getAttribute('data-lesson-id');
      if (lessonId) {
        lesson = this.lessons.find(l => String(l.id) === lessonId) || null;
      }
    }
    if (!lesson && !dataTime) return;
    const roomIdStr = resourceId ? String(resourceId).replace(/^r_/, '') : '';
    let room = roomIdStr ? this.rooms.find(r => String(r.id) === roomIdStr) : null;
    let mode = lesson ? 'edit' : 'create';
    if (lesson) {
      // 同步 room from lesson
      const lessonRoom = this.rooms.find(r => String(r.id) === String(lesson.room_id));
      if (lessonRoom) room = lessonRoom;
    }
    let startDate, endDate;
    if (mode === 'edit' && lesson) {
      startDate = new Date(lesson.start_at);
      endDate = new Date(lesson.end_at);
    } else {
      // create: 取槽时间 → ISO 字符串 + 默认 slotMin 兜底
      // v0.8.4 — slotMin 走 SCD (决策⑥ 格宽动态跟随校区时间排版), 兜底 30min
      // v0.8.10 — 改前这里写的是 this.currentDate.toISOString().slice(0, 10):
      //   toISOString() 先转 UTC 再 slice, 东八区本地 00:00-07:59 → UTC 前一天
      //   → 建课 modal 日期比表头少一天 (辉哥 10/7 13:03 报), 真机会把课约到前一天。
      //   跟 load() L193 是同一个坑 (v0.6.37 在 load 修过, 这里漏改), 现统一走 localDateStr()。
      // ⚠️ 下面 startDate/endDate.toISOString() (openBookingModal 入参) 是**绝对时刻**,
      //   用 UTC 正确, 不要跟着改成本地分量。
      const dateStr = this.activeDateStr();
      // ⚠️ v0.17.57 — dataTime 是格子的**名义**时间 (第 2 行 = '01:00:00'),
      //   必须换回该时间段的真实 start_time, 否则会预填一条 01:00 的课 (后端必拒)。
      //   换算在 realTimeForSlotTime() 里, 换不到就原样用 (兜底渲染模式下 dataTime 本就是真实时间)。
      const realTime = this.realTimeForSlotTime(dataTime);
      startDate = new Date(dateStr + 'T' + realTime);
      endDate = new Date(startDate.getTime() + this.slotMin() * 60000);
    }
    // v0.17.64 — 把「用户点的是哪一档」一起递进弹窗 (新建模式才需要; edit 走 lesson 回显)。
    //   为什么必须递: create 模式改前只传 start, form.scheduleNodeId 恒 null ⇒ 落库带 null ⇒
    //   后端 ScheduleResolver::nodeIn() 只能按 start_at 反推, 而重叠档 (v0.17.62 起合法) 的反推
    //   口径是「sort_order 第一条命中」 ⇒ 一节 10:30 的课被判给 10:15-11:00 那档, 于是
    //   **课存在 10:30, 却画在标着 10:15 的那一行**, 且那一行还显示已占用 (辉哥 10/16 线上现象)。
    //   这里传的是**行下标换算出来的那个节点** (scheduleNodeForSlotTime, 与左列标签
    //   slotLabelContent 同一条换算), 所以「点哪一行」与「存哪一档」从入口就绑死。
    //   换不到 (兜底渲染 / 越界) → null, 弹窗那边会退回按 startTime 反查, 行为与改前一致。
    const slotNode = mode === 'create' && dataTime ? this.scheduleNodeForSlotTime(dataTime) : null;
    // v0.6.37 — 改调 utils/BookingModalHelper.openBookingModal (辉哥 9/29 23:46 反馈, DRY)
    //   行为不变: 时间格 → mode=create/edit, start/end/room 预填
    //   ⚠️ v0.8.12 起调用方是菜单 onBook/onEdit (双击/2-tap 已移除), openBooking 本体没动。
    //   setTimeout(0) + try/catch 移到 helper 内部 (跟 LessonsTab 共享, 防止 mithril race SOP 98)
    openBookingModal({
      mode: mode,
      lesson: lesson,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      room: room || null,
      scheduleNodeId: slotNode && slotNode.id != null ? +slotNode.id : null,
      activeSchoolId: this.activeSchoolId,
      // v0.18.35 (辉哥 10/20 拍板) — fc 课表入口的日期**固定不可改**:
      //   用户点的是课表上**具体某一格**, 那一格的日期就是这次约课的日期 (弹窗里显示只读 chip)。
      //   ⚠️ 显式写 true 而不是靠默认值: 这个入口的语义就是「锁死」, 写出来是为了让
      //     下一个读代码的人一眼看到「这里是故意锁的, 不是忘了传」。
      lockDate: true,
      onClose: () => {
        this.load();
      },
      // v0.19.16 — 学员分流用: 日历**当前查看的那天** (学员点的是课表上某一格 ⇒ 那天的日期
      //   就是这次约课的日期)。⚠️ 用 activeDateStr() —— 它是**本地**日历日 (固定模式下
      //   取「本周选中星期几」那天), ⛔ 不用 toISOString().slice(0,10) (UTC, 东八区凌晨差一天)。
      //   员工分支不读这个字段 (员工版弹窗自己用 start/end)。
      date: this.activeDateStr()
    });
  }

  // ─── v0.8.11 约课卡拖拽遇预留停住 ────────────────────────────────────────

  // 从 FC 传进来的 dropInfo 里解析出"鼠标当前落在哪个教室", 拿不到就 null
  //   FC 6.1.21 实测 (⚠️ v0.8.11b 更正, 原注释判断方向写反了): 真实拖拽时 dropInfo 的键是
  //     ["resource", "start", "end", "startStr", "endStr", "allDay"]
  //   **只有 resource, 没有 resourceId** → 主形态是 dropInfo.resource.id = 'r_<roomId>' 字符串。
  //   逆向勘察 6.1.21 minified 一致: xr() 只取 range/allDay, 不 spread 整个 props;
  //   resource 插件的 dateSpanTransforms 产出 { resource: ResourceApi }。
  //   dropInfo.resourceId 这条是**另一种形态的兼容保留** (兜住 FC 若改按 resourceId 下发),
  //   不是死代码。
  //   ⚠️⚠️ **两条分支都不可删, 删任一条都可能导致功能静默失效且无报错**:
  //     删掉 resource.id 分支 → resourceId 在当前 FC 下恒 undefined → 这里返回 null →
  //     canDropOverReservations() 一律 return true 放行 → 约课卡直接压在预留块上, 不抛错不报警。
  //     删掉 resourceId 分支 → 同理, 只是当前 FC 恰好走不到那条, FC 一变就露馅。
  _roomIdFromDropInfo(dropInfo) {
    let raw = dropInfo.resourceId;
    if (raw == null && dropInfo.resource && dropInfo.resource.id != null) {
      raw = dropInfo.resource.id;
    }
    if (raw == null) return null;
    // 非资源分片 ('') / 拿不到数字 → null, 调用方按"放行"处理, 不误伤
    const id = parseInt(String(raw).replace(/^r_/, ''), 10);
    return Number.isNaN(id) ? null : id;
  }

  // 某段 [startMs, endMs) 是否撞上任意预留块
  //   excludeLessonId 刻意**不提供**: 这里比的是 reservation 而不是 lesson,
  //   被拖的 lesson 自己永远不可能同时是一条 reservation, 没有可排除的对象。
  //   (brief 里的 excludeLessonId 参数是照搬 lesson 冲突检查的形状, 对预留无意义)
  overlapsReservation(startMs, endMs, roomId) {
    if (!(endMs > startMs) || roomId == null) return false;
    const list = this.slotReservations || [];
    for (const rsv of list) {
      if (!rsv || rsv.start_at == null || rsv.end_at == null) continue;
      if (+rsv.room_id !== +roomId) continue;
      const rs = zsParseLocal(rsv.start_at);
      const re = zsParseLocal(rsv.end_at);
      if (!rs || !re) continue;
      // 只比当天: slotReservations 可能带着别的日期的预留 (renderer 渲染时也按 today 过滤)。
      // 时间区间本来就跨天不可能相交, 这层是双保险 —— 万一有脏数据写了跨天预留,
      // 也不该让它把当天的约课卡锁死。
      if (rs.getFullYear() !== new Date(startMs).getFullYear() || rs.getMonth() !== new Date(startMs).getMonth() || rs.getDate() !== new Date(startMs).getDate()) continue;
      // 半开区间相交: rsv.start < newEnd && rsv.end > newStart
      if (rs.getTime() < endMs && re.getTime() > startMs) return true;
    }
    return false;
  }

  /**
   * eventAllow 回调: 拖拽过程中实时判定落点是否合法。
   *
   * ⚠️ 拿不到教室 / 拿不到 start 一律 return true (放行) —— 宁可放过也不能误伤正常拖拽。
   *
   * ── v0.17.57 课表态: 拖拽简化成「拖到第 N 行 ⇒ start_at = 第 N 段的 start_time」 ──
   *   这条规则**不需要**算 snapDuration 的步进/余数 (v0.17.51 那套就是回归的源头):
   *   FC 负责把指针吸附到某一行, 我们只要把「第几行」翻成「第几段的真实区间」就行。
   *   撞教室/撞教练/撞预留照旧判, 但**必须在真实时间上判** —— 拿名义时间 (00:00/01:00)
   *   去比真实的预留数据, 结果恒为「不撞」, 整个拦截能力静默失效且不报任何错。
   *   落点换算跟落库前 (persistReservationMove) 共用 realRangeForNominalRange, 口径一致。
   *
   *   「这一格有没有课/教练冲突」仍交给后端 (LessonConflictChecker) 判 —— 那是老规矩,
   *   约课弹窗提交时的 5 重碰撞检测一直在做, 前端不重复实现一份。
   */
  canDropOverReservations(dropInfo, draggedEvent) {
    if (!dropInfo) return true;
    const roomId = this._roomIdFromDropInfo(dropInfo);
    if (roomId == null) return true;
    const rawStart = dropInfo.start;
    if (!(rawStart instanceof Date) || Number.isNaN(rawStart.getTime())) return true;
    // end 可能缺失 (allDay / defaultAllDay 场景), 兜底走一格 (跟 handleEventDrop 同款)
    const rawEnd = dropInfo.end instanceof Date && !Number.isNaN(dropInfo.end.getTime()) ? dropInfo.end : new Date(rawStart.getTime() + this.slotMin() * 60000);
    const real = this.realRangeForNominalRange(rawStart, rawEnd);
    return !this.overlapsReservation(real.start.getTime(), real.end.getTime(), roomId);
  }

  // ═══ v0.15.0 课表时段预留 (FC event 实现) ═══════════════════════════════
  //
  // 迁移前 (v0.8.4 ~ v0.14.2): 预留块是 SlotReservationRenderer 自绘的绝对定位 div,
  //   挂在 `.fc-timegrid-col > .fc-timegrid-col-frame` 下, 四角手柄 pointerdown 自己算吸附。
  //   代价: ① 依赖 FC **私有 DOM** (.fc-timegrid-col-frame / .fc-timegrid-slot-lane[data-time]),
  //          FC 一改结构就断; ② 位置自己算, getFirstSlotInfo() 拿 new Date() 当基准,
  //          翻到非今天的日期就偏出屏外 (实测 top=-7000px, 本次顺带修掉);
  //          ③ 撞课只能「ghost 转红 + 保存最后合法值」。
  // 现在: 走 FC 自己的 event / source / 回调, 位置、吸附、回弹、跨天都交给 FC。

  /** 这个 FC event 是不是一条时段预留 (按 id 前缀判断, 见 ZS_RSV_PREFIX 注释) */
  isReservationEvent(event) {
    return !!(event && String(event.id || '').indexOf(ZS_RSV_PREFIX) === 0);
  }

  /** 从 FC event id 里取回原始 reservation id ('rsv_68' → 68) */
  reservationIdOf(event) {
    if (!this.isReservationEvent(event)) return null;
    return String(event.id).slice(ZS_RSV_PREFIX.length);
  }

  /**
   * 这条预留块**当前所属**的教室 id (数字), 拿不到返回 null。
   *
   * v0.15.1 新增 —— 跨教室判定必须跟落库比对的是**同一份数据**, 否则两处口径会打架
   * (曾经的 bug: eventAllow 按 FC 给的新教室判, 落库前复检按 rsv.room_id 判)。
   * 优先取数据模型 this.slotReservations; 取不到才退到 FC event 自己的 resource。
   */
  reservationRoomIdOf(event) {
    const rsvId = this.reservationIdOf(event);
    if (rsvId != null) {
      const rsv = (this.slotReservations || []).find(r => String(r.id) === rsvId);
      if (rsv && rsv.room_id != null) return +rsv.room_id;
    }
    const res = event && event.getResources ? event.getResources() : [];
    if (res && res[0]) return this._roomIdFromDropInfo({
      resource: res[0]
    });
    return null;
  }

  /**
   * v0.15.1 —— **拖完那一刻**这条块所在的教室 id (数字), 拿不到返回 null。
   * 跟 reservationRoomIdOf 是一对: 一个读「事件自己的 / 数据模型的」, 一个读「本次落点的」。
   * 跨教室判定 = 这两个不相等。resize 时不会变 (FC resize 不改 resource) ⇒ 天然不误伤。
   */
  droppedRoomIdOf(info) {
    if (!info) return null;
    if (info.newResource) {
      const id = this._roomIdFromDropInfo({
        resource: info.newResource
      });
      if (id != null) return id;
    }
    const ev = info.event;
    if (ev && ev.getResources) {
      const res = ev.getResources();
      if (res && res[0]) {
        const id = this._roomIdFromDropInfo({
          resource: res[0]
        });
        if (id != null) return id;
      }
    }
    return null;
  }

  /**
   * 把 this.slotReservations 转成 FC event 定义 (固定课模式下返回空数组)
   *
   * ⚠️ start/end 给的是 **Date 对象**, 不是 'YYYY-MM-DDTHH:mm:ss' 字符串 ——
   *   这是有意为之: 后端返回的是空格分隔的时间串, 字符串走 FC 会被按本地时区解析,
   *   而 Date 对象由 zsParseLocal() 在本地时区构造, 跟页面上其他日期口径完全一致
   *   (CalendarTab 顶部那段注释就是为这个函数写的)。
   */
  buildReservationEventDefs() {
    if (!this.reservationsVisibleInCurrentMode()) return [];
    const out = [];
    (this.slotReservations || []).forEach(r => {
      if (!r || !r.start_at || !r.end_at) return;
      const start = zsParseLocal(r.start_at);
      const end = zsParseLocal(r.end_at);
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      // ── v0.17.57 课表态: 预留块跟课块走同一套行定位 ──────────────────────
      //   不换的话, 名义轴 (00:00 ~ N×基准) 之外的 09:00/13:00 在 FC 里**根本渲染不出来**
      //   —— 预留块会集体消失 (不是报错, 是静默看不见), 这比画错位置更难发现。
      //   换算口径跟课块完全一致 (scheduleIndexForRender), 落库时再换回真实时间。
      //
      // ── v0.18.43 — 行定位**优先按存下来的 node id** (与 scheduleRowIndexOfLesson 同款口径) ──
      //   为什么必须有这一层: 课表允许**区间重叠** (10:15-11:00 接 10:30-11:15),
      //   而 scheduleIndexOf 是闭区间 + 取 sort_order 第一条 ⇒ start_at=10:30 的预留
      //   会被判给**前一档** (10:15-11:00) ⇒ **画错行** (生产实测坐实)。
      //   存了 node id 就能唯一定位; 悬空 (节点被删) / 没有 (老数据) 才回落 start_at 反推。
      //   ⚠️ 回落路径**必须保留** —— 老预留全是 NULL, 且悬空 id 不能让整页课表白屏。
      const nodeId = r.schedule_node_id != null ? +r.schedule_node_id : null;
      let idx = null;
      if (this.hasSchedule()) {
        if (nodeId) {
          const byId = this.scheduleNodes.findIndex(n => n && +n.id === nodeId);
          if (byId >= 0) idx = byId;
        }
        if (idx == null) idx = this.scheduleIndexForRender(this._minutesOfDay(start));
      }
      // ── v0.18.44 — 跨格预留要画成**多格高** ─────────────────────────────────
      //   改前 end 被写死成一格 ⇒ 库里存了多格跨度也只画出一格高 (视觉与数据不一致)。
      //   反推办法: 从起始行往后找**第一档** end_time 与预留 end_at 的时刻**精确相等**的那一档,
      //   它的下标 = 最后一格 ⇒ cells = 最后一格 - 起始行 + 1。
      //   ⚠️ 判据跟 v0.18.43 回填老数据用的是同一条原则 (时刻精确相等 ⇒ 唯一确定, 不猜)。
      //   ⚠️ **起点必须正好落在起始那一档的开头**才认这个跨度 —— 这条是「不误伤老数据」的闸:
      //     老预留 (schedule_node_id 为 NULL) 在**重叠课表**上, 按 start_at 反推出来的行
      //     往往是**前一档** (10:30 会被闭区间判给 10:15-11:00 那一档), 它的 start_at
      //     并不等于那一档的开头 ⇒ 不认 ⇒ cells = 1 ⇒ 跟改前一模一样, 不会集体长高一格。
      //   找不到精确相等的 (老数据 / 异常数据 / 跨版本) ⇒ cells = 1 (兜底, 不炸)。
      let cells = 1;
      if (idx != null && this.hasSchedule()) {
        const startNode = this.scheduleNodes[idx];
        const startAligned = startNode && this._hhmmToMinutes(startNode.start_time) === this._minutesOfDay(start);
        if (startAligned) {
          const endMin = this._minutesOfDay(end);
          for (let i = idx; i < this.scheduleNodes.length; i++) {
            const n = this.scheduleNodes[i];
            if (!n) continue;
            if (this._hhmmToMinutes(n.end_time) === endMin) {
              cells = i - idx + 1;
              break;
            }
          }
        }
      }
      const rowStart = idx != null ? this.nominalRowDate(start, idx) : start;
      out.push({
        id: ZS_RSV_PREFIX + r.id,
        resourceId: 'r_' + r.room_id,
        start: rowStart,
        end: idx != null ? new Date(rowStart.getTime() + cells * this.nominalSlotMinutes() * 60000) : end,
        // title 只作 hover tooltip 的数据源; 真正显示出来的文字由 eventDidMount 写 title 属性
        title: r.notes ? '已预留 · ' + r.notes : '已预留',
        // .zs-reserve 是命中「黄色斜纹」那段 LESS 的唯一入口, class 名沿用 v0.8.4 不改
        classNames: ['zs-reserve'],
        extendedProps: Object.assign({}, r, {
          zsIsReservation: true
        })
      });
    });
    return out;
  }

  /**
   * 预留块的内容 DOM (eventContent 用)
   *   结构与 v0.8.4 自绘时逐字一致, LESS 一行未改 —— 这是「样式保持不变」的实现方式。
   */
  buildReservationContentNodes(event) {
    const r = event.extendedProps || {};
    const inner = document.createElement('div');
    inner.className = 'zs-rsv-in';
    const title = document.createElement('div');
    title.className = 'zs-rsv-title';
    // v0.10.1 起不再展示发布者用户名, 这里保持一致 (LESS 的 .zs-rsv-who 段是历史留存, 已无 DOM 命中)
    const icon = document.createElement('i');
    icon.className = 'fas fa-lock';
    const label = document.createElement('span');
    label.textContent = '已预留';
    title.appendChild(icon);
    title.appendChild(label);
    inner.appendChild(title);
    if (r.notes) {
      const why = document.createElement('div');
      why.className = 'zs-rsv-why';
      why.textContent = r.notes; // textContent, 不拼 HTML (XSS 安全, 跟课块 4 行同款做法)
      inner.appendChild(why);
    }
    // 「占 N 格」徽标: FC event 自带 duration, 不再从 start_at/end_at 反算毫秒差
    const gran = document.createElement('div');
    gran.className = 'zs-rsv-gran';
    const startMs = event.start ? event.start.getTime() : 0;
    const endMs = event.end ? event.end.getTime() : startMs;
    const durMin = Math.round((endMs - startMs) / 60000);
    gran.textContent = '占 ' + Math.max(1, Math.round(durMin / this.slotMin())) + ' 格';
    return [inner, gran];
  }

  /**
   * eventAllow 的**预留块分支**: 这个新范围能不能放?
   *   四道闸: ⓪ 跨教室 ① 跨天 / 出营业时间 ② 撞约课 ③ 撞别的预留
   *   撞了就 return false → FC 原生弹回原位 (辉哥 10/10 拍板的新行为)。
   *
   * ⚠️ v0.15.1 — ⓪ 跨教室这道闸是**新增**的, 修的是「界面假成功、数据静默丢失」。
   *   根因: 后端 UpdateSlotReservationController 只收 start_at / end_at, **不收 room_id**
   *   (vendor 注释写明「跨教室不走此端点, 要走 copy + delete」)。而 FC 的 resource 视图
   *   天然允许把 event 拖到别的列 —— 于是 v0.14.2 时代被 colAtX 明确禁掉的横向跨列动作,
   *   换 FC event 后重新变成「看着搬过去了 → PATCH 不带 room_id → 刷新弹回」。
   *   这里 return false → FC 连视觉位移都不给, 从根上消掉那个误导性的假成功。
   */
  canPlaceReservation(dropInfo, draggedEvent) {
    if (!dropInfo) return true;
    let start = dropInfo.start;
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) return true;
    let end = dropInfo.end instanceof Date && !Number.isNaN(dropInfo.end.getTime()) ? dropInfo.end : new Date(start.getTime() + this.slotMin() * 60000);
    // v0.17.57 — 有课表时 FC 下发的是**名义**时间 (第 N 行 = N×基准), 真实钟点必须换回来。
    //   不换的后果很具体: 后面 withinVisibleGrid / _checkConflict 会拿 00:00、01:00 去比
    //   真实的预留数据 ⇒ 全部判成非法 ⇒ **一个预留块都拖不动**。
    const real = this.realRangeForNominalRange(start, end);
    start = real.start;
    end = real.end;

    // v0.18.44 — 越界 (扩/缩到课表最后一行之外) → 非法, FC 原生弹回原位。
    //   与「跨教室」「撞课」同一套口径: 判非法 ⇒ FC 连视觉位移都不给。
    //   ⚠️ **必须放在下面「拿不到教室就放行」那道早退之前** —— 那是 eventAllow 的兜底放行分支,
    //     放到它后面就等于把越界落点放过去了 (roomId 拿不到时越界闸会被跳过)。
    if (real.overflow) return false;

    // ① 跨天 / 落在课表的时间段之外 → 非法
    if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth() || start.getDate() !== end.getDate()) return false;
    // ⚠️ v0.17.57 — 原来这里还会 businessHoursViolation() 补一句「营业时间是 XX-XX」的 toast。
    //   business_hours 已随 v0.17.55 整体下线, 那个方法恒返 null, 也就是这段 toast 早就是死代码。
    //   现在这一句拒绝**没有任何提示**: 预留块拖不出时间段 → 静默弹回。
    //   ⚠️ 有课表的校区里这已经是**正常交互** (时间段就是那几个, 拖不出它们就该弹回),
    //     所以不补提示是有意的; 真要补文案, 也得是「该校区的时间段是 09:00-12:00 / …」这种
    //     课表口径, 与本轮范围 (课表配置 UI 下一轮才做) 不匹配, 留到下一轮一起做。
    if (!this.withinVisibleGrid(start, end)) return false;

    // 教室: resize 时 dropInfo 未必带 resource, 退到事件自己的 resource
    let roomId = this._roomIdFromDropInfo(dropInfo);
    if (roomId == null) {
      const res = draggedEvent && draggedEvent.getResources ? draggedEvent.getResources() : [];
      if (res && res[0]) roomId = this._roomIdFromDropInfo({
        resource: res[0]
      });
    }
    // 拿不到教室就放行 (跟 canDropOverReservations 同一个「宁可放过也不误伤」的口径)
    if (roomId == null) return true;

    // ⓪ 跨教室 → 非法 (v0.15.1)
    //   dropInfo.resource 是**鼠标当前所在列**, reservationRoomIdOf 是这条块自己的教室
    //   —— 两者不等就是跨教室。实测 (FC 6.1.21 真实拖拽): 横向拖动时 dropInfo.resource
    //   会从 r_96 变成 r_97, 而 draggedEvent.getResources() 全程保持 r_96。
    //   resize 时 dropInfo.resource 恒等于自己那一列 ⇒ 这道闸对 resize 是恒真的, 不会误伤。
    const ownRoomId = this.reservationRoomIdOf(draggedEvent);
    if (ownRoomId != null && roomId !== ownRoomId) {
      this._warnCrossRoomOnce();
      return false;
    }

    // ② + ③ 撞约课 / 撞别的预留 —— 复用 v0.8.4 的 _checkConflict, 不重写一份
    const renderer = this.slotReservationRenderer;
    if (renderer && typeof renderer._checkConflict === 'function') {
      const rsvId = this.reservationIdOf(draggedEvent);
      return !renderer._checkConflict(roomId, start.getTime(), end.getTime(), rsvId == null ? null : +rsvId);
    }
    return true;
  }

  /**
   * v0.15.1 — 跨教室被拒时的提示, **每次拖拽手势只弹一次**。
   *
   * 为什么必须去重: eventAllow 是拖拽过程中**每帧**都调的 (实测一次跨列拖拽至少两次,
   * 横跨更多列时更多), 直接 toast 会刷屏。而「拖不动又不吭声」正是本项目已有的 backlog
   * 问题 (拖拽静默拒绝无 toast), 所以这个提示也不能省。
   * 去重窗口 = 一次手势 (eventDragStart 置位, eventDragStop 清位), 跨回去再拖会再弹一次。
   */
  _warnCrossRoomOnce() {
    if (this._crossRoomWarned) return;
    this._crossRoomWarned = true;
    this._toast('预留块暂不支持跨教室移动，请在本教室内调整时段', 'error');
  }

  /**
   * [start,end) 是否落在「可放的窗口」内。「同一天」不归这里管 (归 canPlaceReservation)。
   *
   * ⚠️ v0.17.57 口径换过一次, 两态必须分开写, 不能合并:
   *   · **有课表**: 名义轴是 00:00 ~ N×基准, 拿 _withinRange 去比**真实**落点会 100% 判非法
   *     (真实 09:00 永远落在名义窗口之外) ⇒ 预留块会被**全部**拦死, 一个都拖不动。
   *     正确判据 = 落点是否落进**某个时间段的真实区间** (即 scheduleIndexOf 不为 null)。
   *     这也正是后端 ScheduleResolver 的归属口径, 两边一致, 不会出现「前端放行 → 后端拒」。
   *   · **没课表**: 老口径不变 (08:00-22:00 并集窗口), 兜底渲染行为零变化。
   */
  withinVisibleGrid(start, end) {
    if (this.hasSchedule()) {
      return this.scheduleIndexOf(this._minutesOfDay(start)) != null;
    }
    return this._withinRange(start, end, this.slotRangeFromSCD());
  }

  /** 预留块拖完 → PUT start_at / end_at (后端 UpdateSlotReservationController 已支持) */
  async handleReservationDrop(info) {
    await this.persistReservationMove(info, '已移动预留');
  }

  /** 预留块上下边 resize 完 → PUT start_at / end_at (时长不变, 只是边界动了) */
  async handleReservationResize(info) {
    await this.persistReservationMove(info, '已调整预留');
  }

  /** 拖拽 + resize 的落库共用段: 两个都只改 start_at / end_at, 后端端点也是同一个
   *
   *  ⚠️ v0.15.1 — 新增跨教室兜底 (第 ⓪ 段)。
   *    eventAllow 已经是主防线 (跨教室直接 return false), 正常路径走不到这一段。
   *    但它必须留着, 因为 eventAllow 有三条「拿不到教室就放行」的早退分支
   *    (dropInfo 为空 / start 非法 / 解析不出 resource), 万一哪天 FC 的 dropInfo 形状变了,
   *    跨教室就会漏到落库这一段 —— 那时如果没有 ⓪, 就是 v0.15.0 那个「假成功 + 静默丢数据」。
   *    判据跟 eventAllow 完全同一个口径 (droppedRoomIdOf ≠ reservationRoomIdOf), 不各判各的。
   */
  async persistReservationMove(info, okMsg) {
    const rsvId = this.reservationIdOf(info.event);
    // v0.17.57 — 有课表时 FC 的 start/end 是**名义**时间, 落库前换回真实钟点。
    //   (与 canPlaceReservation 里那次换算是同一个方法, 两处口径必须一致,
    //    否则会出现「拖拽判定合法、落库却存了 01:00」这种静默脏数据。)
    const real = this.realRangeForNominalRange(info.event.start, info.event.end || new Date(info.event.start.getTime() + this.slotMin() * 60000));
    const newStart = real.start;
    const newEnd = real.end;
    // v0.18.43 — 落点对应的**目标节点 id** (realRangeForNominalRange 顺带算出来的, 唯一入口)。
    const newNodeId = real.nodeId != null ? +real.nodeId : null;
    const startStr = this.formatDateTimeLocal(newStart);
    const endStr = this.formatDateTimeLocal(newEnd);
    const rsv = (this.slotReservations || []).find(r => String(r.id) === rsvId);
    const oldStart = rsv ? rsv.start_at : null;
    const oldEnd = rsv ? rsv.end_at : null;
    // v0.18.43 — 旧归属节点 (老预留恒 NULL)
    const oldNodeId = rsv && rsv.schedule_node_id != null ? +rsv.schedule_node_id : null;

    // ⓪b-1 越界 (覆盖到课表最后一行之外) → 直接回滚, **不发请求** (v0.18.44)
    //   与本方法里已有的 ⓪b / ⓪ / ① 三处 revert 同一口径 (判为「没生效」就弹回)。
    //   ⚠️ eventAllow (canPlaceReservation) 已是主防线, 正常路径走不到这一段; 但它必须留着:
    //     eventAllow 有三条「拿不到教室就放行」的早退分支, 万一哪天 FC 的 dropInfo 形状变了,
    //     越界就会被绕过去 ⇒ 块画到课表之外、库里却存了一个不存在的档位。
    if (real.overflow) {
      info.revert();
      return;
    }

    // ⓪b 落点不在课表的时间段内 → 直接回滚, **不发请求** (v0.17.57)
    //   canPlaceReservation (eventAllow) 已是主防线, 正常路径走不到这一段。但它必须留着:
    //   因为 eventAllow 有三条「拿不到教室就放行」的早退分支 (dropInfo 为空 / start 非法 /
    //   roomId == null), 万一哪天 FC 的 dropInfo 形状变了, 这道闸就会被绕过去。
    //   ⚠️ v0.17.57 — 这里**不再**有「营业时间 XX-XX」的 toast: business_hours 已整体下线,
    //   那句文案 (businessHoursViolation / _businessHoursMessage) 已随之删除。
    //   判据跟 eventAllow 共用 withinVisibleGrid 这一个方法, 不各判各的。
    if (!this.withinVisibleGrid(newStart, newEnd)) {
      info.revert();
      return;
    }

    // ⓪ 跨教室 → 直接回滚, **不发请求** (v0.15.1)
    //   后端 UpdateSlotReservationController 不收 room_id, 所以「搬过去了但没存」= 数据静默丢失。
    //   与其假装成功, 不如弹回 + 说清楚为什么。
    const droppedRoomId = this.droppedRoomIdOf(info);
    const ownRoomId = rsv && rsv.room_id != null ? +rsv.room_id : null;
    if (droppedRoomId != null && ownRoomId != null && droppedRoomId !== ownRoomId) {
      info.revert();
      this._toast('预留块暂不支持跨教室移动，请在本教室内调整时段', 'error');
      return;
    }

    // ① 事件级回滚兜底 (跟 handleEventDrop 里 overlapsReservation 兜底同一个思路):
    //   正常路径走不到 (eventAllow 判非法时 FC 根本不触发本回调), 但万一 FC 行为变了
    //   也不至于把脏数据写进库 —— 这种失效是**静默的**, 不报错。
    //
    //   ⚠️ v0.15.1 — 判冲突用**落点教室** (droppedRoomId), 不是 rsv.room_id。
    //   改前用 rsv.room_id 是个真实的口径错误: 万一跨教室漏到这里 (⓪ 拦下之前),
    //   块已经画在新列上了, 却拿**旧教室**去查占用 —— 判出来的结果跟用户看到的画面对不上。
    //   走到 ① 时 droppedRoomId 要么等于 ownRoomId (同教室), 要么是 null (拿不到),
    //   两种情况下这个改法都不会改变原有判定结果, 只是把口径对齐。
    const checkRoomId = droppedRoomId != null ? droppedRoomId : ownRoomId;
    if (checkRoomId != null) {
      const renderer = this.slotReservationRenderer;
      if (renderer && typeof renderer._checkConflict === 'function' && renderer._checkConflict(checkRoomId, newStart.getTime(), newEnd.getTime(), rsvId == null ? null : +rsvId)) {
        info.revert();
        this._toast('该时段已被占用', 'error');
        return;
      }
    }
    // ② 没真挪动就别发请求 (跟 v0.8.4 的 `if (draftStart !== originalStart || ...)` 同一口径)
    //
    // v0.18.43 — **节点归属也算「有没有真挪动」**。
    //   起因 (生产实测坐实): 课表允许区间重叠 (10:15-11:00 接 10:30-11:15),
    //   老预留只存了 start_at=10:30 (没有 node id) ⇒ 读侧按 start_at 反推会判给**前一档**
    //   ⇒ 块画在第 1 行。辉哥把它拖到「本该在的那行」(第 2 行) 时, 换算回来的 start_at
    //   **还是 10:30** ⇒ 改前这里判成「没真挪动」⇒ **一个请求都不发**, 而且**没有 revert()**
    //   ⇒ 视觉漂移 + 数据不动, 刷新弹回。
    //   现在: 旧 node id (null) ≠ 新 node id (第 2 行那个) ⇒ 算「有变化」⇒ 发请求,
    //   顺带把老行的 node id 补上 (这是好事)。
    const noop = rsv && oldStart === startStr.replace('T', ' ') && oldEnd === endStr.replace('T', ' ') && oldNodeId === newNodeId;
    if (noop) {
      // ⚠️ 必须 revert(): 改前这里是「静默 return」⇒ 块停在用户拖到的位置, 但库里没变
      //   ⇒ 视觉与数据不一致 (刷新弹回)。revert 后视觉回到原位, 与数据一致。
      //   跟本方法前面三处 `info.revert()` 同一口径 (判为「没生效」就弹回)。
      info.revert();
      return;
    }
    try {
      await Api.update('slot-reservations', rsvId, {
        start_at: startStr,
        end_at: endStr,
        // v0.18.43 — 「用户选了哪一档」一起 PUT 过去 (写侧据此落库 + 校验)
        schedule_node_id: newNodeId
      });
      if (rsv) {
        rsv.start_at = startStr.replace('T', ' ');
        rsv.end_at = endStr.replace('T', ' ');
        // v0.18.43 — 本地对象同步归属节点 (否则下一次重绘还会按旧归属算行)
        rsv.schedule_node_id = newNodeId;
        // ⚠️ v0.15.1 — room_id 也一并对齐到**实际落点**。
        //   走到这里说明 ⓪ 已经判过「不跨教室」, 所以正常情况下 rsv.room_id 本来就等于
        //   droppedRoomId, 这一行是 no-op; 写它是为了让本地对象永远等于「库里的真实状态」,
        //   免得日后有人把 ⓪ 放宽后, 这里留一个跟画面不一致的 stale room_id。
        //   (v0.15.0 的 bug 就有这一半: 本地对象不更新 room_id, 后续 _checkConflict
        //    继续拿旧教室判, 于是 eventAllow 按新教室、落库复检按旧教室, 两套口径打架。)
        if (droppedRoomId != null) rsv.room_id = droppedRoomId;
      }
      this._toast(okMsg, 'success');
    } catch (e) {
      info.revert();
      // v0.17.66 — 为什么不能只看 `e.message` (改前这里只有 `|| '调整失败'`)
      //   拖动改期保存失败时后端抛 ValidationException, HTTP 422, **真实原因在
      //   `e.response.errors[0].detail`**; `e.message` 只是请求层拼的
      //   "Response code 422 (Unprocessable Entity)", 跟后端那句提示无关。
      //   后果: 「该时段已被占用」这类**可操作**的原因被吞成「调整失败」,
      //   用户已经看到卡片弹回去了, 却不知道为什么弹回去、该换到什么时候。
      //   形状照抄 v0.17.65 统一后的写法 (LessonEditorModal.js:1778 等 6 处);
      //   兜底文案「调整失败」原文保留, 降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this._toast(errReason(e, '调整失败'), 'error');
    }
  }

  /**
   * 方向锁定徽标 (v0.15.0: 四角 → 只往上扩 / 只往下扩)
   *
   * 为什么只剩两个文案: FC 在 timegrid 下只有 start / end 两个手柄 (vendor 判定式见
   * eventResizableFromStart 那段注释), 没有四角。原版四角文案「只往左上扩 / 只往右上扩 /
   * 只往左下扩 / 只往右下扩」里, 左右两字是纯装饰 —— 原来的 nw 与 sw 计算逻辑**逐字相同**
   * (SlotReservationRenderer v0.14.2 L498-510: nw 和 sw 都是 `newStart = firstSlotTime + cellIdx*slotMs`),
   * 而 x 轴变量只在注释里出现、代码里从没被读过。→ 四角 → 两手柄是功能无损的。
   *
   * 判定抓的是哪个手柄: eventDidMount 里记下的 this._rsvResizeEdge。
   */
  showResizeDirectionFlag(info) {
    const edge = this._rsvResizeEdge;
    if (!edge) return;
    const el = info && info.el;
    if (!el || !el.getBoundingClientRect) return;
    const r = el.getBoundingClientRect();
    this.hideResizeDirectionFlag();
    const flag = document.createElement('div');
    flag.className = 'zs-rsv-dirflag zs-rsv-dirflag-fc is-' + edge;
    flag.textContent = edge === 'start' ? '只往上扩' : '只往下扩';
    flag.style.top = (edge === 'start' ? Math.max(0, r.top - 26) : r.bottom + 4) + 'px';
    flag.style.left = Math.max(0, r.left + r.width / 2 - 32) + 'px';
    document.body.appendChild(flag);
    this._rsvDirFlagEl = flag;
  }
  hideResizeDirectionFlag() {
    if (this._rsvDirFlagEl && this._rsvDirFlagEl.parentNode) {
      this._rsvDirFlagEl.parentNode.removeChild(this._rsvDirFlagEl);
    }
    this._rsvDirFlagEl = null;
  }

  // v0.6.43 — 拖拽课程全处理 (FC eventDrop callback)
  //   info.event: 新位置的 FC event (含 start/end + extendedProps = lesson)
  //   info.oldEvent: 拖拽前的 FC event
  //   info.newResource / info.oldResource: 新旧教室 (FC resource)
  //   info.revert(): 视觉回滚 (拖拽失败时调用, 把 event 拖回 oldEvent 位置)
  //   限制: FC timeline day view 不允许跨日拖拽 (下一天不显示), 跨 calendar 拖需 eventAllow (本期不实现)
  handleEventDrop(info) {
    const fcEvent = info.el;
    const lessonId = fcEvent ? fcEvent.getAttribute('data-lesson-id') : null;
    const lesson = lessonId ? this.lessons.find(l => String(l.id) === lessonId) || null : null;
    if (!lesson) {
      // 没找到 lesson (历史数据/缓存 stale), 视觉回滚防止 FC 显示空 event
      info.revert();
      return;
    }

    // 1. 拼新 start_at / end_at (FC snap 后 Date 对象 → 'YYYY-MM-DDTHH:MM:SS' 兼容后端)
    //
    // ── v0.17.57 课表态: info.event.start 是**名义**行起点, 必须换回真实钟点 ──
    //   规则只有一条 (老板拍板): 拖到第 N 行 ⇒ start_at = 第 N 段的 start_time。
    //   **时长保持不变** —— 拖动只改「什么时候开始」, 不改这节课有多长 (课的时长归课自己管,
    //   时间段是纯标记, 不约束时长)。
    //   所以结束时间 = 新真实开始 + **原来那节课的真实时长**, 而不是新时间段的末尾。
    //   (若按时间段末尾算, 一节 30 分钟的课拖到「09:00–12:00」那行会瞬间变成 3 小时课。)
    const nominalStart = info.event.start;
    let newStart = nominalStart;
    let newEnd = info.event.end || new Date(nominalStart.getTime() + this.slotMin() * 60000);
    // v0.17.62 — 拖到第 N 行 ⇒ start_at = 第 N 段的 start_time, **并且**把该段的 node id 一起带上。
    //   只改 start_at 不够: 时间段允许重叠后, 同一时刻可能落在好几段里,
    //   后端拿不到 node id 就只能反推, 可能认成**别的**那一行 (存进 A、画在 B)。
    let draggedNodeId = null;
    if (this.hasSchedule()) {
      const row = this.scheduleRowIndexOfDate(nominalStart);
      if (row != null) {
        const node = this.scheduleNodes[row];
        draggedNodeId = node && node.id != null ? +node.id : null;
        const day = new Date(nominalStart.getFullYear(), nominalStart.getMonth(), nominalStart.getDate(), 0, 0, 0, 0);
        const realStartMin = this._hhmmToMinutes(node.start_time);
        const durMin = Math.max(1, Math.round((zsParseLocal(lesson.end_at) - zsParseLocal(lesson.start_at)) / 60000));
        newStart = new Date(day.getTime() + realStartMin * 60000);
        newEnd = new Date(newStart.getTime() + durMin * 60000);
      } else {
        // 落点行下标越界 (理论上不会: FC 的 slotMaxTime 就是最后一行) —— 宁可不发请求,
        //   也不能把一条名义时间 (01:00) 写进库, 那种数据在任何地方都解释不通。
        info.revert();
        return;
      }
    }
    const startStr = this.formatDateTimeLocal(newStart);
    const endStr = this.formatDateTimeLocal(newEnd);

    // 2. 拼新 room_id (FC resource 是 drag 时鼠标落点的列, 也可能不变)
    //    info.newResource.id 格式 'r_<roomId>', strip 'r_' 前缀拿原生 id
    const newResourceId = info.newResource ? String(info.newResource.id).replace(/^r_/, '') : String(lesson.room_id);
    const newRoomId = parseInt(newResourceId, 10) || lesson.room_id;

    // 4. v0.8.11 — eventAllow 兜底: FC 放行了但实际跟预留重叠 → 强制回滚
    //   必须放在 newStart/newEnd/newRoomId 都算完之后 (拼 payload 之前)。
    //   v0.8.11b 补实测结论: eventAllow 返回 false 时 **eventDrop 根本不触发** (FC 直接判非法弹回),
    //   所以正常路径永远走不到这一段 —— **第 1 层 eventAllow 才是主防线, 这道是冗余保险**,
    //   不是等价的第二道拦墙, 排障时别指望在这里看到日志。
    //   它留下的价值是: 哪怕哪天 eventAllow 因为 FC 行为变化 / dropInfo 形状变化而空转,
    //   也不会出现"约课卡静静压在预留块上"这种脏数据 (注意: 那种失效是**静默的, 不报错**)。
    if (this.overlapsReservation(newStart.getTime(), newEnd.getTime(), newRoomId)) {
      info.revert();
      if (this.attrs && this.attrs.flashError) {
        this.attrs.flashError('该时段已预留, 不能放课');
      }
      return;
    }

    // 5. PATCH payload (含所有 LessonConflictChecker 校验必要字段)
    //    school_id / coach_id / student_id / student_card_id 都没变, 但 conflict checker 需要它们
    //    (UpdateLessonController L41-49 merged 数组 ?? $l->xxx fallback, 多传不会改)
    const payload = {
      school_id: +lesson.school_id,
      room_id: newRoomId,
      coach_id: +lesson.coach_id,
      student_id: +lesson.student_id,
      student_card_id: lesson.student_card_id ? +lesson.student_card_id : null,
      start_at: startStr,
      end_at: endStr,
      // v0.17.62 — 拖到哪一行就把那一行的节点 id 带上 (口径与上面 start_at 同一个 node)。
      //   没配课表时 draggedNodeId 恒为 null ⇒ 后端照旧按 start_at 反推, 行为不变。
      schedule_node_id: draggedNodeId
    };

    // ── v0.9.0 拖拽分流 (决策 A3) ────────────────────────────────────────
    //   正常约课模式拖固定课块 → **仅改这一次** (走 lessons/{id} PATCH, 后端盖
    //     occurrence_overridden_at 标记, 后续模板级联不会把这行改回去)
    //   固定课模式拖固定课块   → **改当前日期之后全部** (走 fixed-lessons/series/move)
    //
    // ⚠️ 为什么固定模式必须走后端专用端点而不是同一个 PATCH:
    //     物化器只 INSERT 不 UPDATE, 课次的时间是**创建那一刻**从模板拷过去的。
    //     只改课次不改模板 → 已生成的变了、下次物化新生成的还是旧时间,
    //     课表上同时出现两个时间的「每周同一节课」, 且**不报任何错**。
    //     (这就是任务书点名的「最容易漏的坑」)
    if (this.isFixedLesson(lesson) && this.calendarMode === 'fixed') {
      const occDate = lesson.occurrence_date || this.localDateStr(newStart);
      Api.moveFixedSeries({
        fixed_lesson_id: lesson.fixed_lesson_id,
        date: occDate,
        start_at: startStr,
        end_at: endStr,
        room_id: newRoomId,
        // v0.18.40 — 拖到哪一行就把那一行的节点 id 带上 (与上面 payload.schedule_node_id
        //   同一个 draggedNodeId, 口径完全一致)。
        //   不带的话模板会继续声称自己属于**原来**那一档 ⇒ 固定课模式拖完之后,
        //   课次挂在没拖过去的那一行上 (前端按 node id 找行)。
        //   没配课表时 draggedNodeId 恒为 null ⇒ 后端把模板的节点 id 清空,
        //   回到「按 start_at 反推」的兼容路径, 行为不变。
        schedule_node_id: draggedNodeId
      }).then(res => {
        const attrs = res && res.data && res.data.attributes || {};
        const n = attrs.cascade && attrs.cascade.updated || 0;
        const conflicts = attrs.conflicts || [];
        lesson.start_at = startStr.replace('T', ' ');
        lesson.end_at = endStr.replace('T', ' ');
        lesson.room_id = newRoomId;
        // ⚠️ 模板时间变了 → 本地这节课不再是「单次例外」, 要清掉标记,
        //    否则前端下一次渲染还会给它打「仅本次」红角标 (后端的标记已被级联吸收)
        lesson.is_overridden = false;
        this._toast('整个固定课系列已改到 ' + startStr.slice(11, 16) + '（共 ' + n + ' 次）', 'success');
        this.load();
      }).catch(e => {
        info.revert();
        const msg = errReason(e, '移动失败');
        this._toast('移动失败: ' + msg, 'error');
      });
      return;
    }

    // 6. 调 API + 成功失败分支 (跟 v0.6.7 LessonEditorModal.submit 同 Promise 模式)
    Api.update('lessons', lesson.id, payload).then(() => {
      // 成功 → 同步本地 lesson 数据 (FC event 视觉已在新位置, 但 this.lessons 缓存还是老的,
      //   不更新会导致下次走 openBooking (菜单 onEdit) 拿到 stale start_at/end_at/room_id)
      const wasFixed = this.isFixedLesson(lesson);
      lesson.start_at = startStr.replace('T', ' ');
      lesson.end_at = endStr.replace('T', ' ');
      lesson.room_id = newRoomId;
      // v0.9.0 — 拖了固定课的某一次 → 本地立即打成「单次例外」,
      //   否则这一帧还显示「每周」角标, 用户会以为拖动没生效
      if (wasFixed) lesson.is_overridden = true;
      m.redraw();
      // v0.9.1 阻塞项 2 (辉哥 V 测): 原来这里读 this.attrs.flashSuccess, 但
      //   SchedulerIndexPage 渲染 CalendarTab 时**只传了 activeSchoolId**
      //   (`<CalendarTab activeSchoolId={this.activeSchoolId} />`), 从来没传过
      //   flashSuccess → 这段是**永远不执行的死代码**, 拖拽成功零反馈 (静默操作)。
      //   改走本仓既有 toast: SlotContextMenu.toast() (见 v0.7.9e 注释 —
      //   Flarum 2.0 没有 app.flash, 扩展里统一用自绘 toast)。
      // v0.9.0 语义保留: 固定课在正常模式下拖 = 仅改这一次, 明确告诉用户只改了这一次
      //   (决策 A3; 不说清楚的话用户会以为整周都挪了, 然后下周发现课还在原地)。
      this._toast(wasFixed ? '已改 ' + startStr.slice(11, 16) + ' · 仅这一次（每周同步请到固定课模式）' : '已移动课程到 ' + startStr.slice(11, 16), 'success');
    }).catch(e => {
      // 失败 (422 冲突 / 403 越权 / 网络) → 视觉回滚到原位置
      info.revert();
      const msg = errReason(e, '移动失败');
      this._toast('移动失败: ' + msg, 'error');
    });
  }

  // v0.6.43 — Date → 'YYYY-MM-DDTHH:MM:SS' 转换 (跟 LessonEditorModal.submit L474-475 同款, 抽 helper 复用)
  //   后端 CarbonImmutable::parse 能解析 'YYYY-MM-DDTHH:MM:SS' 无空格格式 (跟 'YYYY-MM-DD HH:MM:SS' 等价)
  //   返回本地时间 (getHours/getMinutes 用本地时区), 跟 Lesson.create / Lesson.update 现有逻辑一致
  formatDateTimeLocal(d) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${day}T${hh}:${mm}:00`;
  }

  // v0.6.7 — 桌面 dblclick + 移动 2-tap 触发入口 (辉哥 9/26 20:44 拍板)
  // v0.6.8 — 区分空格子 vs 已有 lesson (辉哥 9/27 拍板)
  // v0.6.8a — V 测 FAIL fix: fcEvent 路径用 data-lesson-id 读 lesson, 不用 title 反查 + slot top 匹配
  // v0.8.12 (辉哥 10/7 16:22 拍板) — 桌面 dblclick + 移动 2-tap 两条**直连 openBooking** 的路径已移除。
  // v0.10.2 (辉哥 10/8 拍板) — 移动端入口改回**双击时间格**。
  //   ⚠️ 完整历史 (读之前先看完, 免得把这三代混在一起):
  //     v0.6.7   移动 2-tap (300ms 同槽) → **直接 openBooking**, 且监听器是
  //              「非 passive + preventDefault」⇒ 阻断了 FullCalendar 横滑和页面滚动, 课表不能滑。
  //     v0.7.9e  引入长按 500ms 菜单 (passive, 位移 >10px 取消) → openSlotMenu
  //     v0.8.12  删掉 2-tap, 理由「同源第二条路径, 白担维护成本」—— 删的是**直连 openBooking** 的那条
  //     v0.10.2  辉哥要求把双击加回来, 但口径是**双击 → openSlotMenu (菜单)**,
  //              **不是**回退到 v0.6.7 的「直接开约课 modal」。同时修掉 v0.6.7 的
  //              passive 缺陷: 本次全部 passive, 不抢任何手势。
  //
  //   函数名 bindBookingTriggers 保持不变 (改名会牵连 SlotContextMenu/拖拽/多处置顶注释, 收益不抵风险)。
  //   现语义 (v0.10.2): 桌面右键 → 菜单; 移动双击 → **同一个菜单**。
  //   _resolveSlotTarget + openBooking 都保留, 菜单的 onBook/onEdit 回调还在用, 别当死代码清掉。
  bindBookingTriggers(el) {
    if (!el) return;
    if (this._bookingTriggersBound && this._bookingTriggersBoundEl === el) return;
    this._bookingTriggersBound = true;
    this._bookingTriggersBoundEl = el;
    const self = this;

    // ── v0.10.2 入口总览 (改之前先读这段) ──
    //   桌面: contextmenu → openSlotMenu            (v0.7.9e 起, 本版一行没动)
    //   移动: 双击时间格 → openSlotMenu             (v0.10.2 新, 全 passive)
    //   两端触发的是**同一个** openSlotMenu, 菜单项随槽位状态切换。
    //   eventClick 单击路径早在 v0.7.9e 就删了, 打开约课/编辑约课**只剩菜单一条路**。
    //   ⚠️ data-resource-id (slotLabelDidMount 挂) / data-lesson-id (eventDidMount 挂) 看着
    //     像只服务双击, 其实 _resolveSlotTarget 也在读 → 菜单路径复用, **不可清**。

    // ── v0.7.9e (辉哥 10/5 拍板) 桌面: 右键菜单 ──
    //   preventDefault 拦掉浏览器原生右键菜单, 换成我们的 SlotContextMenu。
    el.addEventListener('contextmenu', e => {
      const hit = self._resolveSlotTarget(e.target, e.clientX);
      if (!hit) return;
      e.preventDefault();
      // v0.17.77 — 改法 (v0.17.76 是「学员 preventDefault 完直接 return, 其他人照弹」):
      //   现在**统一** preventDefault, 不再按角色分叉, 然后交给 openSlotMenu 按权限位出项。
      //   · preventDefault 保留: 本仓这套 fc 一直拦原生右键 (v0.7.9e 起), 学员/员工
      //     单独分叉会变成「别的 tab 右键弹我们菜单、某人右键弹浏览器菜单」, 交互不一致。
      //     D4 也要求「不能有原生浏览器右键菜单冒出来」。
      //   · 菜单出不出现, 由 openSlotMenu 里的权限位 gate 决定; 一项都没有时
      //     _closeSlotMenuIfEmpty() 会立刻收掉, 表现就是「右键没反应」——
      //     这正是零权限用户应该看到的结果。
      //   ⚠️ openSlotMenu 内部是单点收口 (桌面右键 / 移动双击两条入口都走它),
      //     这里**不要**再加任何角色判定, 否则又是两处会漂移的副本。
      self.openSlotMenu(hit, e.clientX, e.clientY);
    });

    // ── v0.10.2 (辉哥 10/8 拍板) 移动: 长按 500ms → **双击时间格** ──
    //
    // 【辉哥原话】「课表日历 tab 中的 fc 课表，移动端时间块不再用长按触发右键菜单，
    //   还是用双击时间格来触发」
    //
    // 【口径 —— 三条, 别搞错】
    //   ① 触发的是**同一个** openSlotMenu (跟长按走同一条路), **不是**回退到
    //      v0.6.7 的「直接开约课 modal」。菜单项仍随状态切换 (空槽=[约课] / 已有=[修改][取消])。
    //   ② **桌面端 contextmenu 一行没动** —— 它在 L1548, 与本段完全独立。
    //   ③ 触发的仍是同一个 openSlotMenu, 桌面/移动只是入口不同。
    //
    // 【为什么必须自己实现 (移动端没有原生 dblclick)】
    //   touch 设备不派发 dblclick 事件, 只能用 touchstart + 计时器模拟。
    //
    // 【⚠️ 三条手势纪律 —— 本段最容易回归的地方, 改之前先读完】
    //
    //   纪律 1: **全部 passive:true, 一律不调 preventDefault**。
    //     v0.6.7 那版 2-tap 就是用「非 passive + preventDefault」写的, 结果阻断了
    //     FullCalendar 自己的横向滑动 (滑教室列) 和页面滚动 —— 课表直接不能滑。
    //     本段所有监听器都是 passive, 只**观察**手势, 从不取消它。
    //
    //   纪律 2: **不碰 pointer 事件**。
    //     v0.8.11 修拖拽 bug 时, 因为「劫持 pointer 事件」被否决过一次
    //     (症状: 拖不过去 + 松手回弹)。本段只加 touchstart/touchend/touchmove,
    //     不 addEventListener('pointer*'), 不改既有 pointer 逻辑, v0.9.x 拖拽零影响。
    //
    //   纪律 3: **任何不确定的情况都退化成普通单击**, 绝不吞事件。
    //     第二次 touch 落在别处 / 超时 / 位移 > 10px (判为横滑或拖拽) / 多指
    //     → 全部清状态并 return, 什么都不做。
    //
    // 【双击判定】
    //   第二次 touchstart 落在**同一格** (hit 相同) 且距第一次抬手 < 300ms → 触发菜单。
    //   300ms 是取值理由: 人类双击的间隔 p95 约 250-300ms, 再长会跟"两次独立点击"混淆;
    //   再短则在低端机上容易因主线程卡顿而误判。双击后清空 firstTap, 避免三击连开两次。
    let tapTimer = null; // 第一次抬手后的等待窗口计时器
    let firstTap = null; // { hit, x, y, time }
    let tpStart = null; // 当前这次按下的起点 (判位移用)
    let tapFired = false; // 双击已成立 → 吞掉紧随的 click

    const clearTap = () => {
      if (tapTimer) {
        clearTimeout(tapTimer);
        tapTimer = null;
      }
      firstTap = null;
    };

    /**
     * v0.10.2a — 判定两次 touchstart 是否落在**同一格**
     *
     * ⚠️ v0.10.2 原实现最后一行是 `return true`, 而上面的注释却写着「空槽对比坐标 (x/y)」
     *   —— **代码从来没有比较过任何 x/y**。注释描述的是代码没做的事, 直接把人带偏。
     *   后果实测: 教室A 空槽点一下 → 200ms 内教室B 空槽同高度再点 → **菜单弹出, 标题是 B**。
     *   即任意两个空槽互为同格 (v0.10.2 空槽恒 true)。
     *   (同列跨格没暴露, 是被 touchmove 的 10px 阈值兜住的, 不是本函数生效 —— 那是假象。)
     *
     * 【判据 = (resourceId, dataTime) 组合, 为什么不比较 x/y】
     *   _resolveSlotTarget (同文件 L1743) 本来就返回 { resourceId, dataTime, lesson, rsv, fcEvent },
     *   这两个字段一直在包里躺着没人用 —— 判据现成, 不需要改函数签名, 也不需要像素级容差。
     *   「教室列 + 时间格」正是用户心里那格课的唯一定义, 与像素/缩放/DPR 全部无关。
     *
     *   resourceId 的来源: lane 上的 data-resource-id → 事件块 data-resource-id → 所在列
     *     → 最后 _resourceIdAtX(clientX) 几何兜底。⚠️ 实测 v0.7.8q 引入 scrollgrid 后
     *     slotLabelDidMount 的 cellIndex 映射已失效, **19 个 lane 上 0 个带 data-resource-id**,
     *     空槽实际靠 _resourceIdAtX 几何兜底 —— 这条兜底链能正确定位到被点的那一列。
     *   dataTime 的来源: lane 的 data-time。⚠️ 实测 **19/19 个 lane 都带 data-time**, 不会缺。
     *
     * 【null 退化: 任一缺失 → 判异格 (返回 false), 绝不猜成同格】
     *   两个都 null 的话, 朴素写法 `a.resourceId === b.resourceId && a.dataTime === b.dataTime`
     *   会因为 `null === null` 判成**同格** → 退化成 v0.10.2 那个 bug, 换个形式复发。
     *   这里在比较之前先挡一道: 身份不明就当不同格, 退化成普通单击 (纪律 3)。
     *   方向是**宁可漏弹不可错弹** —— 漏弹的代价是"用户再点一次", 错弹的代价是"在 B 教室
     *   打开了菜单并可能误操作", 两者不对称。
     *   实际触发概率极低: 走到这里的 hit 必然过了 `if (!slot && !lesson && !rsv) return null`,
     *   空槽的 data-time 又实测 19/19 都在; resourceId 只在点到时间轴/列间缝隙时才会缺。
     */
    const isSameHit = (a, b) => {
      if (!a || !b) return false;
      // 有约课 → 比 lesson.id; 有预留块 → 比 rsv.id (不同对象就是不同格)
      if (a.lesson && b.lesson) return Number(a.lesson.id) === Number(b.lesson.id);
      if (a.rsv && b.rsv) return Number(a.rsv.id) === Number(b.rsv.id);
      // 一边有内容一边没有 → 视为不同格 (点空白 vs 点已排课)
      if (!!a.lesson !== !!b.lesson) return false;
      if (!!a.rsv !== !!b.rsv) return false;
      // 到这里两边都是空槽 → 判据是 (教室列, 时间格); 缺任一 → 身份不明, 判异格
      if (!a.resourceId || !b.resourceId) return false;
      if (!a.dataTime || !b.dataTime) return false;
      return a.resourceId === b.resourceId && a.dataTime === b.dataTime;
    };
    el.addEventListener('touchstart', e => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      const hit = self._resolveSlotTarget(e.target, t.clientX);
      if (!hit) return;
      // 多指 (双指缩放) 不参与双击判定
      if (e.touches.length > 1) {
        clearTap();
        tapFired = false;
        return;
      }
      tpStart = {
        x: t.clientX,
        y: t.clientY
      };
      self._showRipple(t.clientX, t.clientY);
      const now = Date.now();
      if (firstTap && isSameHit(firstTap.hit, hit) && now - firstTap.time < 300) {
        // ── 双击成立 ──
        clearTap();
        tpStart = null;
        tapFired = true;
        if (navigator.vibrate) {
          try {
            navigator.vibrate(15);
          } catch (err) {/* noop */}
        }
        self.openSlotMenu(hit, t.clientX, t.clientY);
      }
      // 否则: 记为"第一次", 开窗等待可能的第二次
    }, {
      passive: true
    });

    // 第一次抬手 → 开 300ms 窗口。窗口内没有第二次 touchstart 就自然过期。
    el.addEventListener('touchend', e => {
      const wasTap = !tapFired;
      tpStart = null;
      if (tapFired) {
        // 双击已触发 → 吞掉这次 click, 防止穿透到课块/父元素
        tapFired = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (wasTap && !firstTap) {
        // 第一次抬手: 用抬手位置重新解析一次 hit (抬手时坐标比按下时准)
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const hit = self._resolveSlotTarget(e.target, t.clientX);
        if (!hit) return;
        firstTap = {
          hit,
          x: t.clientX,
          y: t.clientY,
          time: Date.now()
        };
        if (tapTimer) clearTimeout(tapTimer);
        tapTimer = setTimeout(() => {
          tapTimer = null;
          firstTap = null;
        }, 300);
      }
    }, {
      passive: false
    });
    el.addEventListener('touchmove', e => {
      if (!tpStart) return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      // 手指移动超过 10px → 判定为横滑/滚动/拖拽, 取消双击判定 (退化成普通单击)
      if (Math.abs(t.clientX - tpStart.x) > 10 || Math.abs(t.clientY - tpStart.y) > 10) {
        clearTap();
        tpStart = null;
      }
    }, {
      passive: true
    });
    el.addEventListener('touchcancel', () => {
      clearTap();
      tapFired = false;
      tpStart = null;
    }, {
      passive: true
    });
  }

  /**
   * v0.7.9e — 把事件目标解析成 SlotContext (含空槽 / 已有约课两种情况)
   * 最早是从 v0.6.7 dblclick 里的解析逻辑抽出来的, 后来右键 + 长按也复用。
   * ⚠️ v0.8.12 — dblclick / 2-tap 已移除, **当前唯一调用方是右键菜单和长按菜单**
   *   (bindBookingTriggers 里仅剩的这两个监听器), 千万别当死代码删。
   * @returns {?{resourceId:?string, dataTime:?string, lesson:?object, fcEvent:?HTMLElement}}
   */
  _resolveSlotTarget(target, clientX) {
    if (!target) return null;
    const fcEvent = target.closest ? target.closest('.fc-event') : null;
    // v0.8.4 — 区分 fc-event 是不是预留块 (.zs-reserve)
    const isReservation = fcEvent && fcEvent.classList.contains('zs-reserve');
    let slot = null;
    let lesson = null;
    let rsv = null; // v0.8.4

    if (fcEvent) {
      if (isReservation) {
        // v0.8.4 — 预留块: 从 data-rsv-id 反查
        const rsvId = fcEvent.getAttribute('data-rsv-id');
        if (rsvId) rsv = (this.slotReservations || []).find(r => String(r.id) === rsvId) || null;
        // 用 rsv 的 room_id 找列 (而不是 fcEvent.dataset, 跟 v0.7.9e lesson 同款兜底)
        if (rsv) {
          const col = this._findColByRoomId(rsv.room_id);
          if (col) {
            const rId = col.getAttribute('data-resource-id');
            slot = document.querySelector('.fc-timegrid-slot-lane[data-resource-id="' + rId + '"]');
          }
        }
        if (!slot) slot = fcEvent;
      } else {
        const lessonId = fcEvent.getAttribute('data-lesson-id');
        if (lessonId) lesson = this.lessons.find(l => String(l.id) === lessonId) || null;
        const col = fcEvent.closest('.fc-timegrid-col[data-resource-id]');
        if (col) {
          const rId = col.getAttribute('data-resource-id');
          slot = document.querySelector('.fc-timegrid-slot-lane[data-resource-id="' + rId + '"]');
        }
        if (!slot) slot = fcEvent;
      }
    } else {
      slot = target.closest ? target.closest('.fc-timegrid-slot-lane') : null;
    }
    if (!slot && !lesson && !rsv) return null;

    // v0.7.9e — resourceId 兜底链 (详见 eventDidMount 的踩坑注释)
    //   ① lane 上的 data-resource-id
    //   ② 事件块自己的 data-resource-id (已约课路径, eventDidMount 注入)
    //   ③ 事件块所在列
    //   ④ 按鼠标横坐标落在哪一列 (空槽路径唯一可行的办法, 见 _resourceIdAtX)
    let resourceId = slot ? slot.getAttribute('data-resource-id') : null;
    if (!resourceId && fcEvent) resourceId = fcEvent.getAttribute('data-resource-id');
    if (!resourceId && fcEvent) {
      const c2 = fcEvent.closest('.fc-timegrid-col[data-resource-id]');
      if (c2) resourceId = c2.getAttribute('data-resource-id');
    }
    if (!resourceId && rsv) {
      const c2 = this._findColByRoomId(rsv.room_id);
      if (c2) resourceId = c2.getAttribute('data-resource-id');
    }
    if (!resourceId && typeof clientX === 'number') resourceId = this._resourceIdAtX(clientX);

    // dataTime: 已约课时 lane 查不到, 从 lesson.start_at 反推 'HH:MM' (菜单"修改约课"要用)
    let dataTime = slot ? slot.getAttribute('data-time') : null;
    if (!dataTime && lesson && lesson.start_at) dataTime = String(lesson.start_at).slice(11, 16);
    if (!dataTime && rsv && rsv.start_at) dataTime = String(rsv.start_at).slice(11, 16);
    return {
      resourceId,
      dataTime,
      lesson,
      rsv,
      fcEvent
    };
  }

  // v0.8.4 — 给 SlotReservationRenderer 共用的 helper
  _findColByRoomId(roomId) {
    const cols = document.querySelectorAll('.fc-timegrid-col[data-resource-id="r_' + roomId + '"]');
    return cols[0] || null;
  }

  /**
   * v0.7.9e — 按横坐标反查落在哪个教室列
   *
   * ⚠️ 为什么不靠 cellIndex (v0.6.8 老实现的思路):
   *   老实现在 slotLabelDidMount 里拿 axis 格的 cellIndex 去索引 .fc-timegrid-col 列表。
   *   v0.7.8q 引入 @fullcalendar/scrollgrid (dayMinWidth 横向滚动) 之后**彻底失效**, 实测:
   *     axis 表  : TD.fc-timegrid-slot-label.fc-scrollgrid-shrink[ci=0]   ← 只有时间轴一列
   *     slots 表 : TD.fc-timegrid-slot-lane[ci=0]                          ← 也只有一列
   *     资源列   : .fc-timegrid-cols > TABLE > TD.fc-timegrid-col[data-resource-id]
   *   三者在**不同的表**里, cellIndex 全是 0, 没有任何可用的索引对应关系。
   *   后果: 老实现 28 个 lane 一个 data-resource-id 都没打上 → 空槽约课时教室永远是空的。
   *   修法: 直接用布局坐标判断, 不依赖 FC 的内部 DOM 结构 (换回 SimpleScrollGrid 也照样能用)。
   */
  _resourceIdAtX(x) {
    try {
      const cols = document.querySelectorAll('.fc-timegrid-col[data-resource-id]');
      for (let i = 0; i < cols.length; i++) {
        const r = cols[i].getBoundingClientRect();
        if (x >= r.left && x <= r.right) return cols[i].getAttribute('data-resource-id');
      }
    } catch (e) {/* noop */}
    return null;
  }

  /** v0.7.9e — 长按涟漪 (移动端反馈) */
  _showRipple(x, y) {
    try {
      const r = document.createElement('div');
      r.className = 'ZS-ctxmenu-ripple';
      r.style.left = x + 'px';
      r.style.top = y + 'px';
      document.body.appendChild(r);
      setTimeout(() => {
        if (r.parentNode) r.parentNode.removeChild(r);
      }, 520);
    } catch (e) {/* noop */}
  }

  /**
   * v0.7.9e — 打开槽位菜单 (桌面气泡 / 移动底部面板, 由组件内部按 UA 判定)
   * 菜单项随状态切换: 空槽=[约课] 已有约课=[修改约课, 取消约课]
   */
  openSlotMenu(hit, x, y) {
    // v0.17.77 (辉哥 10/16 拍板) — 菜单项改成**按权限位**逐项 gate
    //
    // 【v0.17.76 在这里是 `if (this.isStudentView()) return;` 整体 return】
    //   辉哥这轮的原话是「在后台加上约课权限的选择, 有权限的用户组可以约课、编辑已约课程、
    //   取消约课」—— 一刀切 return 把「有权限的学员」和「没权限的员工」一起挡掉了,
    //   跟需求正好相反。本轮删掉那一行, 改成下面逐项 gate:
    //     约课                      ← canBookLessons()
    //     修改约课 / 改这一次        ← canEditBookedLessons() || canManageLessons()
    //     取消约课 / 取消这一次      ← canCancelBookedLessons() || canManageLessons()
    //     预留 / 取消预留 / 添加固定课 / 检查排课 ← canManageLessons() (重权限位, 不放宽)
    //
    // 【为什么 manageLessons 要 OR 进「修改/取消」两项】
    //   后端 UpdateLessonController 里 manageLessons 是**永远 bypass** 的最高档。
    //   前端若只认新位, 就会让 Mod/Admin「原来能改的课现在菜单里改不了」—— 那是本轮
    //   明令禁止的回归 (D3)。两边口径必须一致: 前端给位, 后端就一定能过。
    //
    // 【SlotContextMenu 不用改】
    //   v0.17.76 已把它改成「菜单项由 handler 驱动, 没传就不画」(桌面 _buildBubble 与
    //   移动 _buildSheet 两半都改了), 所以本轮只要**不传**对应 handler, 那项就不出现。
    const lesson = hit.lesson || null;
    // v0.8.4 — 预留上下文 (右键预留块 → "取消预留" 菜单项)
    const rsv = hit.rsv || null;
    const resourceIdStr = hit.resourceId ? String(hit.resourceId).replace(/^r_/, '') : '';
    let room = resourceIdStr ? this.rooms.find(r => String(r.id) === resourceIdStr) : null;
    if (lesson) {
      const lessonRoom = this.rooms.find(r => String(r.id) === String(lesson.room_id));
      if (lessonRoom) room = lessonRoom;
    } else if (rsv) {
      // v0.8.4 — 预留的教室从 rsv.room_id 兜底拿
      const rsvRoom = this.rooms.find(r => String(r.id) === String(rsv.room_id));
      if (rsvRoom) room = rsvRoom;
    }

    // 时间段显示
    let timeRange = '';
    // 补零 helper (v0.7.9e 实测踩坑: 之前只在 if 分支里定义, else 分支用不到 → ReferenceError)
    const p2 = n => String(n).padStart(2, '0');
    if (lesson) {
      const s = new Date(lesson.start_at);
      const e2 = new Date(lesson.end_at);
      timeRange = p2(s.getHours()) + ':' + p2(s.getMinutes()) + ' - ' + p2(e2.getHours()) + ':' + p2(e2.getMinutes());
    } else if (rsv) {
      // v0.8.4 — 预留按 rsv 的真实起止
      const s = new Date(rsv.start_at);
      const e2 = new Date(rsv.end_at);
      timeRange = p2(s.getHours()) + ':' + p2(s.getMinutes()) + ' - ' + p2(e2.getHours()) + ':' + p2(e2.getMinutes());
    } else if (hit.dataTime) {
      // v0.17.57 — 空格子: 显示该**时间段**的真实区间 ('09:00 - 12:00'), 不是名义的 01:00 - 02:00。
      //   右键菜单标题里的这段文字是运营判断「我现在点的是哪一段」的唯一依据, 显示名义时间
      //   等于什么都没说。兜底态下 realTimeForSlotTime 原样返回, 行为不变。
      const node = this.scheduleNodeForSlotTime(hit.dataTime);
      if (node) {
        timeRange = String(node.start_time).slice(0, 5) + ' - ' + String(node.end_time).slice(0, 5);
      } else {
        const parts = hit.dataTime.split(':');
        const sh = Number(parts[0]);
        const sm = Number(parts[1] || 0);
        const endMin = sh * 60 + sm + this.slotMin();
        timeRange = p2(sh) + ':' + p2(sm) + ' - ' + p2(Math.floor(endMin / 60) % 24) + ':' + p2(endMin % 60);
      }
    }
    SlotContextMenu.setCursor(x, y);

    // v0.8.4 — 3 种状态: 已有预留 / 已有约课 / 空槽
    //   已有预留 → [取消预留] (辉哥 10/6 拍板, 单按钮菜单)
    //   已有约课 → [修改约课] [取消约课] (现有 v0.7.9e 逻辑)
    //   空槽     → [约课] [预留] (v0.8.4 加的预留项, 双按钮菜单)
    if (rsv) {
      SlotContextMenu.open({
        resourceId: hit.resourceId,
        dataTime: hit.dataTime,
        lesson: null,
        rsv,
        roomName: room ? room.name || '教室 ' + room.id : '',
        timeRange
      }, {
        onBook: null,
        onEdit: null,
        onCancel: null,
        onReserve: null,
        // v0.17.77 — 「取消预留」归 manageLessons (重权限位, 时段预留 4 个端点都归它)。
        //   学员视图进不到这里了 (isStudentView 的日历只读化仍在, 但菜单项按位走),
        //   没勾 manageLessons 的组即便右键点到预留块也只会看到一个空菜单。
        onUnreserve: this.canManageLessons() ? () => {
          SlotContextMenu.confirmUnreserve(rsv, () => this.unreserveSlot(rsv));
        } : null
      });
      this._closeSlotMenuIfEmpty();
      return;
    }

    // v0.17.77 — 权限位在 open 之前取一次, 下面每个 handler 三元里复用, 避免读 6 次 session。
    const canBook = this.canBookLessons();
    const canEdit = this.canEditBookedLessons() || this.canManageLessons();
    const canCancel = this.canCancelBookedLessons() || this.canManageLessons();
    const canManage = this.canManageLessons();
    SlotContextMenu.open({
      resourceId: hit.resourceId,
      dataTime: hit.dataTime,
      lesson,
      roomName: room ? room.name || '教室 ' + room.id : '',
      timeRange,
      // v0.9.0 — 菜单按模式分流: 固定课模式才有「添加固定课」和「检查排课」
      mode: this.calendarMode
    }, {
      onBook: canBook ? () => this.openBooking(hit.resourceId, hit.dataTime, hit.fcEvent, null) : null,
      onEdit: canEdit ? () => this.openBooking(hit.resourceId, hit.dataTime, hit.fcEvent, lesson) : null,
      onCancel: canCancel ? () => {
        // 辉哥 10/5 拍板: 取消约课加二次确认
        // v0.9.0: 固定课课次改用「取消这一次」文案并说明后续每周照常,
        //   走 PATCH status=cancelled + is_cancelled=1 (业务取消), **不是**软删 ——
        //   软删是「管理员误删」通道, 混用会导致回收站恢复把已请假的课复活。
        if (this.isFixedLesson(lesson)) {
          SlotContextMenu.confirmCancelOnce(lesson, () => this.cancelFixedOccurrence(lesson));
        } else {
          SlotContextMenu.confirmCancel(lesson, () => this.cancelLesson(lesson));
        }
      } : null,
      // v0.8.4 — 空槽新增「预留」菜单项 (manageLessons 重权限位, 本轮不放宽)
      onReserve: canManage ? () => this.openReserveModal(hit, room, timeRange) : null,
      // ── v0.9.0 新增两项 (只在固定课模式出现在菜单里, 同样归 manageLessons) ──
      onAddFixed: canManage ? () => this.openAddFixedLesson(hit, room) : null,
      onCheckFixed: canManage ? () => this.openFixedCheck() : null
    });
    this._closeSlotMenuIfEmpty();
  }

  /**
   * v0.17.77 — 菜单打开后若「一项可点项都没有」, 立刻收掉。
   *
   * 【为什么不用「算一遍有几项再决定开不开」】
   *   那需要把 SlotContextMenu 的出项规则 (rsv 分支 / lesson 分支 / 空槽分支 /
   *   fixed 模式的 onAddFixed+onCheckFixed / onBook&&onReserve 才出「预留」) 在这里
   *   **抄第二遍** —— 抄一遍就多一个会漂移的副本, 将来加菜单项极易只改一处。
   *   这里改成「先让 SlotContextMenu 按它自己的规则渲染, 再数 DOM」, 规则永远只有一份。
   *
   * 【类名口径】桌面气泡项 = .ZS-ctxmenu-item, 移动底部面板项 = .ZS-ctxmenu-sitem,
   *   两者都挂在 .ZS-ctxmenu-root 下 (SlotContextMenu._ensureRoot / _item / _sheetItem)。
   *
   * 【为什么不是 404/报错】零权限用户右键一个格子, 不该看到任何菜单 (含浏览器原生菜单,
   *   那条由 contextmenu 绑定的 preventDefault 保证), 静默什么都不发生就是正确表现。
   */
  _closeSlotMenuIfEmpty() {
    const root = document.querySelector('.ZS-ctxmenu-root');
    if (!root) return;
    const items = root.querySelectorAll('.ZS-ctxmenu-item, .ZS-ctxmenu-sitem');
    if (items.length === 0) SlotContextMenu.close();
  }

  // v0.9.0 — 打开「添加固定课」modal
  openAddFixedLesson(hit, room) {
    if (!this.activeSchoolId || !hit.dataTime) return;
    const roomId = parseInt(String(hit.resourceId || '').replace(/^r_/, ''), 10);
    const targetRoomId = roomId || room && room.id;
    if (!targetRoomId) return;
    // v0.17.57 — 同 openBooking: 格子时间是名义的, 换回该时间段的真实 start_time
    const realTime = this.realTimeForSlotTime(hit.dataTime);
    const parts = realTime.split(':');
    const sh = Number(parts[0]);
    const sm = Number(parts[1] || 0);
    const node = this.scheduleNodeForSlotTime(hit.dataTime);
    // 有课表时结束时间取该**时间段**的末尾 (固定课是模板, 时长跟时间段走);
    const endMin = node ? this._hhmmToMinutes(node.end_time) : sh * 60 + sm + this.slotMin();
    // v0.18.39 — 改成 mithril Modal 子类 (app.modal.show), 见文件头那条注释。
    //   ⚠️ scheduleNodes 一并递进去: 弹窗里的「时间段下拉」必须跟用户**眼前那张网格**
    //   是同一版课表 —— 本组件 load() 拉课表时带了 date (v0.17.85), 拿到的正是
    //   「这一天生效的那一版」。弹窗自己再拉一次 (不传 date) 会拿到别的版本 ⇒ 分叉。
    app.modal.show(FixedLessonModal, {
      schoolId: this.activeSchoolId,
      rooms: this.rooms,
      roomId: targetRoomId,
      dateStr: this.activeDateStr(),
      startTime: String(sh).padStart(2, '0') + ':' + String(sm).padStart(2, '0'),
      endTime: String(Math.floor(endMin / 60) % 24).padStart(2, '0') + ':' + String(endMin % 60).padStart(2, '0'),
      dayOfWeek: this.fixedDayOfWeek,
      selectedDays: [this.fixedDayOfWeek],
      scheduleNodes: this.scheduleNodes,
      // v0.18.40 — 把「用户点的是哪一行」显式递进弹窗。
      //   上面那个 node 就是**落点那一行**的课表节点, 弹窗据此:
      //     · 预填 this.scheduleNodeId (不再靠 startTime 反查 —— 反查在重叠区会认成前一档);
      //     · 提交时带上 schedule_node_id ⇒ 后端落库 ⇒ 物化出的课次也带着它
      //       ⇒ 日历把课画在用户选的那一行, 而不是前一档。
      scheduleNodeId: node ? node.id : null,
      onDone: () => {
        SlotContextMenu.toast('固定课已创建, 课次已排入本月+下月', 'success');
        this.load();
      }
    });
  }

  // v0.9.0 — 打开「检查排课」
  openFixedCheck() {
    if (!this.activeSchoolId) return;
    FixedCheckModal.open({
      schoolId: this.activeSchoolId,
      onRefresh: () => this.load()
    });
  }

  /**
   * v0.9.0 — 取消固定课的「这一次」(业务取消, 不是软删)
   *
   * PATCH status='cancelled' → UpdateLessonController 会同时盖 is_cancelled=1 +
   * occurrence_overridden_at, 于是:
   *   · 槽位空出来 (冲突检测只查 status IN (scheduled, completed))
   *   · 检查排课能看到「被单次改过」分组
   *   · 模板级联不会把它改回 scheduled
   *   · 回收站里**不会出现**它 (没写 deleted_at) —— 不会跟「误删恢复」打架
   * 下周的课次照常生成 (模板还活着)。
   */
  async cancelFixedOccurrence(lesson) {
    try {
      await Api.update('lessons', lesson.id, {
        status: 'cancelled'
      });
      lesson.status = 'cancelled';
      lesson.is_cancelled = 1;
      lesson.is_overridden = true;
      SlotContextMenu.toast('已取消 ' + (lesson.occurrence_date || '') + ' 这一次 · 下周照常上课', 'success');
      this.load();
    } catch (e) {
      const msg = errReason(e, '取消失败');
      SlotContextMenu.toast('取消失败: ' + msg, 'error');
    }
  }

  // v0.8.4 — 打开预留 modal
  // v0.9.2 (辉哥 10/7 拍板) — 固定课模式**不提供**创建预留的入口, 这里再兜一道:
  //   菜单项已经摘掉了, 但 openReserveModal 是方法入口, 将来任何人加个按钮/快捷键调进来
  //   就会漏。宁可在这里明确拒绝, 也不要留一个「看不见预留却能建预留」的后门。
  //   走既有 toast 出口 (SlotContextMenu.toast), 不新造提示机制。
  openReserveModal(hit, room, timeRange) {
    if (this.calendarMode === 'fixed') {
      SlotContextMenu.toast('固定课模式下不能创建时段预留, 请切到「正常约课」模式', 'error');
      return;
    }
    if (!hit.resourceId || !hit.dataTime || !this.activeSchoolId) return;
    const roomId = parseInt(String(hit.resourceId).replace(/^r_/, ''), 10);
    if (!roomId) return;
    // v0.17.57 — 格子时间是**名义**的 (第 2 行 = '01:00:00'), 必须换回真实钟点,
    //   否则会写进一条 01:00 的预留 (不在任何时间段内, 后端会拒)。
    const node = this.scheduleNodeForSlotTime(hit.dataTime);
    const realTime = this.realTimeForSlotTime(hit.dataTime);
    // v0.18.43 — 显式带上「用户选了哪一档」(节点 id)。
    //   课表允许区间重叠时, 光有 start_at 反推不出归属 (10:30 会被判给前一档) ⇒ 必须记 id。
    //   拿不到节点 (没课表 / 越界) ⇒ null, 读侧回落到 start_at 反推。
    const nodeId = node && node.id != null ? +node.id : null;
    const parts = realTime.split(':');
    const sh = Number(parts[0]);
    const sm = Number(parts[1] || 0);
    // v0.9.0 — 收敛到 activeDateStr(): 固定课模式下这里必须用「本周选中星期几的那天」,
    //   否则会出现「表头显示 10/14, 点预留却建在 10/07」
    const dateStr = this.activeDateStr();
    const startAt = dateStr + ' ' + String(sh).padStart(2, '0') + ':' + String(sm).padStart(2, '0') + ':00';
    // 算 end_at: 有课表取该时间段的真实末尾, 兜底态按一格对齐 (老行为)
    const startDate = new Date(dateStr + 'T' + realTime);
    const endDate = node ? new Date(dateStr + 'T' + String(node.end_time).slice(0, 5)) : new Date(startDate.getTime() + this.slotMin() * 60000);
    const endAt = this._formatDateTimeLocal(endDate);
    const roomName = room ? room.name || '教室 ' + room.id : '';
    app.modal.show(ReservationModal, {
      reservation: {
        school_id: this.activeSchoolId,
        room_id: roomId,
        start_at: startAt,
        end_at: endAt,
        notes: '',
        schedule_node_id: nodeId
      },
      roomName,
      timeRange,
      onSubmit: async notes => {
        await Api.create('slot-reservations', {
          school_id: this.activeSchoolId,
          room_id: roomId,
          start_at: startAt,
          end_at: endAt,
          notes: notes || null,
          // v0.18.43 — 「用户选了哪一档」显式落库 (重叠课表上唯一能定行的依据)
          schedule_node_id: nodeId
        });
        // 本地刷新
        this.load();
        SlotContextMenu.toast('已预留该时段', 'success');
      },
      onCancel: () => app.modal.close()
    });
  }

  // v0.8.4 — 取消预留 (软删除)
  async unreserveSlot(rsv) {
    try {
      await Api.delete('slot-reservations', rsv.id);
      this.slotReservations = this.slotReservations.filter(r => String(r.id) !== String(rsv.id));
      this.calendarTab && (this.calendarTab.slotReservations = this.slotReservations);
      // v0.9.2 — 走统一 helper (固定课模式下即便被调到也不会把预留画出来)
      this._syncReservationBlocks();
      SlotContextMenu.toast('已取消预留', 'success');
      this.load(); // 兜底再 fetch 一遍同步
    } catch (e) {
      console.error('[scheduler] unreserveSlot failed', e);
      const detail = errReasonSafe(e, '请重试');
      SlotContextMenu.toast('取消预留失败: ' + detail, 'error');
    }
  }

  /**
   * v0.9.1 阻塞项 2 (辉哥 V 测) — 统一 toast 出口
   *
   * ## 为什么不用 attrs.flashSuccess
   *   SchedulerIndexPage 渲染本组件时只传了 `activeSchoolId`,
   *   `this.attrs.flashSuccess` 永远是 undefined → 拖拽成功的提示从来没弹过,
   *   等于「改成功了但用户什么都不知道」的静默操作。
   *
   * ## 为什么不用 app.flash
   *   Flarum 2.0 的 frontend 没有 app.flash (实测 typeof app.flash === 'undefined'),
   *   见 SlotContextMenu.toast 的注释。扩展内既有约定就是 SlotContextMenu.toast。
   *
   * 这里保留「父组件如果将来真的传了 flashSuccess 就优先用它」的兼容口,
   * 但**兜底永远有** —— 这正是原来缺的那一环。
   *
   * @param {string} msg
   * @param {'success'|'error'} kind
   */
  _toast(msg, kind) {
    if (kind === void 0) {
      kind = 'success';
    }
    const attrToast = this.attrs && (kind === 'error' ? this.attrs.flashError : this.attrs.flashSuccess);
    if (typeof attrToast === 'function') {
      attrToast(msg);
      return;
    }
    SlotContextMenu.toast(msg, kind);
  }
  _formatDateTimeLocal(d) {
    // 复用已有 helper (跟 handleEventDrop 的 formatDateTimeLocal 同款)
    if (typeof d === 'string') return d;
    return this.formatDateTimeLocal(d);
  }

  /**
   * v0.7.9e — 执行取消约课 (软删除)
   * 后端 DeleteLessonController: soft delete + 审计日志 + 校区越权校验, 且有 restore 通道。
   */
  async cancelLesson(lesson) {
    try {
      await Api.delete('lessons', lesson.id);
      // 本地同步 (避免等 load() 往返, 菜单项状态立刻正确)
      this.lessons = this.lessons.filter(l => String(l.id) !== String(lesson.id));
      // ⚠️ 这里**不能**调 app.store.remove(lesson):
      //   scheduler 的 lesson 是 Api.list() 直接取的普通对象, 不是 Flarum store 里的 model
      //   (没有 .type / .id 之外的 store 元数据), store 按 model.type 分桶存取,
      //   传普通对象进去会抛 `Cannot read properties of undefined (reading 'type')`,
      //   把后面的成功提示和 this.load() 一起打断 —— 用户看到的是"明明成功了却说失败"。
      //   this.lessons 才是这个组件唯一的数据源, 过滤掉即可。
      if (this._calendar) {
        this._calendar.refetchEvents();
      }
      this.load();
      // v0.7.9e — app.flash 在 Flarum 2.0 不存在, 改用 SlotContextMenu.toast (见该文件注释)
      SlotContextMenu.toast('已取消约课', 'success');
    } catch (e) {
      console.error('[scheduler] cancelLesson failed', e);
      // 后端抛 ValidationException 时 message 在 response.errors[0].detail, e.message 往往是 [object Object]
      const detail = errReasonSafe(e, '请重试');
      SlotContextMenu.toast('取消约课失败: ' + detail, 'error');
    }
  }
  setDate(delta) {
    const d = new Date(this.currentDate);
    d.setDate(d.getDate() + delta);
    this.currentDate = d;
    // v0.6.39 — 同步 flatpickr 输入框值, 避免 flatpickr 内部 state 跟 currentDate 不一致
    //   false = 不触发 onChange, 避免 load() 被重入二次调用 (onChange 已自带 load())
    //   防御性: 当前 setDate 没有 caller (FC headerToolbar: false), 但留作后续加 prev/next 按钮的安全垫
    if (this._flatpickrInstance) {
      this._flatpickrInstance.setDate(d, false);
    }
    this.load();
  }

  // v0.6.59 — flatpickr 初始化改用公用 helper DatePickerHelper.mountDatePicker (辉哥 9/30 19:42 拍板)
  //   原 v0.6.11 ~ v0.6.58 113 行 self-contained flatpickr 配置全部下沉到 helper:
  //     - dateFormat 'Y/m/d' (v0.6.12b)
  //     - locale zh fallback (v0.6.12a)
  //     - disableMobile UA 检测 (v0.6.12b)
  //     - appendTo: document.body (SOP 106)
  //     - onReady Math.max(inputWidth, 335) (SOP 107)
  //   沉淀 SOP 105/106/107/108 (跨项目永久, 见 DatePickerHelper.js 注释)
  //
  // ── v0.10.1 重写为「对账式」挂载 (Bug: 正常→固定→正常 后日期框彻底点不动, 无报错) ──
  //   【实测根因 (V 测 puppeteer 打点, 不是猜的)】
  //     切回正常模式时: 点击发生在 t=0.6ms, setTimeout(initDatePicker, 0) 在 t≈1-4ms 执行,
  //     而 .ZS-date-input-text 直到 **t≈21ms** 才被 mithril 重绘出来
  //     (load() 是异步的, DOM 要等它的 finally 里 m.redraw() 才落地)。
  //     于是 initDatePicker 命中 `if (!textInput) return;` **静默退出**,
  //     而 onupdate() 只调 tryMountCalendar()、从不重挂 → flatpickr 从此不存在, 点 input 无反应。
  //     实测同时确认 mithril 在**普通重绘**下会复用同一个 input 节点 (同一次会话里
  //     sameNode 恒为 true), 所以问题不在"节点被换", 而在"挂载时机早于 DOM 落地"+"没人重试"。
  //
  //   【为什么是对账 (reconcile) 而不是重试 / 轮询 / 守卫补 await 前的 flag】
  //     ① 单点补 `_datePickerMounting` 只能堵住并发, 堵不住"input 还没渲染" —— 静默 return 依旧发生。
  //     ② 轮询/加长延时是把确定性 bug 换成"多数时候能好"的时序赌注, 慢网必现。
  //     ③ 对账式: 每次都重新问"当前这个 input 节点上有没有一个活着的实例",
  //        挂上/销毁/什么都不做三选一, **幂等且与调用时机无关** —— 早调用晚调用结果一样。
  //        谁调用都行(oncreate / onupdate / load 收尾 / setCalendarMode), 迟了自然会补挂。
  //   入口保留 initDatePicker() 这个名字, 内部转调 _syncDatePicker(), 避免改一堆调用点。
  async initDatePicker() {
    return this._syncDatePicker();
  }

  /** 销毁当前 flatpickr (幂等)。切模式 / 换节点 / 组件卸载共用。 */
  _destroyDatePicker() {
    try {
      if (this._flatpickrInstance) {
        this._flatpickrInstance.destroy();
      }
    } catch (e) {/* noop: 实例可能已随 DOM 一起被摘掉 */}
    this._flatpickrInstance = null;
    this._datePickerInputEl = null;
  }

  /**
   * v0.10.1 — 把 flatpickr 对账到"当前 DOM 里的 .ZS-date-input-text"上。
   *
   * 幂等; 与调用时机无关 (input 还没渲染就安静返回, 等下一次调用再来)。
   */
  async _syncDatePicker() {
    const textInput = this.$('.ZS-date-input-text')[0];

    // ① 固定课模式不渲染 input, 或 DOM 还没落地 → 无事可做, 等下一次调用
    if (!textInput || !textInput.isConnected) return;

    // ② 在途: 上一次 mount 还没回来。不重复发起, 但**记下这次的 input**,
    //    让在途的那次结束时自己复查一次 (见下面 await 之后的收尾)。
    if (this._datePickerMounting) {
      this._datePickerPendingEl = textInput;
      return;
    }

    // ③ 已有实例且就绑在当前这个 input 上 → 已经是想要的状态, 什么都不做。
    //    注意必须同时判 isConnected: 实例指向一个已被 mithril 摘掉的节点时,
    //    表面"实例非空"但点击毫无反应 —— 那正是本 bug 的另一种表现。
    if (this._flatpickrInstance && this._datePickerInputEl === textInput && textInput.isConnected) {
      return;
    }

    // ④ 实例还在但绑的是**别的/旧的**节点 → 先销毁再重挂, 避免两个实例抢同一个输入框
    if (this._flatpickrInstance) {
      this._destroyDatePicker();
    }
    this._datePickerMounting = true;
    let instance = null;
    try {
      instance = await mountDatePicker(textInput, {
        mode: 'date',
        // v0.7.9j #3 — 课表日期框显示中文格式 (辉哥 10/5 22:51 拍板)
        //   只在这里显式传, 不改 DatePickerHelper 默认值 (会污染 3 个表单 modal 的提交格式)
        dateFormat: 'Y年m月d日',
        defaultDate: this.currentDate,
        onChange: (selectedDates, dateStr) => {
          if (selectedDates.length > 0) {
            this.currentDate = selectedDates[0];
            // v0.6.39 — FC 视图同步切到新日期 (辉哥 9/30 1:03 拍板方案 B, V 测 bg_28e9a3c3 复现)
            //   改前只改 currentDate + load(), FC 视图停留在初始日期, 看起来"切日期没反应"
            //   改后 flatpickr 选日期 → currentDate + FC.gotoDate + load() 三件套同步
            if (this._calendar) {
              this._calendar.gotoDate(this.currentDate);
            }
            this.load();
          }
        }
      });
    } catch (e) {
      console.error('[ziven-scheduler] initDatePicker failed', e);
      instance = null;
    } finally {
      this._datePickerMounting = false;
    }

    // ⑤ mount 期间组件可能已经被卸载 (onremove 已把 _flatpickrInstance 清空) → 别把实例挂回去
    if (this._datePickerDestroyedDuringMount) {
      this._datePickerDestroyedDuringMount = false;
      try {
        if (instance) instance.destroy();
      } catch (e) {/* noop */}
      return;
    }

    // ⑥ vendor 没就绪 → mountDatePicker 返回 null。留着 null, 下次调用会重来。
    if (!instance) return;

    // ⑦ await 期间这个 input 可能已被 mithril 换掉 (切模式必换)。
    //    实例挂在一个已不在文档里的节点上 = 用户点了没反应, 必须销毁重来。
    if (!textInput.isConnected || this.$('.ZS-date-input-text')[0] !== textInput) {
      try {
        instance.destroy();
      } catch (e) {/* noop */}
      return;
    }
    this._flatpickrInstance = instance;
    this._datePickerInputEl = textInput;

    // ⑧ 在途期间有人排过队 → 立刻再对账一次 (此时新 input 通常已经落地)
    if (this._datePickerPendingEl) {
      const pending = this._datePickerPendingEl;
      this._datePickerPendingEl = null;
      if (pending !== textInput) {
        this._syncDatePicker();
      }
    }
  }
  view() {
    // v0.6.11 — 改 ISO text input 视觉 (跟 ziven-bjxy-website BookingModal v0.2.0ebb16a0.c.f.m.e 同款, 辉哥 9/27 14:55 拍板)
    //   旧 v0.6.10 chip 中文 "2026年9月27日 | 周日 · 第40周" 改成 ISO "2026-09-27"
    //   跟 BookingModal 字段"预约日期"完全一致视觉 + 行为:
    //     - 单 text input visible, flatpickr 接管 click (桌面) / native iOS picker (mobile)
    //     - v0.6.12b dateFormat 'Y/m/d' (跟 BM mobile native picker 一致, 桌面 flatpickr popover 不受 input value 影响)
    //     - popover 跟 input 一样宽 (onReady 强制)
    //   不用 minDate (CalendarTab 允许看历史日期, 学员回看历史课表)
    const d = this.currentDate;
    // v0.6.12b — dateStr 'Y-m-d' → 'Y/m/d' (跟 mobile native picker format 一致)
    //   桌面 flatpickr popover 显示由 popover 自身控制, 不依赖 input value
    //   mobile native picker (iOS Safari) 按 format 渲染 input value: '2026/09/27' + 📅 native icon
    //   跟 BM mobile 视觉 100% 同款 (native iOS picker)
    // v0.7.9j #3 — 'Y/m/d' → 'Y年m月d日' (辉哥 10/5 22:51 拍板)
    //   必须跟上面 mountDatePicker 的 dateFormat 保持一致, 否则 mithril redraw 会覆盖回去
    const dateStr = d.getFullYear() + '年' + String(d.getMonth() + 1).padStart(2, '0') + '月' + String(d.getDate()).padStart(2, '0') + '日';
    const conflictCount = this.conflicts.length;
    // v0.8.9 (辉哥 10/7 12:12 报需求 + 12:25 拍板) — 课表区结构调整:
    //   改前 (v0.5.0e 起): controls 与 conflict-banner 都是 .ZS-calendar-frame 的**子节点**
    //   改后: 两者与 .ZS-calendar-frame 平级, frame 内只留 .ZS-calendar-container
    //   改的原因: 辉哥附图要求日期选择器 controls 跟日历网格**分层** ——
    //   日历网格自己带边框/背景 (LESS .ZS-calendar-container), controls 不该被包在
    //   frame 的大 padding 里; 改后 controls 独占一行, 与网格视觉脱开。
    //   frame 的 margin-bottom / 背景 / 圆角等属性一律不动 (见 forum.less v0.8.9 段)。
    //   两个 conflict-banner 分支都要移出 (conflictCount > 0 / === 0), 否则
    //   "有冲突"时 banner 在 frame 内、"无冲突"时在 frame 外, 结构会不一致。
    return (
      /* v0.9.2 (辉哥 10/7 拍板, P2-b 配色) — 根节点挂模式 class
       *   为什么挂在**根 div** 而不是 .ZS-calendar-container:
       *   强调色要染两处 —— 日历容器 (在 frame 里) 和 .ZS-modeswitch 的选中态
       *   (在 .ZS-calendar-controls 里, 是容器的**兄弟**, 不是后代)。
       *   class 挂在容器上的话, 选择器够不到 controls, 只能再加一个 class → 两处真值。
       *   挂根节点 = 一个 class 覆盖两者, 正常模式不带这个 class, 一条 LESS 都不会命中。
       *   ⚠️ 之前这里是裸 <div> 没有 class, 补一个 ZS-cal-root 只是给 LESS 一个稳定挂点,
       *      不改任何结构/层级/样式。
       */
      m("div", {
        className: 'ZS-cal-root' + (this.calendarMode === 'fixed' ? ' is-fixed' : '')
      }, m("div", {
        className: "ZS-calendar-controls"
      }, !this.isStudentView() && m("div", {
        className: "ZS-modeswitch",
        role: "tablist",
        "aria-label": "\u8BFE\u8868\u6A21\u5F0F"
      }, m("button", {
        type: "button",
        role: "tab",
        "aria-selected": this.calendarMode === 'normal' ? 'true' : 'false',
        className: 'ZS-modeswitch-btn' + (this.calendarMode === 'normal' ? ' is-on' : '')
        // v0.9.1 阻塞项 1 (辉哥 V 测): onClick → onclick。
        //   Flarum 2 打包产物 setAttr 里是 `e.dom.addEventListener(r.slice(2), e.events, !1)`,
        //   r 是属性名本身, 全 bundle 无任何 toLowerCase 归一化 →
        //   onClick 会变成 addEventListener('Click'), 而 DOM 事件名大小写敏感, 永不触发。
        //   仓库其余 134 处全是小写 onclick, 这里对齐。
        ,
        onclick: () => this.setCalendarMode('normal')
      }, "\u6B63\u5E38\u7EA6\u8BFE"), m("button", {
        type: "button",
        role: "tab",
        "aria-selected": this.calendarMode === 'fixed' ? 'true' : 'false',
        className: 'ZS-modeswitch-btn' + (this.calendarMode === 'fixed' ? ' is-on' : ''),
        onclick: () => this.setCalendarMode('fixed')
      }, "\u56FA\u5B9A\u8BFE")), this.calendarMode === 'fixed' ? m("div", {
        className: "ZS-dowswitch",
        role: "tablist",
        "aria-label": "\u9009\u62E9\u661F\u671F"
      }, this.fixedDowOptions().map(_ref => {
        let [label, val] = _ref;
        return m("button", {
          key: val,
          type: "button",
          role: "tab",
          "aria-selected": this.fixedDayOfWeek === val ? 'true' : 'false',
          className: 'ZS-dowchip' + (this.fixedDayOfWeek === val ? ' is-on' : ''),
          onclick: () => this.setFixedDayOfWeek(val)
        }, label);
      })) :
      /* v0.6.11 — 改 ISO text input (跟 BookingModal 同款, 辉哥 9/27 14:55 拍板)
       *   - 旧 v0.6.10 中文 chip "2026年9月27日 | 周日 · 第40周" 改成 ISO "2026-09-27"
       * v0.6.12b — input value 'Y-m-d' → 'Y/m/d' (mobile native picker format, 桌面 flatpickr popover 不受影响)
       *   - 单 text input + flatpickr 接管 click (桌面) / native iOS picker (mobile)
       *   - onChange 选日期 → currentDate 更新 + load() 重载 FC 课表
       * v0.7.9j #3 — input value 'Y/m/d' → 'Y年m月d日' (辉哥 10/5 22:51 拍板, 中文显示)
       *   - 桌面 popover 显示由 popover 自身控制, 跟这里 input value 同步 ('Y年m月d日')
       *   - mobile native picker (iOS Safari) 按 format 渲染 input value: 'Y年m月d日' + 📅 native icon
       * v0.8.9 — controls 整块从 .ZS-calendar-frame 内移到外 (辉哥 10/7 12:25 拍板), 内部内容不动
       */
      m("div", {
        className: "ZS-date-input-wrap"
      }, m("input", {
        className: "ZS-date-input-text",
        type: "text",
        value: dateStr
      }))), this.calendarMode === 'fixed' && m("div", {
        className: "ZS-fxdateanchor"
      }, m("span", {
        className: "fxda-ic"
      }, m("i", {
        className: "fas fa-calendar-days"
      })), m("div", {
        className: "fxda-main"
      }, m("span", {
        className: "fxda-date"
      }, this.localDateStr(this.fixedAnchorDate())), m("span", {
        className: "fxda-dow"
      }, this.fixedAnchorDowText()), m("span", {
        className: "fxda-week"
      }, "\u7B2C ", this.fixedAnchorWeek(), " \u5468"), m("span", {
        className: "fxda-note"
      }, "\u672C\u89C6\u56FE\u53EA\u663E\u793A", this.fixedAnchorDowText(), "\u7684\u8BFE")), m("button", {
        type: "button",
        className: 'fxda-today' + (this.fixedAnchorIsToday() ? ' is-on' : ''),
        disabled: this.fixedAnchorIsToday(),
        onclick: () => this.setFixedDayOfWeek(new Date().getDay())
      }, m("i", {
        className: "fas fa-arrows-rotate"
      }), "\u672C\u5468")), !this.isStudentView() && conflictCount > 0 && m("div", {
        className: "ZS-conflict-banner"
      }, m("div", {
        className: "conflict-icon"
      }, m("i", {
        className: "fas fa-triangle-exclamation",
        style: "color:var(--zs-danger-ef4444, #ef4444);"
      })), m("div", {
        className: "conflict-detail"
      }, "\u68C0\u6D4B\u5230 ", m("strong", {
        style: "color:var(--zs-danger-ff3b30, #ff3b30);"
      }, conflictCount, " \u5904\u51B2\u7A81"), ":", this.conflicts.slice(0, 2).map(c => m("div", {
        key: c.lesson.id,
        style: "font-size:12px;margin-top:4px;"
      }, "\u2022 ", c.lesson.start_at, " \u8BFE\u7A0B #", c.lesson.id, " \u2014 ", c.conflict.message))), m("button", {
        className: "ZS-btn",
        style: "background:var(--zs-danger-ff3b30, #ff3b30);color:var(--zs-on-brand, white);"
      }, "\u67E5\u770B\u51B2\u7A81")), !this.isStudentView() && conflictCount === 0 && m("div", {
        className: "ZS-conflict-banner",
        style: "background:var(--zs-success-soft-52-199-89-008, rgba(52,199,89,0.08));border-color:var(--zs-success-52-199-89-03, rgba(52,199,89,0.3));"
      }, m("div", {
        className: "conflict-icon",
        style: "background:var(--zs-success-34c759, #34c759);"
      }, m("i", {
        className: "fas fa-check",
        style: "color:var(--zs-on-brand-ffffff, #ffffff);"
      })), m("div", {
        className: "conflict-detail",
        style: "color:var(--zs-success-34c759, #34c759);"
      }, "\u4ECA\u65E5\u8BFE\u7A0B\u5DF2\u6821\u9A8C\u901A\u8FC7, \u65E0\u51B2\u7A81\u3002")), this.scheduleState() === 'ready' ? m("div", {
        className: "ZS-calendar-frame"
      }, m("div", {
        className: "ZS-calendar-container"
      }, m("div", {
        className: "ZS-calendar-fc-mount"
      }))) : m("div", {
        className: "ZS-calendar-frame"
      }, m("div", {
        className: "ZS-calendar-container"
      }, this.scheduleState() === 'missing' ? scheduleMissingPanel() : this.scheduleState() === 'error' ? scheduleErrorPanel(() => this.retryScheduleLoad()) : m("div", {
        style: "min-height:300px;display:flex;align-items:center;justify-content:center;padding:36px 24px;box-sizing:border-box;"
      }, m("div", {
        className: "ZS-newversion-hint",
        style: "margin-top:0;text-align:center;padding:14px 18px;"
      }, SCHEDULE_LOADING_TEXT)))))
    );
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/CalendarTab', CalendarTab);